import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
// import Backend from 'i18next-http-backend';

// Import our DeepL translation service
import { translateBatch } from '../services/translationService';
import logger from '../utils/logger';

// Batch translation queue
interface PendingTranslation {
  key: string;
  text: string;
  language: string;
}

let translationQueue: PendingTranslation[] = [];
let isProcessingBatch = false;

// Process translation queue in batches (reserved for future batch processing)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const processBatchTranslations = async () => {
  if (isProcessingBatch || translationQueue.length === 0) {
    return;
  }

  isProcessingBatch = true;
  const currentQueue = [...translationQueue];
  translationQueue = []; // Clear queue

  try {
    // Group by language
    const byLanguage: Record<string, PendingTranslation[]> = {};
    currentQueue.forEach(item => {
      if (!byLanguage[item.language]) {
        byLanguage[item.language] = [];
      }
      byLanguage[item.language].push(item);
    });

    // Process each language batch
    for (const [language, items] of Object.entries(byLanguage)) {
      if (language === 'en') continue; // Skip English

      logger.info(`🚀 Batch translating ${items.length} items to ${language}`);
      
      const textsToTranslate = items.map(item => item.text);
      const translations = await translateBatch(textsToTranslate, language);

      // Add translations to i18n store
      const store = i18n.store as any;
      if (!store.data[language]) {
        store.data[language] = {};
      }
      if (!store.data[language]['common']) {
        store.data[language]['common'] = {};
      }

      items.forEach(item => {
        const translation = translations[item.text];
        if (translation) {
          store.data[language]['common'][item.key] = translation;
          logger.info(`✅ Batch translated "${item.key}": "${item.text}" -> "${translation}"`);
        }
      });

      // Trigger UI update for this language
      if (i18n.language === language) {
        logger.info(`🔄 Triggering UI re-render for ${items.length} ${language} translations`);
        i18n.emit('added', language, 'common');
        i18n.emit('languageChanged', language);
        
        // Force re-render
        setTimeout(() => {
          i18n.changeLanguage(language);
        }, 10);
      }
    }

  } catch (error) {
    logger.error('❌ Batch translation failed:', error);
  } finally {
    isProcessingBatch = false;
  }
};

// Queue a translation for batch processing - DISABLED (using static translations)
const queueTranslation = (key: string, text: string, language: string) => {
  // Disabled - we now use comprehensive static translations
  logger.info(`🚫 Translation queueing disabled, using static translations only: "${key}" (${language})`);
  return;
};

// Translation resources (will be moved to separate files)
const resources = {
  en: {
    common: {
      // Navigation
      home: 'Home',
      debates: 'Debates',
      newDebate: 'New Debate',
      yourDebates: 'Your Debates',
      publicDebates: 'Public Debates',
      leaderboards: 'Leaderboards',
      cube: 'CUBE',
      rules: 'Rules',
      about: 'About',
      login: 'Login',
      register: 'Register',
      logout: 'Logout',
      settings: 'Settings',
      
      // Actions
      create: 'Create',
      join: 'Join',
      view: 'View',
      edit: 'Edit',
      delete: 'Delete',
      save: 'Save',
      showNotes: 'Show Notes',
      hideNotes: 'Hide Notes',
      cancel: 'Cancel',
      submit: 'Submit',
      back: 'Back',
      next: 'Next',
      refresh: 'Refresh',
      search: 'Search',
      
      // Status
      active: 'Active',
      completed: 'Completed',
      pending: 'Pending',
      loading: 'Loading data...',
      
      // Common phrases
      welcome: 'Welcome!',
      emailVerification: 'Email Verification',
      required: 'Required',
      optional: 'Optional',
      
      // Page content keys
      activeDebates: 'Active Debates',
      completedDebates: 'Completed Debates', 
      yourTurn: 'Your Turn',
      continueDebate: 'Continue Debate',
      viewResults: 'View Results',
      createYourFirstDebate: 'Create Your First Debate',
      noActiveDebatesYet: "You don't have any active debates yet",
      noCompletedDebatesYet: "You don't have any completed debates yet",
      searchDebates: 'Search debates by topic or description...',
      created: 'Created',
      noDescriptionProvided: 'No description provided.',
      teams: 'Teams',
      noActivityForOverSixDays: 'No activity for over 6 days',
      viewOnly: 'View Only',
      noActivePublicDebates: 'No active public debates match your search.',
      noCompletedPublicDebates: 'No completed public debates match your search.',
      
      // HomePage specific
      welcomeToGauntletDebates: 'Welcome to Gauntlet Debates™',
      platformForStructuredDebates: 'A platform for structured, honorable debates',
      jestersGauntletAlt: "Jester's Gauntlet™",
      searchYourDebates: 'Search your debates...',
      noPublicDebatesAvailable: 'No public debates available',
      createNewDebateToBegin: 'Create a new debate to begin the discussion',
      createNewDebate: 'Create new debate',
      joinDebate: 'Join Debate',
      viewDebate: 'View Debate',
      
      // Email verification
      emailNotVerified: 'Email Not Verified',
      pleaseVerifyEmail: 'Please verify your email address to access all features.',
      verifyEmailNow: 'Verify Email Now',
      checkEmailInbox: 'Check your email inbox and click the verification link.',
      resendVerificationEmail: 'Resend Verification Email',
      verificationEmailSent: 'Verification email sent!',
      emailVerificationRequired: 'Email verification required',
      mustVerifyEmailTo: 'You must verify your email address to',
      
      // Forms and authentication
      emailAddress: 'Email Address',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      username: 'Username',
      signIn: 'Sign In',
      signUp: 'Sign Up',
      dontHaveAccount: "Don't have an account?",
      alreadyHaveAccount: 'Already have an account?',
      forgotPassword: 'Forgot Password?',
      rememberMe: 'Remember me',
      
      // Password reset functionality
      checkYourEmail: 'Check Your Email',
      resetYourPassword: 'Reset Your Password',
      newPassword: 'New Password',
      confirmNewPassword: 'Confirm New Password',
      sendResetLink: 'Send Reset Link',
      backToLogin: 'Back to Login',
      backToLandingPage: 'Back to Landing Page',
      sendAnotherEmail: 'Send Another Email',
      goToLogin2: 'Go to Login',
      requestNewResetLink: 'Request New Reset Link',
      resetPassword: 'Reset Password',
      rememberYourPassword: 'Remember your password?',
      enterYourEmailInstruction: 'Enter your email address and we\'ll send you a link to reset your password.',
      enterNewPasswordInstruction: 'Enter your new password below.',
      checkEmailInboxInstruction: 'Check your email inbox and follow the instructions to reset your password.',
      passwordResetEmailSent: 'If an account with that email exists, you will receive a password reset email shortly.',
      passwordResetSuccessful: 'Password Reset Successful',
      passwordResetSuccessMessage: 'Your password has been successfully reset. You can now log in with your new password.',
      invalidResetLink: 'Invalid Reset Link',
      invalidResetLinkMessage: 'This password reset link is invalid or malformed.',
      passwordResetRateLimit: 'Too many password reset attempts. Please try again in {minutes} minutes.',
      loginRateLimit: 'Too many login attempts. Please try again in {minutes} minutes.',
      passwordValidationError: 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one number.',
      passwordsDoNotMatchError: 'Passwords do not match',
      invalidResetTokenError: 'Invalid reset token',
      passwordRequirementHelper: 'Password must be 8+ characters with uppercase, lowercase, and number',
      passwordsDoNotMatchHelper: 'Passwords do not match',
      errorOccurredTryAgain: 'An error occurred. Please try again.',
      
      // Invitations
      inviteToTeam: 'Invite to Team',
      inviteToDebate: 'Invite to Debate',
      sendInvitation: 'Send Invitation',
      invitationSent: 'Invitation sent!',
      invitationExpired: 'Invitation expired',
      acceptInvitation: 'Accept Invitation',
      declineInvitation: 'Decline Invitation',
      youAreInvited: 'You are invited',
      
      // Debate creation
      debateTopic: 'Debate Topic',
      debateDescription: 'Debate Description',
      selectTeamSize: 'Select Team Size',
      maxArguments: 'Max Arguments',
      timeLimit: 'Time Limit',
      isPublic: 'Public Debate',
      allowPublicJoining: 'Allow Public Joining',
      
      // Rules page
      rulesAndGuidelines: 'Rules & Guidelines',
      conductGuidelines: 'Conduct Guidelines',
      respectfulDebate: 'Respectful Debate',
      evidenceBasedArguments: 'Evidence-Based Arguments',
      timeManagement: 'Time Management',
      
      // Common UI elements
      minutes: 'minutes',
      hours: 'hours',
      days: 'days',
      yes: 'Yes',
      no: 'No',
      close: 'Close',
      continue: 'Continue',
      finish: 'Finish',
      step: 'Step',
      of: 'of',
      
      // Status messages
      success: 'Success',
      error: 'Error',
      warning: 'Warning',
      info: 'Information',
      
      // EmailVerificationBanner specific
      limitedAccessVerifyEmail: '⚔️ Limited Access - Verify Your Email',
    limitedAccessPendingApproval: '⚔️ Limited Access - Pending Admin Approval',
      nobleUserLimitedAccess: 'Noble {username}, your account has limited access until you verify your email address.',
    nobleUserPendingApproval: 'Noble {username}, your account is pending admin approval. You\'ll receive a verification email once approved.',
      verificationEmailSentSuccess: '✅ Verification email sent successfully! Check your inbox.',
      hideDetails: 'Hide Details',
      showDetails: 'Show Details',
      sending: 'Sending...',
      resendEmail: 'Resend Email',
      currentlyUnavailable: '🚫 Currently Unavailable:',
      availableNow: '✅ Available Now:',
      tipCheckSpamFolder: '💡 Tip: Check your spam folder if you don\'t see the verification email!',
      
      // Email verification limitations
      creatingNewDebates: 'Creating new debates',
      joiningDebatesViaInvites: 'Joining debates via invite links', 
      receivingEmailNotifications: 'Receiving email notifications',
      participatingInTeamDebates: 'Participating in team debates',
      accessingPremiumFeatures: 'Accessing premium features (future)',
      
      // Email verification available features
      viewingPublicDebates: 'Viewing public debates',
      browsingTheCube: 'Browsing the CUBE',
      viewingLeaderboards: 'Viewing leaderboards',
      readingRulesAndAbout: 'Reading rules and about pages',
      
      // RegisterPage specific
      registrationSuccessful: 'Registration Successful!',
      accountCreatedSuccessfully: 'Your account has been created successfully!',
      verificationEmailSentTo: 'We\'ve sent a verification email to {email}.',
      checkInboxAndSpam: 'Please check your inbox (and spam folder) and click the verification link to activate your account.',
      canLoginNowLimited: 'You can log in now, but some features may be limited until you verify your email.',
      goToLogin: 'Go to Login',
      continueToHome: 'Continue to home',
      passwordsDoNotMatch: 'Passwords do not match',
      passwordTooShort: 'Password must be at least 6 characters',
      mustAcceptTerms: 'You must accept the Terms of Service to register',
      iAgreeToTerms: 'I agree to the',
      termsOfService: 'Terms of Service',
      loginHere: 'Login here',
      accountPendingApprovalMessage: 'Your account is pending admin approval. You\'ll receive a verification email at {email} once your account has been approved.',
      registrationVerificationEmailSent: 'We sent a verification link to {email}. Open it to confirm your address.',
      canLoginLimitedUntilVerified: 'You\'re signed in. Some features stay limited until you verify your email.',
      canLoginLimitedUntilApproved: 'You can log in now, but most features will be limited until your account is approved and your email is verified.',
      whileWaitingApprovalBrowse: 'While waiting for approval, you can browse public debates, view leaderboards, and explore the CUBE system.',
      whileWaitingVerifyBrowse: 'While you wait, you can browse public debates, view leaderboards, and explore the CUBE system.',
      
      // EmailVerificationGuard specific
      needToVerifyEmailBefore: 'You need to verify your email address before you can {action}.',
      sentVerificationLinkTo: 'We\'ve sent a verification link to {email}.',
      checkEmailAndClick: 'Please check your email and click the verification link to continue.',
      whatYouCanDoUnverified: 'What you can do while unverified:',
      viewPublicDebates: 'View public debates',
      browseLeaderboards: 'Browse leaderboards',
      exploreTheCube: 'Explore the CUBE',
      createOrParticipateDebates: 'Create or participate in debates',
      
      // DebateFormPage specific
      enterTopic: 'Enter Topic',
      reviewGeneratedViews: 'Review Generated Views',
      customize: 'Customize',
      overview: 'Overview',
      pleaseEnterDebateTopic: 'Please enter a debate topic',
      failedToGenerateViews: 'Failed to generate debate views. Please try again.',
      debateTopicPlaceholder: 'What would you like to debate about?',
      debateDescriptionPlaceholder: 'Provide additional context or background for your debate...',
      generateViews: 'Generate Views',
      yourTeamName: 'Your Team Name',
      yourTeamViewpoint: 'Your Team Viewpoint',
      opponentTeamName: 'Opponent Team Name',
      customizePrompt: 'Customize Prompt',
      customizeCounterPrompt: 'Customize Counter Prompt',
      teamConfiguration: 'Team Configuration',
      teamSize: 'Team Size',
      publicSpots: 'Public Spots',
      createDebate: 'Create Debate',
      createPrivateDebate: 'Create Private Debate',
      createPublicDebate: 'Create Public Debate',
      debateCreatedSuccessfully: 'Debate created successfully!',
      inviteOpponents: 'Invite Opponents',
      shareInviteLink: 'Share Invite Link',
      copyInviteLink: 'Copy Invite Link',
      inviteLinkCopied: 'Invite link copied to clipboard!',
      swapViews: 'Swap Views',
      
      // DebateFormPage additional labels and tooltips
      debateTopicLabel: 'Debate Topic',
      descriptionLabel: 'Description (Optional)',
      placeholderExample: 'e.g., Artificial consciousness is achievable through current machine learning methods',
      placeholderDescription: 'Add context, constraints, or specific aspects you want the debate to address',
      teamSizeTooltip: 'The maximum number of members allowed in your team, including yourself. Default is 1 (just you).',
      increaseTeamSizeTooltip: 'Increase team size to allow public spots',
      increaseTeamSizeToAllowPublicSpots: 'Increase team size to allow public spots',
      publicSpotsTooltip: 'Number of spots that can be filled by any public user. Maximum is team size minus you (the creator).',
      opponentTeamSizeTooltip: 'The maximum number of members allowed in the opponent team. Default is 1.',
      opponentPublicSpotsTooltip: 'Number of spots that can be filled by any public user. Maximum is opponent team size.',
      memberText: 'member',
      membersText: 'members',
      includingYouText: '(including you)',
      publicDebateText: 'Public Debate',
      privateDebateText: 'Private Debate',
      yesText: 'Yes',
      noText: 'No',
      debateCreatedSuccessfullyText: 'Debate Created Successfully',
      debateCreatedMessageText: 'Your debate has been created successfully! As the creator, please send the first message to get the debate started.',
      inviteMembersText: 'Invite Members to Your Team',
      shareTeamLinkText: 'Share this link with people you want to join your team:',
      inviteOpponentText: 'Invite Opponent',
      inviteOpponentsText: 'Invite Opponents',
      shareOpponentLinkText: 'Share this link with the person you want to debate against:',
      shareOpponentsLinkText: 'Share this link with the people you want to debate against:',
      linksWorkUntilText: 'The links will work until your debate is completed. All participants will need to sign up or log in to join.',
      copyOpponentLinkText: 'Copy Opponent Link',
      copyOpponentsLinkText: 'Copy Opponents Link',
      shareText: 'Share',
      goToDebateRoomText: 'Go to Debate Room',
      
      // TeamInvitePage specific
      returnToHomeText: 'Return to Home',
      youAreText: 'You are',
      aFellowKnightText: 'A fellow knight',
      callingForAlliesText: 'calling for allies in an upcoming',
      debateOfHonorText: 'Debate of Honor',
      onTheTopicText: 'on the topic:',
      publicText: 'Public',
      publicDebateDescText: 'This debate will be visible to anyone visiting Gauntlet Debates™ and will count toward your public score.',
      privateText: 'Private',
      privateDebateDescText: 'This debate will only be visible to participants.',
      fellowKnightText: 'Fellow Knight',
      youAreBeingCalledText: 'You are being called to stand with',
      yourTeamText: 'your team',
      inDefenseOfHonorText: 'in defense of honor and truth. Your voice and wisdom are needed in this battle of wits.',
      teamPositionNotDefinedText: 'This team\'s position has not been defined yet.',
      teamNotNamedText: 'This team has not been named yet.',
      knightText: 'knight',
      knightsText: 'knights',
      standText: 'stand',
      standsText: 'stands',
      readyText: 'ready',
      noKnightsJoinedText: 'No knights have joined yet.',
      invalidOrExpiredText: 'Invalid or expired invitation link',
      pleaseLoginToViewText: 'Please login to view this invitation',
      invalidInvitationLinkText: 'Invalid invitation link',
      pleaseLoginOrRegisterText: 'Please login or register to join this team',
      successfullyJoinedTeamText: 'Successfully joined the team!',
      failedToJoinTeamText: 'Failed to join team',
      failedToJoinTeamRetryText: 'Failed to join team. Please try again.',
      mustBeLoggedInText: 'You must be logged in to join this team. You\'ll be redirected to login.',
      aCallToArms: 'A Call to Arms!',
      debateType: 'Debate Type:',
      joinTheNobleCause: 'Join the Noble Cause',
      yourTeamsPosition: 'Your Team\'s Position:',
      currentMembers: 'Current Members:',
      declineTheCall: 'Decline the Call',
      joinTheRanks: 'Join the Ranks',
      
      // DebateRoomPage - Batch 1 (Basic UI Labels)
      yourTeamHeader: 'Your Team',
      teamMembersText: 'Team members',
      debateOverviewText: 'Debate Overview',
      statusText: 'Status:',
      inProgressText: 'In Progress',
      completedText: 'Completed',
      pendingText: 'Pending',
      messagesText: 'Messages:',
      startedText: 'Started:',
      lastMessageText: 'Last message:',
      noMessagesYetText: 'No messages yet',
      noPenaltiesText: 'No penalties',
      backToHomeText: 'Back to Home',
      theWinnerIsText: 'The winner is',
      yourTurnText: 'Your turn',
      waitingText: 'Waiting',
      currentTurnText: 'Current Turn',
      
      // DebateRoomPage - Batch 2 (Actions & Common UI)
      jestersGauntletCountsText: 'Jester\'s Gauntlet Counts',
      aboutPenaltiesText: 'About Penalties:',
      noMessagesStartDebateText: 'No messages yet. Start the debate by sending a message.',
      waitingForFirstTeamText: 'Waiting for the debate creator to send the first message.',
      reformulateMessageText: 'Reformulate Your Message',
      liftAllFlagsText: 'Lift All Flags',
      requestReformulationText: 'Request Reformulation',
      notifyOnOpponentMessageText: 'Email me when the opponent posts a new message',
      sendMessageText: 'Send Message',
      reformulateText: 'Reformulate',
      isTypingText: 'is typing',
      areTypingText: 'are typing',
      loadingDebateText: 'Loading debate...',
      debateNotFoundText: 'Debate not found',
      errorLoadingDebateText: 'Error loading debate',
      concedeDefeatText: 'Concede Defeat',
      
      // DebateRoomPage - Batch 3 (Dialogs, Actions & Messages)
      closeText: 'Close',
      copyLinkText: 'Copy link',
      downloadText: 'Download',
      backHomeText: 'Back to Home',
      allFlagsLiftedText: 'All flags have been lifted',
      allFlagsLiftedByUserText: 'All flags have been lifted by {{username}}',
      failedLiftFlagsText: 'Failed to lift some flags. Please try again.',
      reformulationRequestSentText: 'Reformulation request sent. Waiting for the other team to reformulate.',
      failedSendReformulationText: 'Failed to send reformulation request.',
      youConcededText: 'You have conceded. The victory goes to',
      failedConcedeText: 'Failed to concede the debate. Please try again.',
      flagLiftedText: 'Flag has been lifted',
      errorLiftingFlagText: 'Error lifting flag. Please try again.',
      messageSentSuccessfullyText: 'Message sent successfully!',
      byText: 'by',
      analyzingText: 'Analyzing...',
      recommendationText: 'Recommendation:',
      iPreferToReformulateText: 'I Prefer to Reformulate',
      concedeDebateText: 'Concede Debate',
      reformulateYourMessageText: 'Reformulate Your Message',
      
      // DebateRoomPage - Batch 4 (Common UI & Dialog Elements)
      andText: 'and',
      othersText: 'others',
      retryText: 'Retry',
      pleaseLoginText: 'Please login to participate in this debate',
      loginText: 'Login',
      failedExportText: 'Failed to export debate',
      copyDebateLinkText: 'Copy debate link',
      linkCopiedText: 'Link copied!',
      exportDebateText: 'Export debate',
      debateResultsText: 'Debate Results',
      debateCompletedText: 'Debate Completed',
      viewSummaryText: 'View Summary',
      youChosenReformulateText: 'You\'ve chosen to reformulate your message. Please address the flagged issues in your response.',
      otherTeamRequestedReformulateText: 'The other team has requested that you reformulate your message. Please address the flagged issues in your response.',
      areYouSureConcedeText: 'Are you sure you want to concede this debate? This action cannot be undone.',
      thisDebateHasConcludedText: 'This debate has concluded.',
      
      // DebateRoomPage - Batch 5 (Reformulation & Error Messages)
      chooseReformulateNowText: 'Choose to reformulate your message now.',
      penaltyFlagsNoteText: 'If there are penalty flags, reformulating will NOT remove the penalty. Only the other team lifting all flags can do that.',
      teamsLoseTooltipText: 'Teams lose automatically if they reach 3 penalties of the same kind or 5 penalties in total. Other flags may suggest reformulation but don\'t add penalty points toward that loss rule.',
      realTimeConnectionText: 'Real-time connection established',
      otherTeamChosenReformulateToastText: 'The other team has chosen to reformulate their message',
      otherTeamRequestedReformulateToastText: 'The other team has requested that you reformulate your message',
      youChosenReformulateToastText: 'You have chosen to reformulate your message.',
      areYouSureConcededText: 'Are you sure you want to concede this debate? Your team will be counted as the loser.',
      summaryGeneratedText: 'A summary of the debate has been generated and is displayed at the top of the page.',
      jestersGauntletDetectedText: 'Jester\'s Gauntlet Detected:',
      reformulationNeededText: 'Reformulation might be needed:',
      thisMessageContainsText: 'This message contains language or reasoning that requires reformulation.',
      flagTypeText: 'Flag Type:',
      optionsText: 'Options:',
      messageFlagsText: 'Message Flags',
      failedInitiateReformulationText: 'Failed to initiate reformulation.',
      errorLiftingFlagMissingIdText: 'Error lifting flag: Missing message ID',
      issueUpdatingDebateStatusText: 'There was an issue updating the debate status on the server, but the debate has been marked as completed locally.',
      couldNotIdentifyOpposingTeamText: 'Could not identify opposing team',
      failedEndDebateAfterConcessionText: 'Failed to end the debate after concession. Please try again.',
      
      // DebateRoomPage - Batch 6 (Detailed Reformulation Messages)
      aboutPenaltiesDescText: 'Flags highlight possible concerns with how something was said—they are not final rulings. Serious Jester\'s Gauntlets count as penalties; other flags call for reformulation only. The opposing team can lift flags if they disagree with the assistant.',
      onlyFirstTeamCanStartText: 'Only the first team can start the debate',
      youRequestedReformulationText: 'You requested the other team to reformulate this message.',
      otherTeamChosenReformulateText: 'The other team has chosen to reformulate their message.',
      actionRequiredText: 'Action required:',
      otherTeamMessageFlaggedText: 'The other team\'s message has been flagged.',
      reviewFlagsText: 'Please review the flags and either lift all flags (if you believe they\'re not applicable) or request reformulation (if the message needs to be improved).',
      selfInitiatedReformulationText: 'Self-Initiated Reformulation:',
      reformulationRequestedText: 'Reformulation Requested:',
      yourMessageFlaggedAgainText: 'Your message has been flagged again. You can:',
      waitOtherTeamReviewText: 'Wait for the other team to review the flags and decide, or',
      chooseReformulateAgainText: 'Choose to reformulate your message again.',
      yourRecentMessageFlaggedText: 'Your most recent message has been flagged. You can:',
      reformulationRequestedWaitText: 'Reformulation requested. Wait for your turn to submit a new message.',
      yourMessageFlaggedNeedsReformulationText: 'Your message has been flagged and needs reformulation.',
      submitImprovedMessageText: 'Please submit an improved message that addresses the flagged issues.',
      waitingOtherTeamSendText: 'Waiting for the other team to send a message.',
      waitingCubeAIResponseText: 'Waiting for the AI defense system to respond. This should not take longer than 5 minutes. If it takes longer, please refresh this page.',
      cubeAIDefenseFailedText: 'AI Defense System Failed',
      cubeAIDefenseFailedDescription: 'The AI defense system encountered an error and could not respond to your challenge. Your points have been automatically refunded.',
      cubeAIDefenseFailedPermanent: 'The AI defense system was unable to respond to this challenge due to a technical issue. The challenger\'s points have been refunded.',
      cubeSubmissionSuccessTitle: 'Challenge Submitted Successfully! 🎯',
      cubeSubmissionSuccessDescription: 'Your challenge will first be evaluated by our AI defense system. If the AI cannot adequately defend the explanation, it will be opened for human defense.',
      cubeSubmissionSuccessWaitingText: 'The AI is now working on your challenge. You can monitor its progress in your',
      cubeSubmissionSuccessDebatesLink: 'My Debates',
      cubeSubmissionSuccessLinkText: 'section where you\'ll find this challenge listed.',
      waitingThemSubmitImprovedText: 'Waiting for them to submit an improved message that addresses the flagged issue.',
      aiWillRespondAutomaticallyText: 'AI will respond automatically.',
      
      // DebateFormPage - Additional Form Elements
      debateFormHeaderText: 'What would you like to debate about?',
      productiveDebateTipsText: 'Tips for a productive debate:',
      debateTopicGuidanceText: 'Choose a topic that can lead to focused, well-defined views. Specific topics (e.g., "Should renewable energy subsidies be increased in the EU?") work better than overly broad ones (e.g., "Climate change").',
      focusedTopicHelperText: 'Specify a focused topic that can be argued effectively from opposing sides',
      contextHelperText: 'Use this to provide additional context. We recommend 150-300 characters for best results.',
      
      // DebateFormPage - Review and Customize Section
      reviewAndCustomizeDebateViews: 'Review and Customize Debate Views',
      aiGeneratedViewpointsDescription: 'Our AI has generated two opposing viewpoints for your debate topic. You can edit these viewpoints to better reflect the perspectives you want to explore.',
      view1DefendPosition: 'View 1 (You will defend this position):',
      view2OpponentPosition: 'View 2 (Your opponent will defend this position):',
      
      // DebateFormPage - Configure Teams Section
      configureTeamsAndDebateSettings: 'Configure Teams and Debate Settings',
      debateVisibility: 'Debate Visibility',
      publicDebate: 'Public Debate',
      publicDebateDescription: 'Your debate will be visible to everyone. Your arguments and debate style will contribute to your official score on the leaderboard. This helps others interested in the topic find high-quality explanations.',
      privateDebate: 'Private Debate',
      privateDebateDescription: 'Your debate will only be visible to participants. This is perfect for practice or discussing sensitive topics.',
      makeDebatePubliclyJoinable: 'Make debate publicly joinable (allow users to join without invitation if public spots are available)',
      
      // DebateFormPage - Overview Section
      debateOverview: 'Debate Overview',
      reviewAllDetailsDescription: 'Review all details below. When you create the debate, you\'ll receive invitation links to share with your team members and opponent.',
      debateInformation: 'Debate Information',
      topicLabel: 'Topic:',
      visibilityLabel: 'Visibility:',
      yourTeamLabel: 'Your Team:',
      yourViewpointLabel: 'Your Viewpoint:',
      teamSizeLabel: 'Team Size:',
      opponentTeamLabel: 'Opponent Team:',
      opponentViewpointLabel: 'Opponent Viewpoint:',
      
      // DebateFormPage - Additional labels
      needToBeLoggedInToCreate: 'You need to be logged in to create a debate. You\'ll be redirected to login.',
      startANewDebate: 'Start a New Debate',
      publiclyJoinableLabel: 'Publicly Joinable:',
      publicSpotsLabel: 'Public Spots:',
      
      // CreatorInviteLinks component
      inviteLinks: 'Invite Links',
      creatorLabel: 'Creator',
      inviteOpponent: 'Invite Opponent',
      inviteTeamMembers: 'Invite Team Members',
      copyOpponentInviteLink: 'Copy opponent invite link',
      copyTeamInviteLink: 'Copy team invite link',
      inviteLinksHelpText: '💡 These are the same links from when you created the debate. Share them to invite participants!',
      
      // DebateMessageInputForm component
      reformulateYourMessagePlaceholder: 'Reformulate your message...',
      typeYourMessagePlaceholder: 'Type your message here...',
      reformulateButton: 'Reformulate',
      messageInputSendButton: 'Send',
      messageInputSendButtonOffline: 'Send*',
      messageInputCharactersText: 'characters',
      formattingHint: 'Use **bold** and *italic* in plain text, or the toolbar. Paste uses plain text only (like Notepad).',
      unverifiedLinkNote: ' (not reviewed)',
      linkGuidance: 'Only allow-listed links are one-click openable. Other URLs still appear as text with a short “not reviewed” note. To avoid gish gallop, include no more than 2 links per message. See the current whitelist.',
      linkGuidanceWithLink: "Links are for reference/context only. Don't rely on the opponent opening the link. Bring the main arguments from the reference explicitly to this debate. Don't use more than 2 links in a message. Off-list URLs show as plain text with a short notice. See <0>Whitelisted websites</0> for more information.",
      linkWhitelist: 'Whitelisted websites',
      boldTooltip: 'Bold (**text**)',
      italicTooltip: 'Italic (*text*)',
      messageInputConnectionIssuesWarning: '*Connection issues may delay message delivery',
      messageInputDraftRestored: 'Draft restored. Your unsent message was saved.',
      messageInputSendFailedRestored: 'Send failed. Your message is still here — try again when connected.',

      // Invite password (opponent link) UI
      protectOpponentInviteWithPassword: 'Require a password to accept the invite link',
      invitePasswordLabel: 'Invite Password',
      invitePasswordHelp: 'Both the opponent and team invite links require this password. Share it privately with invited participants.',
      invitePasswordPrompt: 'This debate invite is password-protected. Enter the password provided by the challenger:',
      invitePasswordIncorrect: 'Incorrect password. Please try again.',
      tooManyAttempts: 'Too many attempts. Please try again later.',
      teamFull: 'This team is already full.',

      // Personal notes
      yourNotes: 'Your Notes (only you can see this)',
      notesPlaceholder: 'Jot down thoughts, TODOs, sources to check later...',
      notesTooLong: 'Notes too long (max 50000 characters)',
      
      // BetaTesterBanner component
      hailNobleBetaKnight: '🛡️ Hail, Noble Beta Knight! ⚔️',
      betaTesterDescription: 'You are among the chosen few testing our realm. Your valor in finding bugs and providing feedback shapes the future of honorable debate!',
      privacyDataProtection: '🔒 Privacy & Data Protection',
      privateDebatesWarning: 'Private Debates: While we commit to not reading private debate content, this is still beta software. If you don\'t know or trust the developer, please be cautious about sensitive information in private debates.',
      publicContentReminder: 'Public Content: Remember that public debates, leaderboards, and CUBE entries are visible to all users.',
      yourQuestAsBetaKnight: '🏰 Your Quest as a Beta Knight',
      betaTesterPowerDescription: 'As a member of our elite testing order, you wield the power to:',
      huntBugsGlitches: 'Hunt Bugs & Glitches',
      shareYourWisdom: 'Share Your Wisdom',
      testNewFeatures: 'Test New Features',
      rememberRealmBeingForged: '⚡ Remember: This realm is still being forged! You may encounter:',
      featuresBeingRefined: 'Features that are still being refined',
      uiElementsChanging: 'UI elements that may change as we improve',
      limitedAiResponses: 'Limited AI responses to control costs during testing',
      reportIssuesText: '📧 Report Issues: Send your findings to',
      feedbackForgePath: 'Your feedback forges the path to a more honorable debating realm! 🗡️⚔️',
      
      // DebateRoomPage AutoTranslate additions
      challengeAssessment: 'Challenge Assessment',
      debateRoomSummaryText: 'Summary',
      debateHasConcluded: 'This debate has concluded. No more messages can be sent.',
      takenActionOnFlaggedMessage: 'You\'ve taken action on the flagged message.',
      waitingOtherTeamRespond: 'Waiting for the other team to respond with a new or reformulated message.',
      loadingMessages: 'Loading messages...',
      yourMessageHasBeenFlagged: 'Your message has been flagged.',
      waitForOtherTeamDecision: 'You should wait for the other team to decide whether you need to reformulate it.',
      debateRoomResultsText: 'Debate Results',
      
      // DebateInvitePage AutoTranslate additions (only new keys not already defined)
      invitePageImportantText: 'Important:',
      opponentPositionReadonly: 'Your Opponent\'s Position (Read-only):',
      yourPositionEditNeeded: 'Your Position (Edit as needed):',
      invitePagePrivateLabel: 'Private',
      
      // Invite system
      inviteToJoinDebate: 'Invite to Join Debate',
      inviteViaLink: 'Invite via Link',
      teamInviteLink: 'Team Invite Link',
      generalInviteLink: 'General Invite Link',
      shareThisLink: 'Share this link with people you want to invite',
      linkCopiedToClipboard: 'Link copied to clipboard!',
      
      // RulesPage specific
      introductionToHonorableDebate: 'Introduction to Honorable Debate',
      debateRules: 'Debate Rules',
      productiveDebateRelies: 'Productive debate relies on sound reasoning, respectful discourse, and the avoidance of logical fallacies and rhetorical tactics that undermine understanding. The Jester\'s Gauntlet™ system helps identify issues that can derail meaningful conversation, allowing participants to focus on substance rather than rhetoric.',
      aiModeratorAnalyzes: 'Our AI moderator analyzes messages for common debate misdemeanors. When it identifies potential issues, it categorizes them into two types:',
      seriousIssuesPenalties: 'Serious Issues (Penalties)',
      issuesRequiringReformulation: 'Issues Requiring Reformulation (No Penalty)',
      whenYourMessageContains: 'When your message contains a flagged issue, you\'ll have the opportunity to reformulate it. Serious issues count as penalties toward your debate score, while other issues require reformulation but don\'t count as penalties. The opposing team can lift flags they consider irrelevant or request a reformulation.',
      debateMisconductTypes: 'Debate Misconduct Types',
      seriousIssuesCountAsPenalties: 'Serious Issues (Count As Penalties)',
      theseAreOvertlyDisruptive: 'These are overtly disruptive techniques that count as penalties. 3 of the same type or 5 total will lose the debate.',
      countsAgainstYou: 'Counts Against You',
      requiresReformulation: 'Requires Reformulation',
      debateRulesSummary: 'Debate Rules Summary',
      turnBasedStructure: 'Turn-Based Structure',
      turnBasedDescription: 'Teams take turns making arguments. Each team has an opportunity to present their viewpoint and respond to the opposing team\'s points.',
      jestersGauntletPenalties: 'Jester\'s Gauntlet™ Penalties',
      jestersGauntletDescription: 'If a team accumulates 3 of the same type of serious misdemeanor OR 5 total serious misdemeanors, they automatically lose the debate.',
      flaggedMessages: 'Flagged Messages',
      flaggedMessagesDescription: 'When the AI detects a Jester\'s Gauntlet™ in a message, it is flagged. The opposing team can either lift flags they consider irrelevant or request a reformulation.',
      reformulationProcess: 'Reformulation Process',
      reformulationDescription: 'The team with a flagged message must correct the identified issues before the debate can continue, ensuring high-quality discourse.',
      conceding: 'Conceding',
      concedingDescription: 'A team may concede the debate at any time, which immediately ends the debate with victory to the opposing team.',
      inactiveDebates: 'Inactive Debates',
      inactiveDebatesDescription: 'If there are no posts for a week, the AI will summarize the debate and attempt to determine a winner based on argument quality, or declare it a draw.',
      
      // Misconduct types
      adHominem: 'Ad Hominem',
      adHominemDescription: 'Personal attacks that derail focus from the argument',
      textualIntimidation: 'Textual Intimidation',
      textualIntimidationDescription: 'Using formatting (like ALL CAPS) to intimidate rather than argue',
      grammarPolicing: 'Grammar Policing',
      grammarPolicingDescription: 'Avoiding the argument by focusing on trivial errors',
      sarcasmOverload: 'Sarcasm Overload',
      sarcasmOverloadDescription: 'Using excessive mockery that poisons discourse',
      strawman: 'Strawman',
      strawmanDescription: 'Misrepresenting someone\'s argument to create a weaker version to attack',
      wordTwisting: 'Word Twisting',
      wordTwistingDescription: 'Misrepresenting someone\'s words to create a weaker argument to attack',
      whataboutism: 'Whataboutism',
      whataboutismDescription: 'Deflecting from the current point by bringing up unrelated issues',
      gishGallop: 'Gish Gallop',
      gishGallopDescription: 'Overwhelming with too many points or links to prevent meaningful response',
      shiftingGoalposts: 'Shifting Goalposts',
      shiftingGoalpostsDescription: 'Changing criteria of an argument when original criteria are met',
      quoteSniping: 'Quote Sniping',
      quoteSnipingDescription: 'Distorting quotes out of context to mislead deliberately',
      sealioning: 'Sealioning',
      sealioningDescription: 'Relentless questioning that exhausts rather than engages',
      feigningConfusion: 'Feigning Confusion',
      feigningConfusionDescription: 'Pretending not to understand to stall or derail discussion',
      overgeneralizations: 'Overgeneralizations',
      overgeneralizationsDescription: 'Sweeping statements that lack precision but can be refined',
      falseDichotomy: 'False Dichotomy',
      falseDichotomyDescription: 'Forcing an "either-or" choice to oversimplify',
      appealToAuthority: 'Appeal to Authority',
      appealToAuthorityDescription: 'Using irrelevant citations or authorities that can be replaced',
      appealToEmotion: 'Appeal to Emotion',
      appealToEmotionDescription: 'Using emotional appeals that bypass logic but can be rephrased',
      cherryPicking: 'Cherry-Picking',
      cherryPickingDescription: 'Using selective evidence that is misleading but correctable',
      unsubstantiatedClaims: 'Unsubstantiated Claims',
      unsubstantiatedClaimsDescription: 'Making bold claims without evidence or substance',
      selectiveSkepticism: 'Selective Skepticism',
      selectiveSkepticismDescription: 'Applying doubt unevenly based on bias',
      circularReasoning: 'Circular Reasoning',
      circularReasoningDescription: 'Assuming the conclusion in the premise',
      exampleOf: 'Example of {type}:',
      betterApproach: 'Better Approach:',
      internalCode: 'Internal code:',
      
      // Additional missing common keys
      important: 'Important',
      readonly: 'Read-only',
      private: 'Private',
      public: 'Public',
      someone: 'Someone',
      
      // DebateInvitePage missing keys (add English originals)
      theGauntletHasBeenThrown: 'The Gauntlet Has Been Thrown!',
      challengedYouToDebate: 'has challenged you to a',
      debateOfHonor: 'Debate of Honor',
      onTheTopic: 'on the topic',
      publicDebateNotice: 'This debate will be visible to anyone visiting Gauntlet Debates™ and will count toward your public score.',
      privateDebateNotice: 'This debate will only be visible to participants.',
      prepareYourDefense: 'Prepare Your Defense',
      readCarefullyWarning: 'Read carefully what you\'ll be defending! The first dishonorable act could be hidden in the pre-formulated viewpoint. Take a moment to edit it to align with what you truly want to defend.',
      yourOpponentsPosition: 'Your Opponent\'s Position',
      yourPosition: 'Your Position',
      editAsNeeded: 'Edit as needed',
      yourBattleName: 'Your Battle Name',
      battleNamePlaceholder: 'Enter a name that inspires fear in your opponent',
      youMustBeLoggedIn: 'You must be logged in to accept this challenge. You\'ll be redirected to login.',
      declineChallenge: 'Decline Challenge',
      acceptTheChallenge: 'Accept the Challenge',
      invalidOrExpiredInvitation: 'Invalid or expired invitation link',
      pleaseLoginToViewInvitation: 'Please login to view this invitation',
      invalidInvitationLink: 'Invalid invitation link',
      pleaseEnterTeamName: 'Please enter a team name',
    pleaseEnterOpponentTeamName: 'Please enter opponent team name',
    missingRequiredInformation: 'Missing required information',
      successfullyJoinedDebate: 'Successfully joined the debate!',
      failedToJoinDebate: 'Failed to join debate',
      pleaseLoginOrRegisterToJoin: 'Please login or register to join this debate',
      
      // DebateRoomPage specific
      messageFlags: 'Message Flags',
      flagType: 'Flag Type:',
      options: 'Options:',
      reformulateYourMessage: 'Reformulate Your Message',
      concedeDefeat: 'Concede Defeat',
      concedeDebate: 'Concede Debate',
      backHome: 'Back Home',
      areYouSureYouWantToConcede: 'Are you sure you want to concede this debate? Your team will be counted as the loser.',
      debateResults: 'Debate Results',
      debateCompleted: 'Debate Completed',
      thisDebateHasConcluded: 'This debate has concluded.',
      theWinnerIs: 'The winner is',
      summaryGenerated: 'A summary of the debate has been generated and is displayed at the top of the page.',
      viewSummary: 'View Summary',
      jestersGauntletDetected: 'Jester\'s Gauntlet Detected: ',
      reformulationNeeded: 'Reformulation might be needed: ',
      thisMessageContainsLanguage: 'This message contains language or reasoning that requires reformulation.',
      
      // LeaderboardPage specific
      leaderboard: {
        leaderboards: 'Leaderboards',
        viewTopPerformers: 'View top performers in public debates. Only metrics from public debates are counted towards these rankings.',
        topDebaters: 'Top Debaters',
        topDebates: 'Top Debates',
        rankingBy: 'Ranking By',
        rank: 'Rank',
        user: 'User',
        publicDebates: 'Public Debates',
        wins: 'Wins',
        winRate: 'Win Rate',
        excaliburIndex: 'Excalibur Index',
        explanationStrength: 'Explanation Strength (Hard to Vary)',
        argumentClarity: 'Argument Clarity',
        rebuttalEffectiveness: 'Rebuttal Effectiveness',
        evidenceQuality: 'Evidence Quality',
        steelmanningRating: 'Steelmanning Ability',
        topic: 'Topic',
        teams: 'Teams',
        overallQuality: 'Overall Quality',
        explanationQuality: 'Explanation Quality',
        logicalCoherence: 'Logical Coherence',
        civilDiscourse: 'Civil Discourse',
        messageCount: 'Message Count',
        date: 'Date',
        noUsersFound: 'No users found. Be the first to participate in public debates!',
        noDebatesFound: 'No debates found. Be the first to create a public debate!',
      },
      
      // AboutPage specific
      aboutPage: {
        title: 'About Gauntlet Debates™',
        subtitle: 'A platform for structured, AI-moderated debates that encourage thoughtful discourse',
        
        mission: {
          title: 'Our Mission',
          description: 'Gauntlet Debates addresses a fundamental problem in our digital discourse: the prevalence of bad arguments and rhetorical tactics ("Jester\'s Gauntlets") that aim to win debates without regard for truth or understanding. These tactics divide people into tribes that value winning over discovering what is true, making it increasingly difficult to have productive conversations about complex issues.',
          goal: 'We believe democracy depends on our ability to engage in honest, rigorous discussion of important topics. Our platform provides a structured environment where AI moderation identifies fallacies in real-time, allowing participants to focus on building good arguments rather than employing rhetorical tricks. Our long-term vision is to create a Wikipedia-like repository of the current best explanations (CUBE) on any topic, curated through honorable debates. This knowledge base will help people understand complex issues through the strongest arguments available, rather than those most forcefully presented or widely repeated.',
        },
        
        keyFeatures: {
          title: 'Key Features',
          structuredDebate: 'Structured Debate',
          structuredDebateDescription: 'Clear guidelines to ensure fair and focused discussions between opposing viewpoints.',
          jesterGauntlet: 'Jester\'s Gauntlet™',
          jesterGauntletDescription: 'AI-powered detection of logical fallacies and debate misdemeanors in real-time.',
          honorBasedScoring: 'Honor-Based Scoring',
          honorBasedScoringDescription: 'A point system that rewards quality arguments and penalizes debate fallacies.',
          fairResolutions: 'Fair Resolutions',
          fairResolutionsDescription: 'Opposing teams have the ability to lift flags on messages that violate debate rules or agree with the flags and request a reformulation. Teams can also choose to reformulate their flagged message by themselves.',
          debateSummaries: 'Debate Summaries',
          debateSummariesDescription: 'Comprehensive AI-generated summaries of debates serve as a valuable resource for understanding complex topics and accessing well-reasoned perspectives.',
          cubeRepository: 'CUBE Repository',
          cubeRepositoryDescription: 'Current best explanations (CUBE) extracted from debates create a Wikipedia-like knowledge base of the strongest arguments on any topic, with the unique feature that these explanations can be challenged.',
        },
        
        cubeSystem: {
          title: 'The CUBE System & Challenges',
          description: 'CUBE (Current best explanations) is the knowledge repository that emerges from our debates - a living collection of the strongest explanations on any topic, continuously refined through our challenge system.',
          howCubeWorks: 'How CUBE Works',
          cubeExtraction: 'After each debate concludes, our AI extracts the most compelling explanations and adds them to the CUBE repository. These explanations are selected based on their clarity, evidence quality, and logical strength.',
          challengeSystem: 'The Challenge System',
          challengeDescription: 'Unlike traditional wikis where anyone can edit, CUBE explanations can only be improved through formal challenges. When you believe you can provide a better explanation, you can challenge the current CUBE entry.',
          challengeProcess: 'Challenge Process: You submit your alternative explanation and spend CUBE points to initiate the challenge. Our AI evaluates both the current CUBE entry and your challenge based on explanation strength, evidence quality, and logical coherence. If your explanation is deemed superior, it replaces the current CUBE entry and your CUBE points are refunded.',
          cubePoints: 'CUBE Points: You earn CUBE points when your explanations are selected for new CUBE entries from debates. These points can be used to challenge existing explanations, creating an economy that rewards quality contributions while preventing frivolous challenges.',
          continuousImprovement: 'This system ensures that CUBE explanations continuously improve over time, always representing the current best understanding of any topic as determined through rigorous evaluation.',
        },
        
        honorableChallenges: {
          title: 'Throwing Down the Gauntlet',
          description: 'When someone makes a claim you believe should be examined more carefully, you can "throw down the gauntlet" by inviting them to defend their position in an honorable debate.',
          howToChallenge: 'This ancient practice of challenging someone to prove their assertions finds new life on our platform, where the focus is on substance rather than rhetoric. Share a debate link as an honorable challenge: "Do you believe you can defend what you just said in a structured, fair conversation?"',
          jesterGauntlet: 'Unlike typical online arguments, our platform rewards intellectually honest participants who can articulate their views clearly without resorting to fallacies. The "Jester\'s Gauntlets" system distinguishes between honorable debaters and those relying on rhetorical tricks.',
        },
        
        howItWorks: {
          title: 'How It Works',
          step1: '1. Create or Join a Debate: Start a new debate with a clear topic and opposing viewpoints, or join an existing one.',
          step2: '2. Present Your Arguments: Each team takes turns presenting arguments in support of their viewpoint. The AI will monitor for fallacies and debate tactics that violate the guidelines.',
          step3: '3. Maintain Debate Quality: If a message is flagged, opposing teams can lift flags they consider irrelevant or ask for a reformulation.',
          step4: '4. Conclude the Debate: Debates can end in several ways: when one team concedes, when a team accumulates too many penalties, or automatically after a week of inactivity (in which case the AI will analyze the debate to determine a winner or declare it a draw).',
          step5: '5. Insightful Summaries & Final Scores: Each debate concludes with an AI-generated summary highlighting the best arguments from both sides, along with final scores that reflect each team\'s performance. This combination of qualitative assessment and quantitative scoring makes our platform a valuable repository of well-reasoned perspectives on complex topics.',
        },
        
        excaliburIndex: {
          title: 'The Excalibur Index',
          description: 'The Excalibur Index is our comprehensive metric for measuring debate excellence. Just as the legendary sword could only be wielded by the truly worthy, our index recognizes those who excel in honorable debate.',
          calculation: 'How it\'s calculated: The Excalibur Index combines three key components:',
          qualityComponent: 'Quality Component (60%)',
          qualityComponentDescription: 'Based on explanation strength (40%) following David Deutsch\'s concept of explanations that are \'hard to vary\', rebuttal effectiveness (25%), evidence quality (20%), and argument clarity (15%) as assessed by AI.',
          performanceComponent: 'Performance Component (25%)',
          performanceComponentDescription: 'Calculated from win rate and scaled by participation level, rewarding consistent success.',
          goodFaithComponent: 'Good Faith Component (15%)',
          goodFaithComponentDescription: 'Measures steelmanning ability (85%) - representing opposing views fairly, and response timeliness (15%).',
          scale: 'The Excalibur Index is displayed on a scale of 0-100 with one decimal place of precision, allowing for fine distinctions between debaters. A score of 100 represents perfection in all debate metrics, a practically unattainable ideal that gives even the most accomplished debaters room for growth.',
          technicalImplementation: 'Technical implementation: The Excalibur Index currently uses ChatGPT-4o (the latest OpenAI model) to evaluate debate quality, argument strength, and steelmanning ability. As AI technology evolves, we may update our models to maintain the highest standards of assessment, always prioritizing fairness and depth of analysis.',
          publicPrivateIndices: 'We maintain separate public and private indices to ensure that debaters can practice in private settings without affecting their public ranking. Only the public Excalibur Index is displayed on leaderboards.',
        },
        
        evolvingSystem: {
          title: 'Evolving with Our Community',
          description: 'Both our Jester\'s Gauntlet™ system and CUBE repository are constantly evolving works in progress, shaped by our community\'s insights and experiences. We acknowledge several important aspects about our platform:',
          communityDrivenImprovement: 'Community-Driven Improvement',
          communityDrivenImprovementDescription: 'We actively encourage users to identify new rhetorical tactics that should be classified as Jester\'s Gauntlets, and to provide feedback on CUBE explanation quality and AI challenge evaluations. Your input directly shapes the evolution of both systems.',
          learningCurve: 'Learning Curve',
          learningCurveDescription: 'There is a learning period when first using the platform. As you become familiar with our guidelines, you\'ll develop a natural sense for crafting arguments that focus on substance rather than rhetoric, and for recognizing what makes a strong CUBE explanation.',
          falsePositives: 'Imperfect AI Judgments',
          falsePositivesDescription: 'Sometimes messages may be incorrectly flagged, or CUBE challenges may be evaluated in ways that seem questionable. When this happens with flagged messages, remember that reformulation is an opportunity to clarify your point, and opposing teams can lift flags they believe are unwarranted. For CUBE challenges, community feedback helps us refine our evaluation criteria.',
          cubeEvolution: 'CUBE System Evolution',
          cubeEvolutionDescription: 'The CUBE repository grows and improves through community participation. As more debates occur and challenges are made, our AI learns to better identify high-quality explanations and evaluate challenges more accurately. The community\'s engagement with CUBE entries helps us understand what makes explanations truly valuable.',
          nature: 'Neither system will likely ever be perfect, and that\'s by design. The nature of language, rhetoric, and knowledge is too complex for absolute rules. What matters is our commitment to continuous improvement and the shared goals of elevating debate quality and building a valuable knowledge repository. We believe that even imperfect systems can guide us toward more productive conversations and better understanding than having no standards at all.',
          reformulation: 'When a message is flagged, view it as an opportunity rather than a setback. The reformulation process often leads to stronger, clearer arguments. Similarly, when engaging with the CUBE system - whether your explanation becomes a CUBE entry or your challenge succeeds or fails - each interaction contributes to the collective understanding of complex topics.',
        },
        
        callToAction: {
          title: 'Ready to Start Debating?',
          description: 'Join our community of thoughtful debaters and engage in meaningful discussions about topics that matter.',
          startNewDebate: 'Start a New Debate',
          checkPublicDebates: 'Check Public Debates',
          imageAlt: 'Jester\'s Gauntlet™',
        },
      },

      // Whitelist page
      whitelistPage: {
        title: 'Whitelisted Websites',
        description: 'Only links from the following domains and public suffixes are one-click openable in debates. Other URLs still appear as plain text with a short “not reviewed” label. This list may be updated over time.',
        domains: 'Domains',
        suffixes: 'Public Suffixes (any subdomain)',
        note: 'Note: For safety, only http/https links are allowed, and redirects or shortened links may still be blocked.',
        whyTitle: 'Why these sites?',
        whyBody: 'Whitelisted domains are for safety, not authority. A tabloid opinion piece can sometimes contain a solid explanation; a prestigious domain can host weak arguments. Links are references, not endorsements.',
        howTitle: 'How to use links well',
        howBullet1: "Summarize the core claim you're referencing in your own words.",
        howBullet2: 'Include at most 2 references per message.',
        howBullet3: "Don't offload reading to your opponent. If your point stands only if they read the link, you haven't made the argument yet.",
        disclaimer: 'Whitelisting is about harm reduction, not authority.'
      },
      settingsPage: {
        // UserSettingsPage specific translations
        title: 'User Settings',
        loadingSettings: 'Loading settings...',
        loginRequired: 'Please login to view your settings',
        failedToLoadSettings: 'Failed to load your settings. Please try again later.',
        failedToSaveSettings: 'Failed to save your settings. Please try again later.',
        settingsSavedSuccessfully: 'Settings saved successfully!',
        saving: 'Saving...',
        saveSettings: 'Save Settings',
        
        // Email notifications section
        emailNotifications: 'Email Notifications',
        emailNotificationsDescription: 'Configure when you\'d like to receive email notifications.',
        notifyOnTurn: 'Notify me when it\'s my turn and I haven\'t replied in 24 hours',
        notifyOnDebateEndingSoon: 'Notify me when a debate is ending soon and it\'s my turn',
        notifyOnReformulation: 'Notify me when our team is asked to reformulate a message',
        
        // Email address section
        emailAddress: 'Email Address',
        currentEmailAddress: 'Your current email address:',
        
        // Password change section
        changePassword: 'Change Password',
        changePasswordDescription: 'Update your account password for security.',
        currentPassword: 'Current Password',
        newPassword: 'New Password',
        confirmNewPassword: 'Confirm New Password',
        currentPasswordRequired: 'Current password is required',
        newPasswordRequired: 'New password is required',
        confirmPasswordRequired: 'Please confirm your new password',
        passwordMinLength: 'Password must be at least 8 characters long',
        passwordsDoNotMatch: 'Passwords do not match',
        currentPasswordIncorrect: 'Current password is incorrect',
        passwordChangedSuccessfully: 'Password changed successfully',
        failedToChangePassword: 'Failed to change password',
        changingPassword: 'Changing Password...',
      },
      myDashboard: {
        title: 'My Dashboard',
        pleaseLogin: 'Please login to view your dashboard.',
        welcomeBack: 'Welcome back, {{username}}! 👋',
        debateOverview: 'Debate Overview',
        overallPerformance: 'Your overall performance across all debates',
        totalDebates: 'Total Debates',
        totalDebatesWon: 'Total Debates Won',
        publicDebates: 'Public Debates',
        excaliburIndex: 'Excalibur Index',
        winRate: 'Win Rate',
        argumentClarity: 'Argument Clarity',
        explanationStrength: 'Explanation Strength',
        quickActions: 'Quick Actions',
        getStartedWithCommonTasks: 'Get started with common tasks',
        startNewDebate: 'Start New Debate',
        browseDebates: 'Browse Debates',
        exploreCube: 'Explore CUBE',
        viewLeaderboard: 'View Leaderboard',
        yourStats: 'Your Stats',
        debatesWon: 'Debates Won',
        currentStreak: 'Current Streak',
        averageScore: 'Average Score',
      },
      cubeDashboard: {
        title: 'CUBE Dashboard',
        subtitle: 'Monitor your CUBE challenge activity and defend explanations',
        pleaseLogin: 'Please log in to view your CUBE dashboard.',
        cubePoints: 'CUBE Points',
        successfulChallenges: 'Successful Challenges',
        defensesWon: 'Defenses Won',
        cubeContributions: 'CUBE Contributions',
        total: 'total',
        challengeActivity: 'Challenge Activity',
        noSubmittedChallenges: 'No submitted challenges',
        noReceivedChallenges: 'No received challenges',
        noAvailableChallenges: 'No available challenges',
        challengeSuccessful: 'Challenge Successful',
        challengeFailed: 'Challenge Failed',
        explanationMaintained: 'Explanation Maintained',
        refunded: 'Refunded',
        acceptDefense: 'Accept Defense',
        aiAssessmentResults: 'AI Assessment Results',
        finalDecision: 'Final Decision:',
        yourChallengeWasSuccessful: 'Your challenge was successful!',
        originalExplanationMaintained: 'The original explanation was maintained',
        aiReasoning: 'AI Reasoning:',
        assessmentCompleted: 'Assessment completed:',
        viewChallengeDebate: 'View Challenge Debate',
        publicView: 'Public View',
        browseCube: 'Browse CUBE',
        viewDebates: 'View Debates',
        submitted: 'Submitted',
        received: 'Received',
        availableToDefend: 'Available to Defend',
        completed: 'Completed',
        failedToLoadData: 'Failed to load data',
        failedToAcceptDefense: 'Failed to accept defense',
        points: 'points',
        completedOn: 'Completed',
      },
    },
    debate: {
      // Debate-specific terms
      topic: 'Topic',
      description: 'Description', 
      teams: 'Teams',
      participants: 'Participants',
      createDebate: 'Create Debate',
      joinDebate: 'Join Debate',
      viewDebate: 'View Debate',
      startDebate: 'Start Debate',
      
      // Debate flow
      generateViews: 'Generate Views',
      customizeViews: 'Customize Views',
      inviteOpponents: 'Invite Opponents',
      debateInProgress: 'Debate in Progress',
      
      // Teams
      teamName: 'Team Name',
      teamSize: 'Team Size',
      publicSpots: 'Public Spots',
      
      // Messages
      argument: 'Argument',
      counterArgument: 'Counter Argument',
      evidence: 'Evidence',
      rebuttal: 'Rebuttal',
    },
    auth: {
      // Authentication
      emailAddress: 'Email Address',
      password: 'Password',
      username: 'Username',
      confirmPassword: 'Confirm Password',
      forgotPassword: 'Forgot Password?',
      
      // Email verification
      verifyEmail: 'Verify Email',
      emailNotVerified: 'Email Not Verified',
      resendVerification: 'Resend Verification',
      checkEmail: 'Check your email for verification link',
      
      // Messages
      loginSuccess: 'Successfully logged in',
      registrationSuccess: 'Registration successful',
      verificationSent: 'Verification email sent',
      invalidCredentials: 'Invalid credentials',
    },
    
    errors: {
      // Error messages
      general: 'Something went wrong',
      network: 'Network error',
      unauthorized: 'Unauthorized access',
      forbidden: 'Access forbidden',
      notFound: 'Page not found',
      validation: 'Please check your input',
      emailRequired: 'Email is required',
      passwordRequired: 'Password is required',
    },
    cubePage: {
      title: 'CUBE - CUrrent Best Explanations',
      subtitle: 'Discover the highest quality explanations on topics that matter',
      searchPlaceholder: 'Search topics...',
      explanations: 'Explanations',
      cubeExplanations: 'CUBE Explanations',
      underChallenge: 'Under Challenge',
      notInCube: 'Currently not in CUBE',
    },
    
    landingPage: {
      title: 'Gauntlet Debates™',
      subtitle: 'Donde el honor se une al intelecto',
      description: 'Participe en debates estructurados y moderados por IA que elevan el discurso y construyen conocimiento.',
      startDebating: 'Empezar a debatir',
      learnMore: 'Más información',
      cubeTitle: 'CUBO',
      cubeDescription: 'Descubra las mejores explicaciones actuales sobre cualquier tema a partir de nuestros debates de mayor calidad.',
      leaderboardsTitle: 'Tablas de clasificación',
      leaderboardsDescription: 'Consulta tu clasificación entre los debatientes y sigue tu progreso en el Índice Excalibur.',
    },
  },
  es: {
    common: {
      home: 'Inicio',
      newDebate: 'Nuevo debate',
      yourDebates: 'Sus debates',
      publicDebates: 'Debates públicos',
      leaderboards: 'Tablas de clasificación',
      cube: 'CUBO',
      rules: 'Reglas',
      about: 'Acerca de',
      login: 'Inicio de sesión',
      register: 'Regístrese en',
      logout: 'Cierre de sesión',
      settings: 'Ajustes',
      create: 'Cree',
      join: 'Únete a',
      view: 'Ver',
      edit: 'Editar',
      delete: 'Borrar',
      save: 'Guardar',
      cancel: 'Cancelar',
      submit: 'Enviar',
      back: 'Volver',
      next: 'Siguiente',
      refresh: 'Actualizar',
      search: 'Buscar en',
      active: 'Activo',
      completed: 'Completado',
      pending: 'Pendiente',
      loading: 'Cargando datos...',
      welcome: 'Bienvenido',
      emailVerification: 'Verificación del correo electrónico',
      required: 'Requerido',
      optional: 'Opcional',
      activeDebates: 'Debates activos',
      completedDebates: 'Debates finalizados',
      yourTurn: 'Tu turno',
      continueDebate: 'Continuar el debate',
      viewResults: 'Ver resultados',
      createYourFirstDebate: 'Cree su primer debate',
      noActiveDebatesYet: 'Aún no tienes ningún debate activo',
      noCompletedDebatesYet: 'Aún no ha completado ningún debate',
      searchDebates: 'Buscar debates por tema o descripción...',
      created: 'Creado',
      noDescriptionProvided: 'No se proporciona descripción.',
      teams: 'Equipos',
      noActivityForOverSixDays: 'Sin actividad durante más de 6 días',
      viewOnly: 'Ver sólo',
      noActivePublicDebates: 'No hay debates públicos activos que coincidan con su búsqueda.',
      noCompletedPublicDebates: 'No hay debates públicos finalizados que coincidan con su búsqueda.',
      welcomeToGauntletDebates: 'Bienvenido a Gauntlet Debates™',
      platformForStructuredDebates: 'Una plataforma para debates estructurados y honorables',
      jestersGauntletAlt: 'Guantelete de Jester™',
      searchYourDebates: 'Busca en tus debates...',
      noPublicDebatesAvailable: 'No hay debates públicos',
      createNewDebateToBegin: 'Crear un nuevo debate para comenzar la discusión',
      createNewDebate: 'Crear un nuevo debate',
      joinDebate: 'Únete al debate',
      viewDebate: 'Ver Debate',
      emailNotVerified: 'Correo electrónico no verificado',
      pleaseVerifyEmail: 'Verifique su dirección de correo electrónico para acceder a todas las funciones.',
      verifyEmailNow: 'Verificar correo electrónico ahora',
      checkEmailInbox: 'Compruebe su buzón de correo electrónico y haga clic en el enlace de verificación.',
      resendVerificationEmail: 'Reenviar correo de verificación',
      verificationEmailSent: 'Correo electrónico de verificación enviado',
      emailVerificationRequired: 'Se requiere verificación de correo electrónico',
      mustVerifyEmailTo: 'Debe verificar su dirección de correo electrónico para',
      emailAddress: 'Dirección de correo electrónico',
      password: 'Contraseña',
      confirmPassword: 'Confirmar contraseña',
      username: 'Nombre de usuario',
      signIn: 'Iniciar sesión',
      signUp: 'Inscribirse',
      dontHaveAccount: '¿No tiene cuenta?',
      alreadyHaveAccount: '¿Ya tiene una cuenta?',
      forgotPassword: '¿Ha olvidado su contraseña?',
      rememberMe: 'Acuérdate de mí',
      inviteToTeam: 'Invitar al equipo',
      inviteToDebate: 'Invitación al debate',
      sendInvitation: 'Enviar invitación',
      invitationSent: 'Invitación enviada',
      invitationExpired: 'Invitación caducada',
      acceptInvitation: 'Aceptar invitación',
      declineInvitation: 'Rechazar invitación',
      youAreInvited: 'Le invitamos',
      debateTopic: 'Tema de debate',
      debateDescription: 'Descripción del debate',
      selectTeamSize: 'Seleccione el tamaño del equipo',
      maxArguments: 'Argumentos máximos',
      timeLimit: 'Límite de tiempo',
      isPublic: 'Debate público',
      allowPublicJoining: 'Permitir la adhesión pública',
      rulesAndGuidelines: 'Normas y directrices',
      conductGuidelines: 'Normas de conducta',
      respectfulDebate: 'Debate respetuoso',
      evidenceBasedArguments: 'Argumentos basados en pruebas',
      timeManagement: 'Gestión del tiempo',
      minutes: 'minutos',
      hours: 'horas',
      days: 'días',
      yes: 'Sí',
      no: 'No',
      close: 'Cerrar',
      continue: 'Continúe en',
      finish: 'Acabado',
      step: 'Paso',
      of: 'de',
      success: 'Éxito',
      error: 'Error',
      warning: 'Advertencia',
      info: 'Información',
      limitedAccessVerifyEmail: '⚔️ Acceso limitado - Verifique su correo electrónico',
      limitedAccessPendingApproval: '⚔️ Acceso limitado - Pendiente de aprobación administrativa',
      nobleUserLimitedAccess: 'Noble {username}, su cuenta tiene acceso limitado hasta que verifique su dirección de correo electrónico.',
      nobleUserPendingApproval: 'Noble {username}, su cuenta está pendiente de la aprobación del administrador. Recibirás un correo electrónico de verificación una vez aprobada.',
      verificationEmailSentSuccess: '✅ ¡Email de verificación enviado correctamente! Compruebe su bandeja de entrada.',
      hideDetails: 'Ocultar detalles',
      showDetails: 'Mostrar detalles',
      sending: 'Enviando...',
      resendEmail: 'Reenviar correo electrónico',
      currentlyUnavailable: '🚫 Actualmente no disponible:',
      availableNow: '✅ Disponible ahora:',
      tipCheckSpamFolder: '💡 Consejo: ¡Comprueba tu carpeta de spam si no ves el correo de verificación!',
      creatingNewDebates: 'Crear nuevos debates',
      joiningDebatesViaInvites: 'Participación en debates a través de enlaces de invitación',
      receivingEmailNotifications: 'Recibir notificaciones por correo electrónico',
      participatingInTeamDebates: 'Participar en debates de equipo',
      accessingPremiumFeatures: 'Acceso a funciones premium (futuro)',
      viewingPublicDebates: 'Ver los debates públicos',
      browsingTheCube: 'Navegar por el CUBO',
      viewingLeaderboards: 'Ver tablas de clasificación',
      readingRulesAndAbout: 'Leer las normas y las páginas "Acerca de',
      registrationSuccessful: 'Éxito de inscripción',
      accountCreatedSuccessfully: 'Su cuenta se ha creado correctamente',
      verificationEmailSentTo: 'Hemos enviado un correo electrónico de verificación a {email}.',
      checkInboxAndSpam: 'Compruebe su bandeja de entrada (y su carpeta de correo no deseado) y haga clic en el enlace de verificación para activar su cuenta.',
      canLoginNowLimited: 'Ya puedes conectarte, pero algunas funciones pueden estar limitadas hasta que verifiques tu correo electrónico.',
      goToLogin: 'Ir al inicio de sesión',
      passwordsDoNotMatch: 'Las contraseñas no coinciden',
      passwordTooShort: 'La contraseña debe tener al menos 6 caracteres',
      mustAcceptTerms: 'Debe aceptar las Condiciones del servicio para registrarse',
      iAgreeToTerms: 'Acepto la',
      termsOfService: 'Condiciones de uso',
      loginHere: 'Conéctese aquí',
      needToVerifyEmailBefore: 'Tienes que verificar tu dirección de correo electrónico antes de poder {actuar}.',
      sentVerificationLinkTo: 'Hemos enviado un enlace de verificación a {email}.',
      checkEmailAndClick: 'Compruebe su correo electrónico y haga clic en el enlace de verificación para continuar.',
      whatYouCanDoUnverified: 'Qué puedes hacer mientras no estés verificado:',
      viewPublicDebates: 'Ver los debates públicos',
      browseLeaderboards: 'Navegar por las clasificaciones',
      exploreTheCube: 'Explora el CUBO',
      createOrParticipateDebates: 'Crear o participar en debates',
      enterTopic: 'Introducir tema',
      reviewGeneratedViews: 'Revisar las visitas generadas',
      customize: 'Personalice',
      overview: 'Visión general',
      pleaseEnterDebateTopic: 'Introduzca un tema de debate',
      failedToGenerateViews: 'No se han generado vistas del debate. Por favor, inténtelo de nuevo.',
      debateTopicPlaceholder: '¿Sobre qué le gustaría debatir?',
      debateDescriptionPlaceholder: 'Proporcione contexto o antecedentes adicionales para su debate...',
      generateViews: 'Generar vistas',
      yourTeamName: 'Nombre de su equipo',
      yourTeamViewpoint: 'El punto de vista de su equipo',
      opponentTeamName: 'Nombre del equipo adversario',
      customizePrompt: 'Personalizar aviso',
      customizeCounterPrompt: 'Personalizar el contador',
      teamConfiguration: 'Configuración del equipo',
      teamSize: 'Tamaño del equipo',
      publicSpots: 'Plazas públicas',
      createDebate: 'Crear debate',
      createPrivateDebate: 'Crear debate privado',
      createPublicDebate: 'Crear debate público',
      debateCreatedSuccessfully: '¡Debate creado con éxito!',
      inviteOpponents: 'Invitar a los adversarios',
      shareInviteLink: 'Compartir enlace de invitación',
      copyInviteLink: 'Copiar enlace de invitación',
      inviteLinkCopied: 'Enlace de invitación copiado en el portapapeles',
      swapViews: 'Intercambiar vistas',
      inviteToJoinDebate: 'Invitación a participar en el debate',
      inviteViaLink: 'Invitar mediante enlace',
      teamInviteLink: 'Enlace de invitación al equipo',
      generalInviteLink: 'Enlace de invitación general',
      shareThisLink: 'Comparte este enlace con las personas a las que quieras invitar',
      linkCopiedToClipboard: 'Enlace copiado en el portapapeles',
      introductionToHonorableDebate: 'Introducción al Debate de Honor',
      productiveDebateRelies: 'El debate productivo se basa en el razonamiento sólido, el discurso respetuoso y la evitación de falacias lógicas y tácticas retóricas que socavan el entendimiento. El sistema Jester\'s Gauntlet™ ayuda a identificar los problemas que pueden hacer descarrilar una conversación significativa, lo que permite a los participantes centrarse en la sustancia en lugar de en la retórica.',
      aiModeratorAnalyzes: 'Nuestro moderador de IA analiza los mensajes en busca de faltas comunes en los debates. Cuando identifica posibles problemas, los clasifica en dos tipos:',
      seriousIssuesPenalties: 'Cuestiones graves (sanciones)',
      issuesRequiringReformulation: 'Cuestiones que requieren reformulación (sin sanción)',
      whenYourMessageContains: 'Cuando su mensaje contenga una cuestión marcada, tendrá la oportunidad de reformularla. Las cuestiones graves cuentan como penalizaciones para tu puntuación en el debate, mientras que otras cuestiones requieren una reformulación pero no cuentan como penalizaciones. El equipo contrario puede levantar las banderas que considere irrelevantes o solicitar una reformulación.',
      debateMisconductTypes: 'Tipos de faltas en los debates',
      seriousIssuesCountAsPenalties: 'Asuntos graves (cuentan como sanciones)',
      theseAreOvertlyDisruptive: 'Estas son técnicas abiertamente disruptivas que cuentan como penalizaciones. 3 del mismo tipo o 5 en total harán perder el debate.',
      countsAgainstYou: 'Cuenta en su contra',
      requiresReformulation: 'Requiere reformulación',
      debateRulesSummary: 'Resumen de las reglas del debate',
      turnBasedStructure: 'Estructura por turnos',
      turnBasedDescription: 'Los equipos se turnan para exponer sus argumentos. Cada equipo tiene la oportunidad de exponer su punto de vista y responder a los argumentos del equipo contrario.',
      jestersGauntletPenalties: 'Penalizaciones del Guantelete del Bufón',
      jestersGauntletDescription: 'Si un equipo acumula 3 faltas graves del mismo tipo O 5 faltas graves en total, pierde automáticamente el debate.',
      flaggedMessages: 'Mensajes marcados',
      flaggedMessagesDescription: 'Cuando la IA detecta un Jester\'s Gauntlet™ en un mensaje, se marca. El equipo contrario puede levantar las banderas que considere irrelevantes o solicitar una reformulación.',
      reformulationProcess: 'Proceso de reformulación',
      reformulationDescription: 'El equipo con un mensaje marcado debe corregir los problemas identificados antes de que el debate pueda continuar, lo que garantiza un discurso de alta calidad.',
      conceding: 'Concediendo',
      concedingDescription: 'Un equipo puede conceder el debate en cualquier momento, lo que pone fin inmediatamente al debate con la victoria del equipo contrario.',
      inactiveDebates: 'Debates inactivos',
      inactiveDebatesDescription: 'Si no hay mensajes durante una semana, la IA resumirá el debate e intentará determinar un ganador basándose en la calidad de los argumentos, o declararlo empate.',
      adHominem: 'Ad Hominem',
      adHominemDescription: 'Ataques personales que desvían la atención del argumento',
      textualIntimidation: 'Intimidación textual',
      textualIntimidationDescription: 'Utilizar el formato (como TODAS MAYÚSCULAS) para intimidar en lugar de argumentar',
      grammarPolicing: 'Gramática policial',
      grammarPolicingDescription: 'Evitar la discusión centrándose en errores triviales',
      sarcasmOverload: 'Sobrecarga de sarcasmo',
      sarcasmOverloadDescription: 'El uso excesivo de la burla que envenena el discurso',
      strawman: 'Hombre de paja',
      strawmanDescription: 'Tergiversar el argumento de alguien para crear una versión más débil con la que atacar',
      wordTwisting: 'Retorcimiento de palabras',
      wordTwistingDescription: 'Tergiversar las palabras de alguien para crear un argumento más débil con el que atacar',
      whataboutism: 'Whataboutism',
      whataboutismDescription: 'Desviar la atención del tema actual sacando a colación asuntos no relacionados',
      gishGallop: 'Galope Gish',
      gishGallopDescription: 'Abruma con demasiados puntos o enlaces que impiden una respuesta significativa',
      shiftingGoalposts: 'Cambio de portería',
      shiftingGoalpostsDescription: 'Modificación de los criterios de un argumento cuando se cumplen los criterios originales',
      quoteSniping: 'Recorte de citas',
      quoteSnipingDescription: 'Distorsionar citas fuera de contexto para engañar deliberadamente',
      sealioning: 'Sellado',
      sealioningDescription: 'Cuestionamiento implacable que agota en lugar de comprometer',
      feigningConfusion: 'Fingir confusión',
      feigningConfusionDescription: 'Fingir que no se entiende para bloquear o desviar el debate',
      overgeneralizations: 'Sobregeneralizaciones',
      overgeneralizationsDescription: 'Afirmaciones generales que carecen de precisión pero que pueden afinarse',
      falseDichotomy: 'Falsa dicotomía',
      falseDichotomyDescription: 'Forzar una elección "o lo uno o lo otro" para simplificar demasiado',
      appealToAuthority: 'Apelar a la autoridad',
      appealToAuthorityDescription: 'Utilizar citas irrelevantes o autoridades que puedan sustituirse',
      appealToEmotion: 'Apelar a la emoción',
      appealToEmotionDescription: 'Utilizar apelaciones emocionales que eluden la lógica pero pueden reformularse',
      cherryPicking: 'Selección de cerezas',
      cherryPickingDescription: 'Utilización de pruebas selectivas engañosas pero corregibles',
      unsubstantiatedClaims: 'Reclamaciones infundadas',
      unsubstantiatedClaimsDescription: 'Hacer afirmaciones audaces sin pruebas ni fundamento',
      selectiveSkepticism: 'Escepticismo selectivo',
      selectiveSkepticismDescription: 'Aplicación desigual de la duda basada en prejuicios',
      circularReasoning: 'Razonamiento circular',
      circularReasoningDescription: 'Asumiendo la conclusión en la premisa',
      exampleOf: 'Ejemplo de {tipo}:',
      betterApproach: 'Mejor enfoque:',
      internalCode: 'Código interno:',
      important: 'Importante',
      readonly: 'Sólo lectura',
      private: 'Privado',
      public: 'Público',
      someone: 'Alguien',
      theGauntletHasBeenThrown: '¡Se ha lanzado el guante!',
      challengedYouToDebate: 'te ha retado a un',
      debateOfHonor: 'Debate de honor',
      onTheTopic: 'sobre el tema',
      publicDebateNotice: 'Este debate será visible para cualquiera que visite Gauntlet Debates™ y contará para tu puntuación pública.',
      privateDebateNotice: 'Este debate sólo será visible para los participantes.',
      prepareYourDefense: 'Prepare su defensa',
      readCarefullyWarning: 'Lea atentamente lo que va a defender El primer acto deshonroso podría estar oculto en el punto de vista preformulado. Tómate un momento para editarlo y alinearlo con lo que realmente quieres defender.',
      yourOpponentsPosition: 'Posición del adversario',
      yourPosition: 'Su posición',
      editAsNeeded: 'Editar según sea necesario',
      yourBattleName: 'Tu nombre de batalla',
      battleNamePlaceholder: 'Introduce un nombre que inspire miedo a tu oponente',
      youMustBeLoggedIn: 'Debes iniciar sesión para aceptar este reto. Serás redirigido para iniciar sesión.',
      declineChallenge: 'Reto a la baja',
      acceptTheChallenge: 'Acepte el reto',
      invalidOrExpiredInvitation: 'Enlace de invitación no válido o caducado',
      pleaseLoginToViewInvitation: 'Inicie sesión para ver esta invitación',
      invalidInvitationLink: 'Enlace de invitación no válido',
      pleaseEnterTeamName: 'Introduzca el nombre del equipo',
      successfullyJoinedDebate: '¡Se ha unido con éxito al debate!',
      failedToJoinDebate: 'No se ha incorporado al debate',
      pleaseLoginOrRegisterToJoin: 'Inicia sesión o regístrate para participar en este debate',
      messageFlags: 'Indicadores de mensaje',
      flagType: 'Tipo de bandera:',
      options: 'Opciones:',
      reformulateYourMessage: 'Reformule su mensaje',
      concedeDefeat: 'Conceder la derrota',
      concedeDebate: 'Conceder el debate',
      backHome: 'Volver a casa',
      areYouSureYouWantToConcede: '¿Estás seguro de que quieres conceder este debate? Su equipo será considerado perdedor.',
      debateResults: 'Resultados del debate',
      debateCompleted: 'Debate finalizado',
      thisDebateHasConcluded: 'Este debate ha concluido.',
      theWinnerIs: 'El ganador es',
      summaryGenerated: 'Se ha generado un resumen del debate que aparece en la parte superior de la página.',
      viewSummary: 'Ver resumen',
      jestersGauntletDetected: 'Guantelete de bufón detectado:',
      reformulationNeeded: 'Podría ser necesaria una reformulación:',
      thisMessageContainsLanguage: 'Este mensaje contiene lenguaje o razonamientos que requieren reformulación.',
      leaderboard: {
        leaderboards: 'Tablas de clasificación',
        viewTopPerformers: 'Ver los mejores resultados en debates públicos. En esta clasificación solo se tienen en cuenta los resultados de los debates públicos.',
        topDebaters: 'Los mejores debatientes',
        topDebates: 'Top Debates',
        rankingBy: 'Clasificación por',
        rank: 'Rango',
        user: 'Usuario',
        publicDebates: 'Debates públicos',
        wins: 'Gana',
        winRate: 'Porcentaje de victorias',
        excaliburIndex: 'Índice Excalibur',
        explanationStrength: 'Explicación Fuerza (Difícil de variar)',
        argumentClarity: 'Claridad argumental',
        rebuttalEffectiveness: 'Eficacia de la refutación',
        evidenceQuality: 'Calidad de las pruebas',
        steelmanningRating: 'Capacidad siderúrgica',
        topic: 'Tema',
        teams: 'Equipos',
        overallQuality: 'Calidad general',
        explanationQuality: 'Explicación Calidad',
        logicalCoherence: 'Coherencia lógica',
        civilDiscourse: 'Discurso civil',
        messageCount: 'Recuento de mensajes',
        date: 'Fecha',
        noUsersFound: 'No se han encontrado usuarios. ¡Sé el primero en participar en debates públicos!',
        noDebatesFound: 'No se han encontrado debates. ¡Sé el primero en crear un debate público!',
      },
      aboutPage: {
        title: 'Acerca de Gauntlet Debates™',
        subtitle: 'Una plataforma de debates estructurados y moderados por inteligencia artificial que fomenta la reflexión',
        mission: {
          title: 'Nuestra misión',
          description: 'Gauntlet Debates aborda un problema fundamental de nuestro discurso digital: la prevalencia de malos argumentos y tácticas retóricas ("Guanteletes de bufón") que pretenden ganar debates sin tener en cuenta la verdad o la comprensión. Estas tácticas dividen a la gente en tribus que valoran más ganar que descubrir la verdad, lo que hace cada vez más difícil mantener conversaciones productivas sobre temas complejos.',
          goal: 'Creemos que la democracia depende de nuestra capacidad para entablar un debate honesto y riguroso sobre temas importantes. Nuestra plataforma ofrece un entorno estructurado en el que la moderación por IA identifica las falacias en tiempo real, lo que permite a los participantes centrarse en construir buenos argumentos en lugar de emplear trucos retóricos. Nuestra visión a largo plazo es crear un repositorio similar a Wikipedia de las mejores explicaciones actuales (CUBE) sobre cualquier tema, curadas a través de debates honorables. Esta base de conocimientos ayudará a la gente a entender cuestiones complejas a través de los argumentos más sólidos disponibles, en lugar de los más enérgicos o los más repetidos.',
        },
        keyFeatures: {
          title: 'Características principales',
          structuredDebate: 'Debate estructurado',
          structuredDebateDescription: 'Directrices claras para garantizar debates justos y centrados entre puntos de vista opuestos.',
          jesterGauntlet: 'Guantelete de Jester™',
          jesterGauntletDescription: 'Detección de falacias lógicas y faltas de debate en tiempo real mediante IA.',
          honorBasedScoring: 'Puntuación basada en el honor',
          honorBasedScoringDescription: 'Un sistema de puntos que premia los argumentos de calidad y penaliza las falacias del debate.',
          fairResolutions: 'Resoluciones justas',
          fairResolutionsDescription: 'Los equipos contrarios tienen la posibilidad de levantar las banderas de los mensajes que infrinjan las reglas del debate o estar de acuerdo con las banderas y solicitar una reformulación. Los equipos también pueden optar por reformular ellos mismos su mensaje marcado.',
          debateSummaries: 'Resúmenes de los debates',
          debateSummariesDescription: 'Los exhaustivos resúmenes de los debates generados por la IA constituyen un valioso recurso para comprender temas complejos y acceder a perspectivas bien razonadas.',
          cubeRepository: 'Repositorio CUBE',
          cubeRepositoryDescription: 'Las mejores explicaciones actuales (CUBE) extraídas de los debates crean una base de conocimientos similar a la de Wikipedia con los argumentos más sólidos sobre cualquier tema, con la particularidad de que estas explicaciones pueden ser rebatidas.',
        },
        cubeSystem: {
          title: 'El sistema CUBE y sus retos',
          description: 'CUBE (Current best explanations) es el repositorio de conocimientos que surge de nuestros debates: una colección viva de las explicaciones más sólidas sobre cualquier tema, perfeccionadas continuamente a través de nuestro sistema de retos.',
          howCubeWorks: 'Cómo funciona CUBE',
          cubeExtraction: 'Una vez concluido cada debate, nuestra IA extrae las explicaciones más convincentes y las añade al repositorio CUBE. Estas explicaciones se seleccionan en función de su claridad, calidad probatoria y solidez lógica.',
          challengeSystem: 'El sistema de retos',
          challengeDescription: 'A diferencia de los wikis tradicionales, en los que cualquiera puede editar, las explicaciones de CUBE sólo pueden mejorarse mediante impugnaciones formales. Cuando creas que puedes ofrecer una explicación mejor, puedes impugnar la entrada actual de CUBE.',
          challengeProcess: 'Proceso de impugnación: Envías tu explicación alternativa y gastas puntos CUBE para iniciar el desafío. Nuestra IA evalúa tanto la entrada CUBE actual como tu desafío basándose en la fuerza de la explicación, la calidad de las pruebas y la coherencia lógica. Si tu explicación es considerada superior, sustituye a la actual entrada CUBE y tus puntos CUBE son reembolsados.',
          cubePoints: 'Puntos CUBE: Ganas puntos CUBE cuando tus explicaciones son seleccionadas para nuevas entradas CUBE de debates. Estos puntos se pueden utilizar para impugnar explicaciones existentes, creando una economía que recompensa las contribuciones de calidad al tiempo que evita impugnaciones frívolas.',
          continuousImprovement: 'Este sistema garantiza que las explicaciones CUBE mejoren continuamente con el paso del tiempo, representando siempre la mejor comprensión actual de cualquier tema, determinada mediante una evaluación rigurosa.',
        },
        honorableChallenges: {
          title: 'Arrojar el guante',
          description: 'Cuando alguien hace una afirmación que crees que debería examinarse con más detenimiento, puedes "lanzar el guante" invitándole a defender su postura en un debate honorable.',
          howToChallenge: 'Esta antigua práctica de retar a alguien a demostrar sus afirmaciones encuentra nueva vida en nuestra plataforma, donde la atención se centra en la sustancia más que en la retórica. Comparte un enlace de debate como un reto honorable: "¿Crees que puedes defender lo que acabas de decir en una conversación estructurada y justa?"',
          jesterGauntlet: 'A diferencia de las típicas discusiones en línea, nuestra plataforma premia a los participantes intelectualmente honestos que pueden articular sus puntos de vista con claridad sin recurrir a falacias. El sistema de "Guanteletes del bufón" distingue entre los debatientes honorables y los que recurren a trucos retóricos.',
        },
        howItWorks: {
          title: 'Cómo funciona',
          step1: '1. Crear o unirse a un debate: Inicie un nuevo debate con un tema claro y puntos de vista opuestos, o únase a uno ya existente.',
          step2: '2. Presenta tus argumentos: Cada equipo presenta por turnos argumentos en apoyo de su punto de vista. La IA vigilará las falacias y las tácticas de debate que infrinjan las directrices.',
          step3: '3. Mantener la calidad del debate: Si un mensaje está marcado, los equipos contrarios pueden levantar las marcas que consideren irrelevantes o pedir una reformulación.',
          step4: '4. Concluir el debate: Los debates pueden terminar de varias maneras: cuando un equipo concede, cuando un equipo acumula demasiadas penalizaciones, o automáticamente después de una semana de inactividad (en cuyo caso la IA analizará el debate para determinar un ganador o declararlo un empate).',
          step5: '5. Resúmenes perspicaces y puntuaciones finales: Cada debate concluye con un resumen generado por IA que destaca los mejores argumentos de ambas partes, junto con puntuaciones finales que reflejan el rendimiento de cada equipo. Esta combinación de evaluación cualitativa y puntuación cuantitativa convierte a nuestra plataforma en un valioso repositorio de perspectivas bien razonadas sobre temas complejos.',
        },
        excaliburIndex: {
          title: 'Índice Excalibur',
          description: 'El Índice Excalibur es nuestra métrica exhaustiva para medir la excelencia en el debate. Al igual que la legendaria espada sólo podía ser empuñada por los verdaderamente dignos, nuestro índice reconoce a aquellos que sobresalen en el debate honorable.',
          calculation: 'Cómo se calcula: El Índice Excalibur combina tres componentes clave:',
          qualityComponent: 'Componente de calidad (60%)',
          qualityComponentDescription: 'Basado en la solidez de la explicación (40%) siguiendo el concepto de David Deutsch de explicaciones "difíciles de variar", la eficacia de la refutación (25%), la calidad de las pruebas (20%) y la claridad de los argumentos (15%) evaluados por AI.',
          performanceComponent: 'Componente de rendimiento (25%)',
          performanceComponentDescription: 'Se calcula a partir del porcentaje de victorias y se escala según el nivel de participación, recompensando el éxito constante.',
          goodFaithComponent: 'Componente de buena fe (15%)',
          goodFaithComponentDescription: 'Mide la capacidad de acertar (85%) - representando justamente los puntos de vista opuestos, y la puntualidad de las respuestas (15%).',
          scale: 'El Índice Excalibur se muestra en una escala de 0 a 100 con un decimal de precisión, lo que permite hacer distinciones finas entre los debatientes. Una puntuación de 100 representa la perfección en todas las métricas del debate, un ideal prácticamente inalcanzable que deja margen de crecimiento incluso a los debatientes más consumados.',
          technicalImplementation: 'Implementación técnica: El Índice Excalibur utiliza actualmente ChatGPT-4o (el último modelo de OpenAI) para evaluar la calidad del debate, la fuerza de los argumentos y la capacidad de acertar. A medida que evolucione la tecnología de IA, podremos actualizar nuestros modelos para mantener los más altos estándares de evaluación, priorizando siempre la imparcialidad y la profundidad del análisis.',
          publicPrivateIndices: 'Mantenemos índices públicos y privados separados para garantizar que los debatientes puedan practicar en entornos privados sin que ello afecte a su clasificación pública. En las tablas de clasificación solo se muestra el índice Excalibur público.',
        },
        evolvingSystem: {
          title: 'Evolucionando con nuestra comunidad',
          description: 'Tanto nuestro sistema Jester\'s Gauntlet™ como el repositorio CUBE son obras en constante evolución, moldeadas por las ideas y experiencias de nuestra comunidad. Reconocemos varios aspectos importantes de nuestra plataforma:',
          communityDrivenImprovement: 'Mejora impulsada por la comunidad',
          communityDrivenImprovementDescription: 'Animamos activamente a los usuarios a que identifiquen nuevas tácticas retóricas que deban clasificarse como Guanteletes del Bufón, y a que aporten sus comentarios sobre la calidad de las explicaciones CUBE y las evaluaciones de los retos de IA. Sus aportaciones influyen directamente en la evolución de ambos sistemas.',
          learningCurve: 'Curva de aprendizaje',
          learningCurveDescription: 'Hay un periodo de aprendizaje cuando se utiliza la plataforma por primera vez. A medida que te familiarices con nuestras directrices, desarrollarás un sentido natural para elaborar argumentos que se centren en la sustancia más que en la retórica, y para reconocer qué hace que una explicación CUBE sea sólida.',
          falsePositives: 'Juicios imperfectos de la IA',
          falsePositivesDescription: 'A veces los mensajes pueden ser marcados incorrectamente, o los retos CUBE pueden ser evaluados de forma que parezcan cuestionables. Cuando esto ocurra con mensajes marcados, recuerda que la reformulación es una oportunidad para aclarar tu punto de vista, y los equipos contrarios pueden levantar las marcas que consideren injustificadas. En el caso de los desafíos CUBE, los comentarios de la comunidad nos ayudan a perfeccionar nuestros criterios de evaluación.',
          cubeEvolution: 'Evolución del sistema CUBE',
          cubeEvolutionDescription: 'El repositorio CUBE crece y mejora gracias a la participación de la comunidad. A medida que se producen más debates y se plantean más retos, nuestra IA aprende a identificar mejor las explicaciones de alta calidad y a evaluar los retos con mayor precisión. El compromiso de la comunidad con las entradas de CUBE nos ayuda a entender qué hace que las explicaciones sean realmente valiosas.',
          nature: 'Es probable que ninguno de los dos sistemas sea nunca perfecto, y eso es por diseño. La naturaleza del lenguaje, la retórica y el conocimiento es demasiado compleja para establecer reglas absolutas. Lo que importa es nuestro compromiso con la mejora continua y los objetivos compartidos de elevar la calidad del debate y crear un valioso repositorio de conocimientos. Creemos que incluso los sistemas imperfectos pueden guiarnos hacia conversaciones más productivas y un mejor entendimiento que la ausencia total de normas.',
          reformulation: 'Cuando se señale un mensaje, considérelo una oportunidad y no un contratiempo. El proceso de reformulación suele conducir a argumentos más sólidos y claros. Del mismo modo, al interactuar con el sistema CUBE -tanto si tu explicación se convierte en una entrada CUBE como si tu reto tiene éxito o fracasa- cada interacción contribuye a la comprensión colectiva de temas complejos.',
        },
        callToAction: {
          title: '¿Listo para empezar a debatir?',
          description: 'Únase a nuestra comunidad de polemistas reflexivos y participe en debates significativos sobre temas importantes.',
          startNewDebate: 'Iniciar un nuevo debate',
          checkPublicDebates: 'Comprobar los debates públicos',
          imageAlt: 'Guantelete de Jester™',
        },
      },
      debateRules: 'Reglas del debate',
      cubePage: {
        title: 'CUBE - Las mejores explicaciones actuales',
        subtitle: 'Descubra las explicaciones de mayor calidad sobre los temas que le interesan',
        searchPlaceholder: 'Buscar temas...',
        explanations: 'Explicaciones',
        cubeExplanations: 'CUBE Explicaciones',
        underChallenge: 'Bajo Desafío',
        notInCube: 'Actualmente no está en CUBE',
      },
      landingPage: {
        title: 'Gauntlet Debates™',
        subtitle: 'Donde el honor se une al intelecto',
        description: 'Participe en debates estructurados y moderados por IA que elevan el discurso y construyen conocimiento.',
        startDebating: 'Empezar a debatir',
        learnMore: 'Más información',
        cubeTitle: 'CUBO',
        cubeDescription: 'Descubra las mejores explicaciones actuales sobre cualquier tema a partir de nuestros debates de mayor calidad.',
        leaderboardsTitle: 'Tablas de clasificación',
        leaderboardsDescription: 'Consulta tu clasificación entre los debatientes y sigue tu progreso en el Índice Excalibur.',
      },
      accountPendingApprovalMessage: 'Tu cuenta está pendiente de la aprobación del administrador. Recibirás un correo electrónico de verificación en {email} una vez que tu cuenta haya sido aprobada.',
      canLoginLimitedUntilApproved: 'Ya puedes conectarte, pero la mayoría de las funciones estarán limitadas hasta que se apruebe tu cuenta y se verifique tu correo electrónico.',
      whileWaitingApprovalBrowse: 'Mientras esperas la aprobación, puedes navegar por los debates públicos, ver las tablas de clasificación y explorar el sistema CUBE.',
      debateTopicLabel: 'Tema de debate',
      descriptionLabel: 'Descripción (opcional)',
      placeholderExample: 'por ejemplo, la conciencia artificial puede lograrse con los métodos actuales de aprendizaje automático',
      placeholderDescription: 'Añada contexto, limitaciones o aspectos específicos que desee que se aborden en el debate',
      teamSizeTooltip: 'El número máximo de miembros permitidos en tu equipo, incluyéndote a ti. Por defecto es 1 (sólo tú).',
      increaseTeamSizeTooltip: 'Aumentar el tamaño de los equipos para permitir plazas públicas',
      increaseTeamSizeToAllowPublicSpots: 'Aumentar el tamaño de los equipos para permitir plazas públicas',
      publicSpotsTooltip: 'Número de plazas que puede ocupar cualquier usuario público. El máximo es el tamaño del equipo menos tú (el creador).',
      opponentTeamSizeTooltip: 'El número máximo de miembros permitidos en el equipo adversario. Por defecto es 1.',
      opponentPublicSpotsTooltip: 'Número de plazas que puede ocupar cualquier usuario público. El máximo es el tamaño del equipo rival.',
      memberText: 'miembro',
      membersText: 'miembros',
      includingYouText: '(incluyéndote a ti)',
      publicDebateText: 'Debate público',
      privateDebateText: 'Debate privado',
      yesText: 'Sí',
      noText: 'No',
      debateCreatedSuccessfullyText: 'Debate creado con éxito',
      debateCreatedMessageText: '¡Tu debate ha sido creado con éxito! Como creador, envíe el primer mensaje para iniciar el debate.',
      inviteMembersText: 'Invite a miembros a su equipo',
      shareTeamLinkText: 'Comparte este enlace con las personas que quieras que se unan a tu equipo:',
      inviteOpponentText: 'Invitar al adversario',
      inviteOpponentsText: 'Invitar a los adversarios',
      shareOpponentLinkText: 'Comparte este enlace con la persona contra la que quieras debatir:',
      shareOpponentsLinkText: 'Comparte este enlace con las personas contra las que quieras debatir:',
      linksWorkUntilText: 'Los enlaces funcionarán hasta que finalice el debate. Todos los participantes deberán registrarse o iniciar sesión para participar.',
      copyOpponentLinkText: 'Copiar enlace del oponente',
      copyOpponentsLinkText: 'Copiar enlace del oponente',
      shareText: 'Compartir',
      goToDebateRoomText: 'Ir a la sala de debate',
      returnToHomeText: 'Volver a la página de inicio',
      youAreText: 'Usted es',
      aFellowKnightText: 'Un compañero caballero',
      callingForAlliesText: 'pidiendo aliados en un próximo',
      debateOfHonorText: 'Debate de honor',
      onTheTopicText: 'sobre el tema:',
      publicText: 'Público',
      publicDebateDescText: 'Este debate será visible para cualquiera que visite Gauntlet Debates™ y contará para tu puntuación pública.',
      privateText: 'Privado',
      privateDebateDescText: 'Este debate sólo será visible para los participantes.',
      fellowKnightText: 'Compañero Caballero',
      youAreBeingCalledText: 'Estás llamado a estar con',
      yourTeamText: 'tu equipo',
      inDefenseOfHonorText: 'en defensa del honor y la verdad. Tu voz y tu sabiduría son necesarias en esta batalla de ingenio.',
      teamPositionNotDefinedText: 'Aún no se ha definido la posición de este equipo.',
      teamNotNamedText: 'Este equipo aún no ha sido nombrado.',
      knightText: 'caballero',
      knightsText: 'caballeros',
      standText: 'stand',
      standsText: 'stands',
      readyText: 'listo',
      noKnightsJoinedText: 'Todavía no se ha unido ningún caballero.',
      invalidOrExpiredText: 'Enlace de invitación no válido o caducado',
      pleaseLoginToViewText: 'Inicie sesión para ver esta invitación',
      invalidInvitationLinkText: 'Enlace de invitación no válido',
      pleaseLoginOrRegisterText: 'Inicie sesión o regístrese para unirse a este equipo',
      successfullyJoinedTeamText: 'Se ha incorporado con éxito al equipo',
      failedToJoinTeamText: 'No se ha unido al equipo',
      failedToJoinTeamRetryText: 'No se ha podido unir al equipo. Por favor, inténtelo de nuevo.',
      mustBeLoggedInText: 'Debes iniciar sesión para unirte a este equipo. Serás redirigido para iniciar sesión.',
      aCallToArms: 'Una llamada a las armas',
      debateType: 'Tipo de debate:',
      joinTheNobleCause: 'Únete a la noble causa',
      yourTeamsPosition: 'La posición de su equipo:',
      currentMembers: 'Miembros actuales:',
      declineTheCall: 'Rechazar la llamada',
      joinTheRanks: 'Unirse a las filas',
      yourTeamHeader: 'Su equipo',
      teamMembersText: 'Miembros del equipo',
      debateOverviewText: 'Debate general',
      statusText: 'Estado:',
      inProgressText: 'En curso',
      completedText: 'Completado',
      pendingText: 'Pendiente',
      messagesText: 'Mensajes:',
      startedText: 'Comenzó:',
      lastMessageText: 'Último mensaje:',
      noMessagesYetText: 'Aún no hay mensajes',
      noPenaltiesText: 'Sin sanciones',
      backToHomeText: 'Volver al inicio',
      theWinnerIsText: 'El ganador es',
      yourTurnText: 'Tu turno',
      waitingText: 'En espera',
      currentTurnText: 'Giro actual',
      jestersGauntletCountsText: 'El Guantelete del Bufón Cuenta',
      aboutPenaltiesText: 'Sobre las sanciones:',
      noMessagesStartDebateText: 'Aún no hay mensajes. Inicia el debate enviando un mensaje.',
      waitingForFirstTeamText: 'A la espera de que el creador del debate envíe el primer mensaje.',
      reformulateMessageText: 'Reformule su mensaje',
      liftAllFlagsText: 'Izar todas las banderas',
      requestReformulationText: 'Solicitar reformulación',
      sendMessageText: 'Enviar mensaje',
      reformulateText: 'Reformular',
      isTypingText: 'está escribiendo',
      areTypingText: 'están escribiendo',
      loadingDebateText: 'Cargando debate...',
      debateNotFoundText: 'Debate no encontrado',
      errorLoadingDebateText: 'Error al cargar el debate',
      concedeDefeatText: 'Conceder la derrota',
      closeText: 'Cerrar',
      copyLinkText: 'Copiar enlace',
      downloadText: 'Descargar',
      backHomeText: 'Volver al inicio',
      allFlagsLiftedText: 'Se han izado todas las banderas',
      failedLiftFlagsText: 'No se han podido levantar algunas banderas. Por favor, inténtelo de nuevo.',
      reformulationRequestSentText: 'Solicitud de reformulación enviada. Esperando a que el otro equipo reformule.',
      failedSendReformulationText: 'No se ha podido enviar la solicitud de reformulación.',
      youConcededText: 'Has cedido. La victoria es para',
      failedConcedeText: 'No ha concedido el debate. Por favor, inténtelo de nuevo.',
      flagLiftedText: 'Se ha izado la bandera',
      errorLiftingFlagText: 'Error al levantar la bandera. Por favor, inténtelo de nuevo.',
      messageSentSuccessfullyText: '¡Mensaje enviado con éxito!',
      byText: 'por',
      analyzingText: 'Analizar...',
      recommendationText: 'Recomendación:',
      iPreferToReformulateText: 'Prefiero reformular',
      concedeDebateText: 'Conceder el debate',
      reformulateYourMessageText: 'Reformule su mensaje',
      andText: 'y',
      othersText: 'otros',
      retryText: 'Reintentar',
      pleaseLoginText: 'Inicie sesión para participar en este debate',
      loginText: 'Inicio de sesión',
      failedExportText: 'Debate fallido sobre la exportación',
      copyDebateLinkText: 'Copiar enlace del debate',
      linkCopiedText: 'Enlace copiado',
      exportDebateText: 'Debate sobre la exportación',
      debateResultsText: 'Resultados del debate',
      debateCompletedText: 'Debate finalizado',
      viewSummaryText: 'Ver resumen',
      youChosenReformulateText: 'Ha optado por reformular su mensaje. Por favor, aborda las cuestiones señaladas en tu respuesta.',
      otherTeamRequestedReformulateText: 'El otro equipo le ha pedido que reformule su mensaje. Por favor, resuelva los problemas señalados en su respuesta.',
      areYouSureConcedeText: '¿Estás seguro de que quieres conceder este debate? Esta acción no se puede deshacer.',
      thisDebateHasConcludedText: 'Este debate ha concluido.',
      chooseReformulateNowText: 'Elija reformular su mensaje ahora.',
      penaltyFlagsNoteText: 'Si hay banderas de penalización, reformular NO eliminará la penalización. Sólo el otro equipo que levante todas las banderas puede hacerlo.',
      teamsLoseTooltipText: 'Los equipos pierden automáticamente si alcanzan 3 penalizaciones del mismo tipo o 5 penalizaciones en total. Otras banderas pueden sugerir una reformulación, pero no añaden puntos de penalización a esa regla de pérdida.',
      realTimeConnectionText: 'Conexión en tiempo real establecida',
      otherTeamChosenReformulateToastText: 'El otro equipo ha optado por reformular su mensaje',
      otherTeamRequestedReformulateToastText: 'El otro equipo le ha pedido que reformule su mensaje',
      youChosenReformulateToastText: 'Ha optado por reformular su mensaje.',
      areYouSureConcededText: '¿Estás seguro de que quieres conceder este debate? Su equipo será considerado perdedor.',
      summaryGeneratedText: 'Se ha generado un resumen del debate que aparece en la parte superior de la página.',
      jestersGauntletDetectedText: 'Guantelete de bufón detectado:',
      reformulationNeededText: 'Podría ser necesaria una reformulación:',
      thisMessageContainsText: 'Este mensaje contiene lenguaje o razonamientos que requieren reformulación.',
      flagTypeText: 'Tipo de bandera:',
      optionsText: 'Opciones:',
      messageFlagsText: 'Indicadores de mensaje',
      failedInitiateReformulationText: 'No se ha iniciado la reformulación.',
      errorLiftingFlagMissingIdText: 'Bandera de levantamiento de error: Falta ID de mensaje',
      issueUpdatingDebateStatusText: 'Se ha producido un problema al actualizar el estado del debate en el servidor, pero el debate se ha marcado como finalizado localmente.',
      couldNotIdentifyOpposingTeamText: 'No se pudo identificar al equipo contrario',
      failedEndDebateAfterConcessionText: 'No ha podido finalizar el debate tras la concesión. Por favor, inténtelo de nuevo.',
      aboutPenaltiesDescText: 'Los indicadores señalan posibles problemas con la forma en que se ha dicho algo; no son decisiones definitivas. Los Guanteletes del bufón graves cuentan como penalizaciones; otras banderas sólo requieren una reformulación. El equipo contrario puede levantar las banderas si no está de acuerdo con el asistente.',
      onlyFirstTeamCanStartText: 'Sólo el primer equipo puede iniciar el debate',
      youRequestedReformulationText: 'Ha pedido al otro equipo que reformule este mensaje.',
      otherTeamChosenReformulateText: 'El otro equipo ha optado por reformular su mensaje.',
      actionRequiredText: 'Acción requerida:',
      otherTeamMessageFlaggedText: 'El mensaje del otro equipo ha sido marcado.',
      reviewFlagsText: 'Por favor, revise las banderas y, o bien levante todas las banderas (si cree que no son aplicables), o bien solicite una reformulación (si el mensaje necesita ser mejorado).',
      selfInitiatedReformulationText: 'Reformulación por iniciativa propia:',
      reformulationRequestedText: 'Reformulación solicitada:',
      yourMessageFlaggedAgainText: 'Su mensaje ha sido marcado de nuevo. Puedes hacerlo:',
      waitOtherTeamReviewText: 'Esperar a que el otro equipo revise las banderas y decida, o',
      chooseReformulateAgainText: 'Opte por reformular de nuevo su mensaje.',
      yourRecentMessageFlaggedText: 'Tu mensaje más reciente ha sido marcado. Puedes hacerlo:',
      reformulationRequestedWaitText: 'Reformulación solicitada. Espere su turno para enviar un nuevo mensaje.',
      yourMessageFlaggedNeedsReformulationText: 'Su mensaje ha sido marcado y necesita ser reformulado.',
      submitImprovedMessageText: 'Envíe un mensaje mejorado que aborde los problemas señalados.',
      waitingOtherTeamSendText: 'Esperando a que el otro equipo envíe un mensaje.',
      waitingThemSubmitImprovedText: 'A la espera de que envíen un mensaje mejorado que aborde el problema señalado.',
      debateFormHeaderText: '¿Sobre qué le gustaría debatir?',
      productiveDebateTipsText: 'Consejos para un debate productivo:',
      debateTopicGuidanceText: 'Elija un tema que pueda dar lugar a opiniones concretas y bien definidas. Los temas específicos (por ejemplo, "¿Deberían aumentarse las subvenciones a las energías renovables en la UE?") funcionan mejor que los demasiado amplios (por ejemplo, "Cambio climático").',
      focusedTopicHelperText: 'Especificar un tema concreto que pueda argumentarse eficazmente desde posiciones opuestas',
      contextHelperText: 'Utilícelo para proporcionar un contexto adicional. Recomendamos entre 150 y 300 caracteres para obtener mejores resultados.',
      reviewAndCustomizeDebateViews: 'Revisar y personalizar las vistas del debate',
      aiGeneratedViewpointsDescription: 'Nuestra IA ha generado dos puntos de vista opuestos para tu tema de debate. Puede editar estos puntos de vista para reflejar mejor las perspectivas que desea explorar.',
      view1DefendPosition: 'Opinión 1 (Usted defenderá esta posición):',
      view2OpponentPosition: 'Vista 2 (Su oponente defenderá esta posición):',
      configureTeamsAndDebateSettings: 'Configuración de equipos y debates',
      debateVisibility: 'Visibilidad del debate',
      publicDebate: 'Debate público',
      publicDebateDescription: 'Tu debate será visible para todos. Tus argumentos y estilo de debate contribuirán a tu puntuación oficial en la tabla de clasificación. Esto ayuda a otros interesados en el tema a encontrar explicaciones de alta calidad.',
      privateDebate: 'Debate privado',
      privateDebateDescription: 'Tu debate sólo será visible para los participantes. Es perfecto para practicar o debatir sobre temas delicados.',
      makeDebatePubliclyJoinable: 'Hacer que el debate sea accesible al público (permitir que los usuarios se unan sin invitación si hay espacios públicos disponibles)',
      debateOverview: 'Debate general',
      reviewAllDetailsDescription: 'Revise todos los detalles a continuación. Cuando crees el debate, recibirás enlaces de invitación para compartir con los miembros de tu equipo y tu oponente.',
      debateInformation: 'Información sobre el debate',
      topicLabel: 'Tema:',
      visibilityLabel: 'Visibilidad:',
      yourTeamLabel: 'Su equipo:',
      yourViewpointLabel: 'Su punto de vista:',
      teamSizeLabel: 'Tamaño del equipo:',
      opponentTeamLabel: 'Equipo adversario:',
      opponentViewpointLabel: 'Punto de vista del oponente:',
      needToBeLoggedInToCreate: 'Debe iniciar sesión para crear un debate. Se le redirigirá para iniciar sesión.',
      startANewDebate: 'Iniciar un nuevo debate',
      publiclyJoinableLabel: 'Unible públicamente:',
      publicSpotsLabel: 'Espacios públicos:',
      inviteLinks: 'Invitar a enlaces',
      creatorLabel: 'Creador',
      inviteOpponent: 'Invitar al adversario',
      inviteTeamMembers: 'Invitar a miembros del equipo',
      copyOpponentInviteLink: 'Copiar el enlace de invitación al oponente',
      copyTeamInviteLink: 'Copiar enlace de invitación al equipo',
      inviteLinksHelpText: '💡 Son los mismos enlaces de cuando creaste el debate. ¡Compártelos para invitar a los participantes!',
      reformulateYourMessagePlaceholder: 'Reformule su mensaje...',
      typeYourMessagePlaceholder: 'Escriba aquí su mensaje...',
      reformulateButton: 'Reformular',
      messageInputSendButton: 'Enviar',
      messageInputSendButtonOffline: 'Enviar*',
      messageInputCharactersText: 'caracteres',
      messageInputConnectionIssuesWarning: '*Los problemas de conexión pueden retrasar la entrega de los mensajes',
      messageInputDraftRestored: 'Borrador restaurado. Tu mensaje no enviado ha sido guardado.',
      messageInputSendFailedRestored: 'Envío fallido. Su mensaje sigue aquí - inténtelo de nuevo cuando esté conectado.',
      hailNobleBetaKnight: '🛡️ ¡Salve, Noble Caballero Beta! ⚔️',
      betaTesterDescription: 'Estás entre los pocos elegidos que prueban nuestro reino. Tu valentía a la hora de encontrar errores y darnos tu opinión marcará el futuro de un debate honorable',
      privacyDataProtection: 'privacidad y protección de datos',
      privateDebatesWarning: 'Debates privados: Aunque nos comprometemos a no leer el contenido de los debates privados, éste sigue siendo un software beta. Si no conoces al desarrollador o no confías en él, ten cuidado con la información sensible en los debates privados.',
      publicContentReminder: 'Contenido público: Recuerda que los debates públicos, las tablas de clasificación y las entradas de CUBE son visibles para todos los usuarios.',
      yourQuestAsBetaKnight: '🏰 Tu búsqueda como caballero beta',
      betaTesterPowerDescription: 'Como miembro de nuestra orden de pruebas de élite, tienes el poder de:',
      huntBugsGlitches: 'Errores y fallos en la caza',
      shareYourWisdom: 'Comparte tu sabiduría',
      testNewFeatures: 'Probar nuevas funciones',
      rememberRealmBeingForged: '⚡ Recuerda: ¡Este reino aún se está forjando! Puede que te encuentres con:',
      featuresBeingRefined: 'Funciones que aún se están perfeccionando',
      uiElementsChanging: 'Elementos de la interfaz de usuario que pueden cambiar a medida que mejoramos',
      limitedAiResponses: 'Respuestas AI limitadas para controlar los costes durante las pruebas',
      reportIssuesText: '📧 Informar de problemas: Envíe sus hallazgos a',
      feedbackForgePath: 'Tus comentarios forjan el camino hacia un debate más honorable 🗡️⚔️',
      challengeAssessment: 'Desafío Evaluación',
      debateRoomSummaryText: 'Resumen',
      debateHasConcluded: 'Este debate ha concluido. No se pueden enviar más mensajes.',
      takenActionOnFlaggedMessage: 'Has actuado sobre el mensaje marcado.',
      waitingOtherTeamRespond: 'Esperar a que el otro equipo responda con un mensaje nuevo o reformulado.',
      loadingMessages: 'Cargando mensajes...',
      yourMessageHasBeenFlagged: 'Su mensaje ha sido marcado.',
      waitForOtherTeamDecision: 'Debe esperar a que el otro equipo decida si necesita reformularlo.',
      debateRoomResultsText: 'Resultados del debate',
      invitePageImportantText: 'Importante:',
      opponentPositionReadonly: 'Posición del adversario (sólo lectura):',
      yourPositionEditNeeded: 'Su posición (Edítela si es necesario):',
      invitePagePrivateLabel: 'Privado',
      settingsPage: {
        title: 'Configuración de usuario',
        loadingSettings: 'Cargando ajustes...',
        loginRequired: 'Inicie sesión para ver su configuración',
        failedToLoadSettings: 'No se ha podido cargar la configuración. Vuelve a intentarlo más tarde.',
        failedToSaveSettings: 'No se ha podido guardar la configuración. Vuelve a intentarlo más tarde.',
        settingsSavedSuccessfully: '¡Ajustes guardados correctamente!',
        saving: 'Ahorrar...',
        saveSettings: 'Guardar ajustes',
        emailNotifications: 'Notificaciones por correo electrónico',
        emailNotificationsDescription: 'Configura cuándo quieres recibir notificaciones por correo electrónico.',
        notifyOnTurn: 'Notificarme cuando sea mi turno y no haya contestado en 24 horas',
        notifyOnDebateEndingSoon: 'Notificarme cuando un debate vaya a terminar pronto y sea mi turno',
        notifyOnReformulation: 'Notificarme cuando se pida a nuestro equipo que reformule un mensaje',
        emailAddress: 'Dirección de correo electrónico',
        currentEmailAddress: 'Su dirección de correo electrónico actual:',
        changePassword: 'Cambiar contraseña',
        changePasswordDescription: 'Actualice la contraseña de su cuenta por seguridad.',
        currentPassword: 'Contraseña actual',
        newPassword: 'Nueva contraseña',
        confirmNewPassword: 'Confirmar nueva contraseña',
        currentPasswordRequired: 'Se requiere la contraseña actual',
        newPasswordRequired: 'Se requiere una nueva contraseña',
        confirmPasswordRequired: 'Confirme su nueva contraseña',
        passwordMinLength: 'La contraseña debe tener al menos 8 caracteres',
        passwordsDoNotMatch: 'Las contraseñas no coinciden',
        currentPasswordIncorrect: 'La contraseña actual es incorrecta',
        passwordChangedSuccessfully: 'Contraseña modificada correctamente',
        failedToChangePassword: 'Error al cambiar la contraseña',
        changingPassword: 'Cambiar contraseña...',
      },
      myDashboard: {
        title: 'Mi panel de control',
        yourStats: 'Sus estadísticas',
        excaliburIndex: 'Índice Excalibur',
        totalDebates: 'Total Debates',
        debatesWon: 'Debates ganados',
        currentStreak: 'Racha actual',
        averageScore: 'Puntuación media',
        exploreCube: 'Explora CUBE',
        viewLeaderboard: 'Ver clasificación',
        pleaseLogin: 'Inicie sesión para ver su panel de control.',
        welcomeBack: '¡Bienvenido de nuevo, {{username}}! 👋',
        debateOverview: 'Debate general',
        overallPerformance: 'Su rendimiento general en todos los debates',
        quickActions: 'Acciones rápidas',
        startNewDebate: 'Iniciar un nuevo debate',
        browseDebates: 'Navegar por los debates',
        totalDebatesWon: 'Total de debates ganados',
        publicDebates: 'Debates públicos',
        winRate: 'Porcentaje de victorias',
        argumentClarity: 'Claridad argumental',
        explanationStrength: 'Explicación Fuerza',
        getStartedWithCommonTasks: 'Comience con las tareas habituales',
      },
      cubeDashboard: {
        title: 'Cuadro de mandos CUBE',
        subtitle: 'Supervisa tu actividad en el reto CUBE y defiende las explicaciones',
        pleaseLogin: 'Inicia sesión para ver tu panel CUBE.',
        cubePoints: 'Puntos CUBE',
        successfulChallenges: 'Desafíos con éxito',
        defensesWon: 'Defensas ganadas',
        cubeContributions: 'Contribuciones CUBE',
        total: 'total',
        challengeActivity: 'Desafío Actividad',
        noSubmittedChallenges: 'No se han presentado impugnaciones',
        noReceivedChallenges: 'Ninguna impugnación recibida',
        noAvailableChallenges: 'No hay retos disponibles',
        challengeSuccessful: 'Reto superado con éxito',
        challengeFailed: 'Desafío fallido',
        explanationMaintained: 'Explicación Mantenida',
        refunded: 'Reembolsado',
        acceptDefense: 'Aceptar Defensa',
        aiAssessmentResults: 'Resultados de la evaluación de la IA',
        finalDecision: 'Decisión final:',
        yourChallengeWasSuccessful: 'Tu reto ha sido un éxito',
        originalExplanationMaintained: 'Se mantuvo la explicación original',
        aiReasoning: 'Razonamiento AI:',
        assessmentCompleted: 'Evaluación finalizada:',
        viewChallengeDebate: 'Ver el debate sobre el desafío',
        publicView: 'Vista pública',
        browseCube: 'Navegar por CUBE',
        viewDebates: 'Ver los debates',
        submitted: 'Enviado',
        received: 'Recibido',
        availableToDefend: 'Disponible para defender',
        completed: 'Completado',
        failedToLoadData: 'Error al cargar datos',
        failedToAcceptDefense: 'No aceptó la defensa',
        points: 'puntos',
        completedOn: 'Completado',
      },
      checkYourEmail: 'Compruebe su correo electrónico',
      resetYourPassword: 'Restablecer contraseña',
      newPassword: 'Nueva contraseña',
      confirmNewPassword: 'Confirmar nueva contraseña',
      sendResetLink: 'Enviar enlace de restablecimiento',
      backToLogin: 'Volver al inicio de sesión',
      sendAnotherEmail: 'Enviar otro correo electrónico',
      goToLogin2: 'Ir al inicio de sesión',
      requestNewResetLink: 'Solicitar nuevo enlace de reinicio',
      resetPassword: 'Restablecer contraseña',
      rememberYourPassword: '¿Recuerdas tu contraseña?',
      enterYourEmailInstruction: 'Introduce tu dirección de correo electrónico y te enviaremos un enlace para restablecer tu contraseña.',
      enterNewPasswordInstruction: 'Introduzca su nueva contraseña a continuación.',
      checkEmailInboxInstruction: 'Compruebe su buzón de correo electrónico y siga las instrucciones para restablecer su contraseña.',
      passwordResetEmailSent: 'Si existe una cuenta con esa dirección de correo electrónico, recibirá en breve un mensaje para restablecer la contraseña.',
      passwordResetSuccessful: 'Restablecimiento de contraseña correcto',
      passwordResetSuccessMessage: 'Su contraseña se ha restablecido correctamente. Ahora puede iniciar sesión con su nueva contraseña.',
      invalidResetLink: 'Enlace de reinicio no válido',
      invalidResetLinkMessage: 'Este enlace de restablecimiento de contraseña no es válido o está malformado.',
      passwordResetRateLimit: 'Demasiados intentos de restablecimiento de contraseña. Por favor, inténtalo de nuevo en {minutos} minutos.',
      loginRateLimit: 'Demasiados intentos de conexión. Por favor, inténtelo de nuevo en {minutos} minutos.',
      passwordValidationError: 'La contraseña debe tener al menos 8 caracteres y contener como mínimo una letra mayúscula, una letra minúscula y un número.',
      passwordsDoNotMatchError: 'Las contraseñas no coinciden',
      invalidResetTokenError: 'Token de reinicio inválido',
      passwordRequirementHelper: 'La contraseña debe tener más de 8 caracteres, con mayúsculas, minúsculas y números',
      passwordsDoNotMatchHelper: 'Las contraseñas no coinciden',
      errorOccurredTryAgain: 'Se ha producido un error. Por favor, inténtelo de nuevo.',
      debates: 'Debates',
      formattingHint: 'Utiliza **negrita** y *cursiva* en texto plano, o la barra de herramientas. Pegar utiliza sólo texto plano (como el Bloc de notas).',
      unverifiedLinkNote: ' (no revisado)',
      boldTooltip: 'Negrita (**texto**)',
      italicTooltip: 'Cursiva (*texto*)',
      selectTextToFormat: 'Seleccione el texto y haga clic en los botones de formato, o escriba **negrita** y *cursiva*',
      pleaseEnterOpponentTeamName: 'Introduzca el nombre del equipo adversario',
      missingRequiredInformation: 'Falta información obligatoria',
      backToLandingPage: 'Volver a la página de inicio',
      waitingCubeAIResponseText: 'Esperar a que el sistema de defensa de la IA responda. No debería tardar más de 5 minutos. Si tarda más, actualiza la página.',
      cubeAIDefenseFailedText: 'Fracaso del sistema de defensa de la IA',
      cubeAIDefenseFailedDescription: 'El sistema de defensa de la IA ha encontrado un error y no ha podido responder a tu desafío. Tus puntos han sido reembolsados automáticamente.',
      cubeAIDefenseFailedPermanent: 'El sistema de defensa de la IA no ha podido responder a este desafío debido a un problema técnico. Se han devuelto los puntos del retador.',
      cubeSubmissionSuccessTitle: '¡Reto enviado con éxito! 🎯',
      cubeSubmissionSuccessDescription: 'Su desafío será evaluado primero por nuestro sistema de defensa de IA. Si la IA no puede defender adecuadamente la explicación, se abrirá para la defensa humana.',
      cubeSubmissionSuccessWaitingText: 'La IA ya está trabajando en tu desafío. Puedes seguir su progreso en tu',
      cubeSubmissionSuccessDebatesLink: 'Mis debates',
      cubeSubmissionSuccessLinkText: 'donde encontrará este reto.',
      allFlagsLiftedByUserText: 'Todas las banderas han sido izadas por {{username}}',
      aiWillRespondAutomaticallyText: 'La IA responderá automáticamente.',
      notifyOnOpponentMessageText: 'Enviarme un correo electrónico cuando el oponente publique un nuevo mensaje',
      linkGuidance: 'Sólo los enlaces permitidos pueden abrirse con un clic. Las demás URL siguen apareciendo como texto con una breve nota de "no revisado". Para evitar el galope, no incluya más de 2 enlaces por mensaje. Consulte la lista blanca actual.',
      linkWhitelist: 'Sitios web en la lista blanca',
      whitelistPage: {
        title: 'Sitios web en la lista blanca',
        description: 'Sólo los enlaces de los siguientes dominios y sufijos públicos se pueden abrir con un clic en los debates. Las demás URL siguen apareciendo como texto sin formato con una breve etiqueta "no revisado". Esta lista puede actualizarse con el tiempo.',
        domains: 'Dominios',
        suffixes: 'Sufijos públicos (cualquier subdominio)',
        note: 'Nota: Por seguridad, sólo se permiten enlaces http/https, y los redireccionamientos o enlaces acortados pueden seguir bloqueados.',
        whyTitle: '¿Por qué estos sitios?',
        whyBody: 'Los dominios de la lista blanca son de seguridad, no de autoridad. Un artículo de opinión sensacionalista puede contener a veces una explicación sólida; un dominio prestigioso puede albergar argumentos débiles. Los enlaces son referencias, no avales.',
        howTitle: 'Cómo utilizar bien los enlaces',
        howBullet1: 'Resuma con sus propias palabras la afirmación principal a la que hace referencia.',
        howBullet2: 'Incluya como máximo 2 referencias por mensaje.',
        howBullet3: 'No descargue la lectura en su oponente. Si su argumento sólo se sostiene si leen el enlace, aún no ha presentado el argumento.',
        disclaimer: 'Las listas blancas tienen que ver con la reducción de daños, no con la autoridad.',
      },
      protectOpponentInviteWithPassword: 'Requerir una contraseña para aceptar el enlace de invitación',
      invitePasswordLabel: 'Invitar Contraseña',
      invitePasswordHelp: 'Tanto el enlace de invitación al adversario como el de invitación al equipo requieren esta contraseña. Compártala en privado con los participantes invitados.',
      invitePasswordPrompt: 'Esta invitación al debate está protegida por contraseña. Introduzca la contraseña proporcionada por el retador:',
      invitePasswordIncorrect: 'Contraseña incorrecta. Por favor, inténtelo de nuevo.',
      tooManyAttempts: 'Demasiados intentos. Vuelva a intentarlo más tarde.',
      teamFull: 'Este equipo ya está completo.',
      showNotes: 'Notas del programa',
      hideNotes: 'Ocultar notas',
      yourNotes: 'Sus notas (sólo usted puede verlas)',
      notesPlaceholder: 'Anotar pensamientos, TODOs, fuentes para comprobar más tarde...',
      notesTooLong: 'Notas demasiado largas (máx. 50000 caracteres)',
      linkGuidanceWithLink: 'Los enlaces son sólo para referencia/contexto. No confíe en que el oponente abra el enlace. Traiga los argumentos principales de la referencia explícitamente a este debate. No utilices más de 2 enlaces en un mensaje. Las URL fuera de la lista se muestran como texto sin formato con un breve aviso. Ver <0>Whitelisted websites</0> para más información.',
      continueToHome: 'Continuar',
      registrationVerificationEmailSent: 'Hemos enviado un enlace de verificación a {email}. Ábrelo para confirmar tu dirección.',
      canLoginLimitedUntilVerified: 'Has iniciado sesión. Algunas funciones permanecen limitadas hasta que verifiques tu correo electrónico.',
      whileWaitingVerifyBrowse: 'Mientras esperas, puedes navegar por los debates públicos, ver las tablas de clasificación y explorar el sistema CUBE.',
    },
    debate: {
      topic: 'Tema',
      description: 'Descripción',
      teams: 'Equipos',
      participants: 'Participantes',
      createDebate: 'Crear debate',
      joinDebate: 'Únete al debate',
      viewDebate: 'Ver Debate',
      startDebate: 'Iniciar el debate',
      generateViews: 'Generar vistas',
      customizeViews: 'Personalizar vistas',
      inviteOpponents: 'Invitar a los adversarios',
      debateInProgress: 'Debate en curso',
      teamName: 'Nombre del equipo',
      teamSize: 'Tamaño del equipo',
      publicSpots: 'Plazas públicas',
      argument: 'Argumento',
      counterArgument: 'Contraargumento',
      evidence: 'Pruebas',
      rebuttal: 'Refutación',
    },
    auth: {
      emailAddress: 'Dirección de correo electrónico',
      password: 'Contraseña',
      username: 'Nombre de usuario',
      confirmPassword: 'Confirmar contraseña',
      forgotPassword: '¿Ha olvidado su contraseña?',
      verifyEmail: 'Verificar correo electrónico',
      emailNotVerified: 'Correo electrónico no verificado',
      resendVerification: 'Reenviar verificación',
      checkEmail: 'Consulta tu correo electrónico para ver el enlace de verificación',
      loginSuccess: 'Se ha conectado correctamente',
      registrationSuccess: 'Registro efectuado con éxito',
      verificationSent: 'Correo electrónico de verificación enviado',
      invalidCredentials: 'Credenciales no válidas',
    },
    errors: {
      general: 'Algo salió mal',
      network: 'Error de red',
      unauthorized: 'Acceso no autorizado',
      forbidden: 'Acceso prohibido',
      notFound: 'Página no encontrada',
      validation: 'Compruebe su entrada',
      emailRequired: 'Correo electrónico obligatorio',
      passwordRequired: 'Se requiere contraseña',
    },
    cubePage: {
      title: 'CUBE - Las mejores explicaciones actuales',
      subtitle: 'Descubra las explicaciones de mayor calidad sobre los temas que le interesan',
      searchPlaceholder: 'Buscar temas...',
      explanations: 'Explicaciones',
      cubeExplanations: 'CUBE Explicaciones',
      underChallenge: 'Bajo Desafío',
      notInCube: 'Actualmente no está en CUBE',
    },
    cubeDashboard: {
      title: 'Cuadro de mandos CUBE',
      subtitle: 'Supervisa tu actividad en el reto CUBE y defiende las explicaciones',
      pleaseLogin: 'Inicia sesión para ver tu panel CUBE.',
      cubePoints: 'Puntos CUBE',
      successfulChallenges: 'Desafíos con éxito',
      defensesWon: 'Defensas ganadas',
      cubeContributions: 'Contribuciones CUBE',
      total: 'total',
      finalDecision: 'Decisión final:',
      yourChallengeWasSuccessful: 'Tu reto ha sido un éxito',
      originalExplanationMaintained: 'Se mantuvo la explicación original',
      browseCube: 'Navegar por CUBE',
      viewDebates: 'Ver los debates',
      completed: 'Completado',
      failedToLoadData: 'Error al cargar datos',
      failedToAcceptDefense: 'No aceptó la defensa',
      points: 'puntos',
      completedOn: 'Completado',
    },
    myDashboard: {
      title: 'Mi panel de control',
      pleaseLogin: 'Inicie sesión para ver su panel de control.',
      welcomeBack: '¡Bienvenido de nuevo, {{username}}! 👋',
      debateOverview: 'Debate general',
      overallPerformance: 'Su rendimiento general en todos los debates',
      totalDebates: 'Debates Totales',
      quickActions: 'Acciones rápidas',
      startNewDebate: 'Iniciar un nuevo debate',
      browseDebates: 'Navegar por los debates',
      exploreCube: 'CUBO Explora',
      viewLeaderboard: 'Ver clasificación',
      yourStats: 'Tus Estadísticas',
      excaliburIndex: 'Índice Excalibur',
      debatesWon: 'Debates Ganados',
      currentStreak: 'Racha Actual',
      averageScore: 'Puntuación Media',
    },
    landingPage: {
      title: 'Gauntlet Debates™',
      subtitle: 'Donde el honor se une al intelecto',
      description: 'Participe en debates estructurados y moderados por IA que elevan el discurso y construyen conocimiento.',
      startDebating: 'Empezar a debatir',
      learnMore: 'Más información',
      cubeTitle: 'CUBO',
      cubeDescription: 'Descubra las mejores explicaciones actuales sobre cualquier tema a partir de nuestros debates de mayor calidad.',
      leaderboardsTitle: 'Tablas de clasificación',
      leaderboardsDescription: 'Consulta tu clasificación entre los debatientes y sigue tu progreso en el Índice Excalibur.',
    },
    hello: 'Hola',
    rules_guidelines: 'Normas y directrices',
    cube: 'CUBO',
    leaderboards: 'Tablas de posicionamiento',
    about: 'Acerca de',
    'Your Debates': 'común:Sus debates',
    'New Debate': 'común:Nuevo debate',
    rulesguidelines: 'normasDirectrices',
    public_debates: 'Debates públicos',
    cancel: 'Cancelar',
    completed: 'Completado',
    pending: 'Pendiente',
    your_turn: 'Tu turno',
    view_results: 'Ver resultados',
    continue_debate: 'Continuar el debate',
    created: 'Creado',
    you_dont_have_any_active_debates_yet: 'Aún no tienes ningún debate activo',
    create_your_first_debate: 'Cree su primer debate',
    you_dont_have_any_completed_debates_yet: 'Aún no ha completado ningún debate',
    search_debates_by_topic_or_description: 'Buscar debates por tema o descripción...',
    active_debates: 'Debates activos',
    completed_debates: 'Debates finalizados',
    no_description_provided: 'No se proporciona descripción.',
    teams: 'Equipos',
    no_activity_for_over_6_days: 'Sin actividad durante más de 6 días',
    no_completed_public_debates_match_your_search: 'No hay debates públicos finalizados que coincidan con su búsqueda.',
    view_only: 'Ver sólo',
    no_active_public_debates_match_your_search: 'No hay debates públicos activos que coincidan con su búsqueda.',
    welcome_to_gauntlet_debates: 'Bienvenido a Gauntlet Debates™',
    no_public_debates_available: 'No hay debates públicos',
    a_platform_for_structured_honorable_debates: 'Una plataforma para debates estructurados y honorables',
    search_your_debates: 'Busca en tus debates...',
    create_new_debate: 'Crear un nuevo debate',
    view_debate: 'Ver Debate',
    please_verify_your_email_address_to_access_all_fea: 'Verifique su dirección de correo electrónico para acceder a todas las funciones.',
    create_a_new_debate_to_begin_the_discussion: 'Crear un nuevo debate para comenzar la discusión',
    sign_up: 'Inscribirse',
    verify_email_now: 'Verificar correo electrónico ahora',
    email_address: 'Dirección de correo electrónico',
    password: 'Contraseña',
    join_debate: 'Únete al debate',
    already_have_an_account: '¿Ya tiene una cuenta?',
    dont_have_an_account: '¿No tiene cuenta?',
    username: 'Nombre de usuario',
    verification_email_sent: 'Correo electrónico de verificación enviado.',
    confirm_password: 'Confirmar contraseña',
    resend_verification_email: 'Reenviar correo de verificación',
    you_must_verify_your_email_address_to: 'Debe verificar su dirección de correo electrónico para',
    jesters_gauntlet: 'Guantelete de Jester™',
    sign_in: 'Iniciar sesión',
    email_not_verified: 'Correo electrónico no verificado',
    invitation_sent: 'Invitación enviada.',
    email_verification_required: 'Se requiere verificación de correo electrónico',
    accept_invitation: 'Aceptar invitación',
    forgot_password: '¿Ha olvidado su contraseña?',
    remember_me: 'Acuérdate de mí',
    invite_to_team: 'Invitar al equipo',
    invite_to_debate: 'Invitación al debate',
    you_are_invited: 'Le invitamos',
    decline_invitation: 'Rechazar invitación',
    debate_topic: 'Tema de debate',
    time_limit: 'Límite de tiempo',
    conduct_guidelines: 'Normas de conducta',
    invitation_expired: 'Invitación caducada',
    respectful_debate: 'Debate respetuoso',
    public_debate: 'Debate público',
    time_management: 'Gestión del tiempo',
    yes: 'Sí',
    allow_public_joining: 'Permitir la adhesión pública',
    minutes: 'minutos',
    days: 'días',
    hours: 'horas',
    of: 'de',
    no: 'No',
    send_invitation: 'Enviar invitación',
    success: 'Éxito',
    continue: 'Continúe en',
    finish: 'Acabado',
    error: 'Error',
    information: 'Información',
    warning: 'Advertencia',
    evidencebased_arguments: 'Argumentos basados en pruebas',
    check_your_email_inbox_and_click_the_verification_: 'Compruebe su buzón de correo electrónico y haga clic en el enlace de verificación.',
    select_team_size: 'Seleccione el tamaño del equipo',
    debate_description: 'Descripción del debate',
    step: 'Paso',
    close: 'Cerrar',
    max_arguments: 'Argumentos máximos',
    hide_details: 'Ocultar detalles',
    verification_email_sent_successfully_check_your_in: '✅ ¡Email de verificación enviado correctamente! Compruebe su bandeja de entrada.',
    limited_access_verify_your_email: '⚔️ Acceso limitado - Verifique su correo electrónico',
    show_details: 'Mostrar detalles',
    sending: 'Enviando...',
    currently_unavailable: 'No disponible en estos momentos',
    creating_new_debates: 'Crear nuevos debates',
    resend_email: 'Reenviar mensaje de correo electrónico',
    available_now: 'Disponible ahora',
    tip_check_your_spam_folder_if_you_dont_see_the_ver: '💡 Consejo: ¡Revisa tu carpeta de spam si no ves el correo electrónico de verificación!',
    accessing_premium_features_future: 'Acceso a las funciones prémium (futuro)',
    receiving_email_notifications: 'Recibir notificaciones por correo electrónico',
    viewing_leaderboards: 'Visualización de tablas de clasificación',
    noble_username_your_account_has_limited_access_unt: 'Noble {username}, su cuenta tiene acceso limitado hasta que verifique su dirección de correo electrónico.',
    joining_debates_via_invite_links: 'Unirse a los debates a través de enlaces de invitación',
    participating_in_team_debates: 'Participar en debates de equipo',
    reading_rules_and_about_pages: 'Leyendo las reglas y sobre las páginas',
    browsing_the_cube: 'Navegar por',
    viewing_public_debates: 'Visualización de debates públicos',
    registration_successful: 'Registro Exitoso!',
    go_to_login: 'Acceder',
    passwords_do_not_match: 'Las contraseñas no coinciden',
    i_agree_to_the: 'Estoy de acuerdo con la ',
    weve_sent_a_verification_email_to_email: 'Hemos enviado un correo electrónico de verificación a {email}.',
    terms_of_service: 'Términos del servicio',
    login_here: 'Inicie sesión aquí',
    you_need_to_verify_your_email_address_before_you_c: 'Debe verificar su dirección de correo electrónico antes de poder {action}.',
    password_must_be_at_least_6_characters: 'La contraseña debe tener al menos 6 caracteres',
    your_account_has_been_created_successfully: '¡Su cuenta ha sido creada correctamente!',
    view_public_debates: 'Ver debates públicos',
    you_can_log_in_now_but_some_features_may_be_limite: 'Puedes iniciar sesión ahora, pero es posible que algunas funciones estén limitadas hasta que verifiques tu correo electrónico.',
    please_check_your_inbox_and_spam_folder_and_click_: 'Revisa tu bandeja de entrada (y la carpeta de correo no deseado) y haz clic en el enlace de verificación para activar tu cuenta.',
    browse_leaderboards: 'Navegar por las tablas de clasificación',
    what_you_can_do_while_unverified: 'Qué puedes hacer si no estás verificado:',
    please_check_your_email_and_click_the_verification: 'Revisa tu correo electrónico y haz clic en el enlace de verificación para continuar.',
    you_must_accept_the_terms_of_service_to_register: 'Debes aceptar los Términos de servicio para registrarte',
    weve_sent_a_verification_link_to_email: 'Enviamos un enlace de verificación a tu correo electrónico.',
    explore_the_cube: 'Explora el CUBO',
    create_or_participate_in_debates: 'Crear o participar en debates',
    overview: 'Resumen',
    enter_topic: 'Introduzca el título del tema',
    failed_to_generate_debate_views_please_try_again: 'Error al generar vistas de debate. Inténtalo de nuevo.',
    what_would_you_like_to_debate_about: '¿Sobre qué te gustaría debatir?',
    provide_additional_context_or_background_for_your_: 'Proporcione contexto o antecedentes adicionales para su debate...',
    customize: 'Personalizar',
    review_generated_views: 'Revisar vistas generadas',
    your_team_viewpoint: 'El punto de vista de su equipo',
    opponent_team_name: 'Nombre del equipo oponente',
    customize_prompt: 'Personalizar mensaje',
    customize_counter_prompt: 'Personalizar el indicador del contador',
    team_configuration: 'Asignación equipos',
    team_size: 'Empleados',
    please_enter_a_debate_topic: 'Por favor escribe un tema',
    create_debate: 'Crear debate',
    create_private_debate: 'Crear debate privado',
    create_public_debate: 'Crear debate público',
    invite_opponents: 'Invitar a los oponentes',
    share_invite_link: 'Compartir enlace de invitación',
    copy_invite_link: 'Copiar vínculo de invitación',
    invite_link_copied_to_clipboard: '¡Enlace copiado al portapapeles!',
    debatetype: 'debateType',
    your_team_name: 'Nombre de su equipo',
    description: 'Descripción',
    generate_views: 'Generar vistas',
    returntohome: 'returnToHome',
    public_spots: 'Lugares públicos',
    debate_created_successfully: '¡Debate creado con éxito!',
    introduction_to_honorable_debate: 'Introducción al debate honorable',
    issues_requiring_reformulation_no_penalty: 'Problemas que requieren reformulación (sin penalización)',
    productive_debate_relies_on_sound_reasoning_respec: 'El debate productivo se basa en un razonamiento sólido, un discurso respetuoso y la evitación de falacias lógicas y tácticas retóricas que socavan la comprensión. El sistema Jester\'s Gauntlet™ ayuda a identificar problemas que pueden descarrilar una conversación significativa, lo que permite a los participantes centrarse en la sustancia en lugar de la retórica.',
    when_your_message_contains_a_flagged_issue_youll_h: 'Cuando tu mensaje contenga un problema marcado, tendrás la oportunidad de reformularlo. Los problemas graves cuentan como penalizaciones para su puntaje de debate, mientras que otros problemas requieren una reformulación, pero no cuentan como penalizaciones. El equipo contrario puede levantar banderas que considere irrelevantes o solicitar una reformulación.',
    debate_misconduct_types: 'Tipos de mala conducta en el debate',
    these_are_overtly_disruptive_techniques_that_count: 'Estas son técnicas abiertamente disruptivas que cuentan como penalizaciones. 3 del mismo tipo o 5 en total perderán el debate.',
    debate_rules_summary: 'Resumen de las reglas del debate',
    counts_against_you: 'Cuenta en su contra',
    turnbased_structure: 'Estructura por turnos',
    teams_take_turns_making_arguments_each_team_has_an: 'Los equipos se turnan para hacer argumentos. Cada equipo tiene la oportunidad de presentar su punto de vista y responder a los puntos del equipo contrario.',
    serious_issues_penalties: 'Problemas graves (penalizaciones)',
    our_ai_moderator_analyzes_messages_for_common_deba: 'Nuestro moderador de IA analiza los mensajes en busca de delitos menores de debate comunes. Cuando identifica problemas potenciales, los clasifica en dos tipos:',
    flagged_messages: 'Mensajes marcados',
    reformulation_process: 'Proceso de reformulación',
    when_the_ai_detects_a_jesters_gauntlet_in_a_messag: 'Cuando la IA detecta un Jester\'s Gauntlet™ en un mensaje, se marca. El equipo contrario puede levantar banderas que considere irrelevantes o solicitar una reformulación.',
    the_team_with_a_flagged_message_must_correct_the_i: 'El equipo con un mensaje marcado debe corregir los problemas identificados antes de que el debate pueda continuar, asegurando un discurso de alta calidad.',
    conceding: 'Concedente',
    a_team_may_concede_the_debate_at_any_time_which_im: 'Un equipo puede conceder el debate en cualquier momento, lo que inmediatamente termina el debate con la victoria del equipo contrario.',
    using_formatting_like_all_caps_to_intimidate_rathe: 'Usar el formato (como TODAS LAS MAYÚSCULAS) para intimidar en lugar de discutir',
    using_excessive_mockery_that_poisons_discourse: 'Usar burlas excesivas que envenenan el discurso',
    personal_attacks_that_derail_focus_from_the_argume: 'Ataques personales que desvían la atención del argumento',
    misrepresenting_someones_words_to_create_a_weaker_: 'Tergiversar las palabras de alguien para crear un argumento más débil para atacar',
    avoiding_the_argument_by_focusing_on_trivial_error: 'Evitar el argumento centrándose en errores triviales',
    ad_hominem: 'Ad Hominem',
    whataboutism: 'Whataboutism',
    jesters_gauntlet_penalties: 'Penalizaciones de Jester\'s Gauntlet™',
    if_there_are_no_posts_for_a_week_the_ai_will_summa: 'Si no hay publicaciones durante una semana, la IA resumirá el debate e intentará determinar un ganador en función de la calidad del argumento, o lo declarará empatado.',
    strawman: 'Falacia del hombre de paja',
    changing_criteria_of_an_argument_when_original_cri: 'Cambiar los criterios de un argumento cuando se cumplen los criterios originales',
    if_a_team_accumulates_3_of_the_same_type_of_seriou: 'Si un equipo acumula 3 del mismo tipo de delito menor grave O 5 delitos menores graves totales, pierde automáticamente el debate.',
    deflecting_from_the_current_point_by_bringing_up_u: 'Desviarse del punto actual al plantear cuestiones no relacionadas',
    sealioning: 'Sellado',
    inactive_debates: 'Debates inactivos',
    relentless_questioning_that_exhausts_rather_than_e: 'Preguntas implacables que agotan en lugar de involucrar',
    misrepresenting_someones_argument_to_create_a_weak: 'Tergiversar el argumento de alguien para crear una versión más débil para atacar',
    gish_gallop: 'Galope Gish',
    sarcasm_overload: 'Sobrecarga de sarcasmo',
    distorting_quotes_out_of_context_to_mislead_delibe: 'Distorsionar citas fuera de contexto para engañar deliberadamente',
    word_twisting: 'Torsión de palabras',
    textual_intimidation: 'Intimidación textual',
    quote_sniping: 'Francotirador de presupuestos',
    pretending_not_to_understand_to_stall_or_derail_di: 'Fingir no entender para detener o descarrilar la discusión',
    using_irrelevant_citations_or_authorities_that_can: 'Usar citas o autoridades irrelevantes que puedan reemplazarse',
    appeal_to_emotion: 'Apelar a la emoción',
    appeal_to_authority: 'Apelación a la autoridad',
    requires_reformulation: 'Requiere reformulación',
    making_bold_claims_without_evidence_or_substance: 'Hacer afirmaciones audaces sin pruebas ni sustancia',
    forcing_an_eitheror_choice_to_oversimplify: 'Forzar una opción de "o lo uno o lo otro" para simplificar en exceso',
    serious_issues_count_as_penalties: 'Problemas graves (contar como penalizaciones)',
    false_dichotomy: 'Falsa dicotomía',
    internal_code: 'Código Interno:',
    grammar_policing: 'Grammar Policing',
    circular_reasoning: 'Razonamiento circular',
    applying_doubt_unevenly_based_on_bias: 'Aplicar la duda de manera desigual en función del sesgo',
    overgeneralizations: 'Sobregeneralizaciones',
    using_selective_evidence_that_is_misleading_but_co: 'Uso de evidencia selectiva que es engañosa pero corregible',
    better_approach: 'Mejor enfoque:',
    example_of_type: 'Ejemplo de {type}:',
    feigning_confusion: 'Fingir confusión',
    overwhelming_with_too_many_points_or_links_to_prev: 'Abrumador con demasiados puntos o enlaces para evitar una respuesta significativa',
    sweeping_statements_that_lack_precision_but_can_be: 'Afirmaciones generales que carecen de precisión pero que se pueden refinar',
    selective_skepticism: 'Escepticismo selectivo',
    unsubstantiated_claims: 'Afirmaciones sin fundamento',
    shifting_goalposts: 'Cambio de postes de meta',
    assuming_the_conclusion_in_the_premise: 'Asumiendo la conclusión en la premisa',
    using_emotional_appeals_that_bypass_logic_but_can_: 'Usar apelaciones emocionales que eluden la lógica pero que pueden reformularse',
    cherrypicking: 'selección exclusiva',
    has_challenged_you_to_a: 'te ha desafiado a un',
    the_gauntlet_has_been_thrown: '¡El guantelete ha sido lanzado!',
    on_the_topic: 'sobre el tema',
    debate_of_honor: 'Debate de honor',
    this_debate_will_only_be_visible_to_participants: 'Este debate solo será visible para los participantes.',
    your_opponents_position: 'La posición de su oponente',
    this_debate_will_be_visible_to_anyone_visiting_gau: 'Este debate será visible para cualquiera que visite Gauntlet Debates™ y contará para su puntaje público.',
    your_position: 'Tu Posición',
    prepare_your_defense: 'Prepare su defensa',
    edit_as_needed: 'Según sea necesario',
    read_carefully_what_youll_be_defending_the_first_d: '¡Lee atentamente lo que defenderás! El primer acto deshonroso podría ocultarse en el punto de vista preformulado. Tómate un momento para editarlo y alinearlo con lo que realmente quieres defender.',
    your_battle_name: 'Tu nombre de batalla',
    decline_challenge: 'Rechazar Desafío',
    invalid_or_expired_invitation_link: 'El enlace no es válido o ha caducado',
    successfully_joined_the_debate: '¡Se ha unido con éxito al debate!',
    accept_the_challenge: '¡Acepta el reto!',
    invalid_invitation_link: 'Invitación no válida',
    failed_to_join_debate: 'No se ha podido unir al debate',
    please_enter_a_team_name: 'Por favor, introduce un nombre de equipo',
    please_login_to_view_this_invitation: 'Inicia sesión para ver esta invitación',
    please_login_or_register_to_join_this_debate: 'Inicie sesión o regístrese para unirse a este debate',
    enter_a_name_that_inspires_fear_in_your_opponent: 'Introduce un nombre que inspire miedo en tu oponente',
    you_must_be_logged_in_to_accept_this_challenge_you: 'Debes iniciar sesión para aceptar este desafío. Se te redirigirá a iniciar sesión.',
    flag_type: 'Tipo de bandera',
    concede_defeat: 'Concede la derrota',
    are_you_sure_you_want_to_concede_this_debate_your_: '¿Está seguro de que desea conceder este debate? Tu equipo será considerado como el perdedor.',
    debate_results: 'Resultados del debate',
    this_debate_has_concluded: 'Este debate ha concluido.',
    debate_completed: 'Debate completado',
    the_winner_is: 'El ganador es',
    view_summary: 'Ver reserva',
    a_summary_of_the_debate_has_been_generated_and_is_: 'Se ha generado un resumen del debate que se muestra en la parte superior de la página.',
    reformulation_needed: 'Quizá haga falta reformular:',
    jesters_gauntlet_detected: 'Guantelete de Jester detectado:',
    options: 'Opciones:',
    reformulate_your_message: 'Reformule su mensaje',
    message_flags: 'Indicadores del mensaje',
    this_message_contains_language_or_reasoning_that_r: 'Este mensaje contiene lenguaje o razonamiento que requiere reformulación.',
    back_home: 'Volver a Inicio',
    concede_debate: 'Concede el debate',
    swap_views: 'Intercambiar vistas',
    rank: 'Pos.',
    view_top_performers_in_public_debates_only_metrics: 'Ver los mejores resultados en debates públicos. Solo las métricas de los debates públicos se cuentan para estas clasificaciones.',
    ranking_by: 'Clasificación por',
    top_debates: 'Principales debates',
    top_debaters: 'Top Debaters',
    win_rate: 'Tasa de ganancia',
    rebuttal_effectiveness: 'Eficacia de la refutación',
    excalibur_index: 'Índice Excalibur',
    explanation_strength_hard_to_vary: 'Punto fuerte de la explicación (difícil de variar)',
    logical_coherence: 'Coherencia lógica',
    message_count: 'Recuento de Mensajes',
    civil_discourse: 'un diálogo civilizado.',
    topic: 'Tema',
    steelmanning_ability: 'Habilidad de Steelmanning',
    explanation_quality: 'Calidad de la explicación',
    overall_quality: 'Calidad general',
    date: 'Fecha',
    no_users_found_be_the_first_to_participate_in_publ: 'No se han encontrado usuarios. ¡Sé el primero en participar en debates públicos!',
    wins: 'Ganancias',
    user: 'Usuario',
    argument_clarity: 'Claridad del argumento',
    evidence_quality: 'Calidad de evidencia',
    no_debates_found_be_the_first_to_create_a_public_d: 'No se han encontrado debates. ¡Sé el primero en crear un debate público!',
    key_features: 'Características principales',
    our_mission: 'Nuestra Misión',
    structured_debate: 'Debate estructurado:',
    fair_resolutions: 'Resoluciones justas',
    throwing_down_the_gauntlet: 'Lanzando el guante',
    honorbased_scoring: 'Puntuación basada en el honor',
    comprehensive_summaries_of_debates_serve_as_a_valu: 'Los resúmenes completos de los debates sirven como un recurso valioso para comprender temas complejos y acceder a las mejores explicaciones actuales.',
    when_someone_makes_a_claim_you_believe_should_be_e: 'Cuando alguien hace una afirmación que crees que debe ser examinada con más cuidado, puedes "tirar el guante" invitándolo a defender su posición en un debate honorable.',
    argument_repository: 'Repositorio de argumentos',
    this_ancient_practice_of_challenging_someone_to_pr: 'Esta antigua práctica de desafiar a alguien a probar sus afirmaciones encuentra nueva vida en nuestra plataforma, donde el enfoque está en la sustancia más que en la retórica. Comparta un enlace de debate como un desafío honorable: "¿Cree que puede defender lo que acaba de decir en una conversación estructurada y justa?"',
    clear_guidelines_to_ensure_fair_and_focused_discus: 'Directrices claras para garantizar discusiones justas y centradas entre puntos de vista opuestos.',
    opposing_teams_have_the_ability_to_lift_flags_on_m: 'Los equipos opositores tienen la capacidad de levantar banderas en mensajes que violan las reglas de debate o están de acuerdo con las banderas y solicitar una reformulación.',
    gauntlet_debates_addresses_a_fundamental_problem_i: 'Gauntlet Debates aborda un problema fundamental en nuestro discurso digital: la prevalencia de malos argumentos y tácticas retóricas ("Jester\'s Gauntlets") que tienen como objetivo ganar debates sin tener en cuenta la verdad o la comprensión. Estas tácticas dividen a las personas en tribus que valoran ganar sobre descubrir lo que es verdad, lo que hace cada vez más difícil tener conversaciones productivas sobre temas complejos.',
    we_believe_democracy_depends_on_our_ability_to_eng: 'QUERY LENGTH LIMIT EXCEEDED. MAX ALLOWED QUERY : 500 CHARS',
    aipowered_detection_of_logical_fallacies_and_debat: 'Detección impulsada por IA de falacias lógicas y delitos menores de debate en tiempo real.',
    about_gauntlet_debates: 'Acerca de Gauntlet Debates™',
    a_point_system_that_rewards_quality_arguments_and_: 'Un sistema de puntos que premia los argumentos de calidad y penaliza las falacias del debate.',
    a_platform_for_structured_aimoderated_debates_that: 'Una plataforma para debates estructurados y moderados por IA que fomentan un discurso reflexivo',
    how_it_works: 'Cómo funciona',
    performance_component_25: 'Componente de rendimiento (25%)',
    learning_curve: 'Curva de aprendizaje',
    good_faith_component_15: 'Componente de buena fe (15%)',
    '2_strongpresent_your_argumentsstrong_each_team_tak': '2. <strong>Presente sus argumentos:</strong> cada equipo se turnará para presentar argumentos en apoyo de su punto de vista. La IA supervisará las falacias y debatirá las tácticas que violen las pautas.',
    stronghow_its_calculatedstrong_the_excalibur_index: '<strong>Cómo se calcula:</strong> El Índice Excalibur combina tres componentes clave:',
    calculated_from_win_rate_and_scaled_by_participati: 'Calculado a partir de la tasa de ganancias y escalado por nivel de participación, recompensando el éxito constante.',
    '3_strongmaintain_debate_qualitystrong_if_a_message': '3. <strong>Mantener la calidad del debate:</strong> si se marca un mensaje, los equipos contrarios pueden levantar banderas que consideren irrelevantes o pedir una reformulación.',
    we_maintain_separate_public_and_private_indices_to: 'Mantenemos índices públicos y privados separados para garantizar que los debatidores puedan practicar en entornos privados sin afectar su clasificación pública. Solo el Índice Excalibur público se muestra en las tablas de clasificación.',
    evolving_with_our_community: 'Evolucionando con nuestra comunidad',
    measures_steelmanning_ability_85_representing_oppo: 'Mide la capacidad de la mano de obra siderúrgica (85%): representa de manera justa los puntos de vista opuestos y la puntualidad de la respuesta (15%).',
    quality_component_60: 'Componente de calidad (60%)',
    the_excalibur_index: 'El Índice Excalibur',
    based_on_explanation_strength_40_following_david_d: 'Basado en la fortaleza de la explicación (40%) siguiendo el concepto de explicaciones de David Deutsch que son "difíciles de variar", la efectividad de la refutación (25%), la calidad de la evidencia (20%) y la claridad de los argumentos (15%) según lo evaluado por AI.',
    unlike_typical_online_arguments_our_platform_rewar: 'A diferencia de los argumentos típicos en línea, nuestra plataforma recompensa a los participantes intelectualmente honestos que pueden articular sus puntos de vista claramente sin recurrir a falacias. El sistema "Jester\'s Gauntlets" distingue entre los debatientes honorables y los que se basan en trucos retóricos.',
    we_actively_encourage_users_to_identify_new_rhetor: 'Animamos activamente a los usuarios a identificar nuevas tácticas retóricas que deberían clasificarse como Guanteletes de Jester. Sus comentarios dan forma directa a la evolución de nuestro sistema.',
    the_excalibur_index_is_our_comprehensive_metric_fo: 'El Índice Excalibur es nuestra métrica integral para medir la excelencia del debate. Así como la espada legendaria solo podía ser empuñada por los verdaderamente dignos, nuestro índice reconoce a aquellos que sobresalen en el debate honorable.',
    the_jesters_gauntlet_system_like_any_aipowered_too: 'El sistema Jester\'s Gauntlet™, como cualquier herramienta impulsada por IA, es un trabajo en constante evolución. Reconocemos varios aspectos importantes sobre nuestra plataforma:',
    communitydriven_improvement: 'Mejora impulsada por la comunidad',
    '5_stronginsightful_summaries_final_scoresstrong_ea': '5. <strong>Resúmenes perspicaces y puntuaciones finales:</strong> cada debate concluye con un resumen generado por IA que destaca los mejores argumentos de ambas partes, junto con las puntuaciones finales que reflejan el rendimiento de cada equipo. Esta combinación de evaluación cualitativa y puntuación cuantitativa hace de nuestra plataforma un valioso repositorio de perspectivas bien razonadas sobre temas complejos.',
    strongtechnical_implementationstrong_the_excalibur: '<strong>Implementación técnica:</strong> El Índice Excalibur utiliza actualmente ChatGPT-4o (el último modelo de OpenAI) para evaluar la calidad del debate, la solidez de los argumentos y la capacidad de la mano de obra siderúrgica. A medida que la tecnología de IA evoluciona, podemos actualizar nuestros modelos para mantener los más altos estándares de evaluación, siempre priorizando la imparcialidad y la profundidad del análisis.',
    there_is_a_learning_period_when_first_using_the_pl: 'Hay un periodo de aprendizaje cuando se utiliza por primera vez la plataforma. A medida que te familiarices con nuestras pautas, desarrollarás un sentido natural para elaborar argumentos que se centren en la sustancia en lugar de la retórica.',
    the_excalibur_index_is_displayed_on_a_scale_of_010: 'El índice Excalibur se muestra en una escala de 0 a 100 con un decimal de precisión, lo que permite hacer distinciones precisas entre los que debaten. Una puntuación de 100 representa la perfección en todas las métricas de debate, un ideal prácticamente inalcanzable que da espacio para el crecimiento incluso a los debatientes más consumados.',
    '1_strongcreate_or_join_a_debatestrong_start_a_new_': '1. <strong>Crea o únete a un debate:</strong> inicia un nuevo debate con un tema claro y puntos de vista opuestos, o únete a uno existente.',
    '4_strongconclude_the_debatestrong_debates_can_end_': '4. <strong>Concluir el debate:</strong> los debates pueden terminar de varias maneras: cuando un equipo concede, cuando un equipo acumula demasiadas penalizaciones o automáticamente después de una semana de inactividad (en cuyo caso la IA analizará el debate para determinar un ganador o declararlo empate).',
    check_public_debates: 'Consulta los debates públicos',
    false_positives: 'Falsos positivos',
    when_a_message_is_flagged_view_it_as_an_opportunit: 'Cuando se marca un mensaje, véalo como una oportunidad en lugar de un revés. El proceso de reformulación a menudo conduce a argumentos más fuertes y claros que sirven mejor al propósito final: comprender temas complejos a través de un discurso reflexivo.',
    join_our_community_of_thoughtful_debaters_and_enga: 'Únase a nuestra comunidad de personas que debaten atentamente y participe en debates significativos sobre temas que importan.',
    start_a_new_debate: 'Iniciar un nuevo debate',
    sometimes_messages_may_be_incorrectly_flagged_when: 'A veces, los mensajes pueden estar marcados incorrectamente. Cuando esto suceda, recuerde que la reformulación es una oportunidad para aclarar su punto, y los equipos opuestos pueden levantar banderas que creen que son injustificadas.',
    our_system_will_likely_never_be_perfect_and_thats_: 'Es probable que nuestro sistema nunca sea perfecto, y eso es por diseño. La naturaleza del lenguaje y la retórica es demasiado compleja para las reglas absolutas. Lo que importa es nuestro compromiso con la mejora continua y el objetivo compartido de elevar la calidad del debate. Creemos que incluso las barandillas imperfectas pueden guiarnos hacia conversaciones más productivas que no tener estándares en absoluto.',
    ready_to_start_debating: '¿Listo para empezar a debatir?',
    eg_artificial_consciousness_is_achievable_through_: 'Por ejemplo, la conciencia artificial puede lograrse con los métodos actuales de aprendizaje automático.',
    description_optional: 'Descripción (opcional)',
    add_context_constraints_or_specific_aspects_you_wa: 'Añada contexto, limitaciones o aspectos específicos que desee que se aborden en el debate.',
    use_this_to_provide_additional_context_we_recommen: 'Utilícelo para proporcionar un contexto adicional. Recomendamos entre 150 y 300 caracteres para obtener mejores resultados.',
    tips_for_a_productive_debate: 'Consejos para un debate productivo:',
    specify_a_focused_topic_that_can_be_argued_effecti: 'Especificar un tema concreto que pueda argumentarse eficazmente desde posiciones opuestas.',
    choose_a_topic_that_can_lead_to_focused_welldefine: 'Elija un tema que pueda dar lugar a opiniones concretas y bien definidas. Los temas específicos (por ejemplo, "¿Deberían aumentarse las subvenciones a las energías renovables en la UE?") funcionan mejor que los demasiado amplios (por ejemplo, "Cambio climático").',
    our_ai_has_generated_two_opposing_viewpoints_for_y: 'Nuestra IA ha generado dos puntos de vista opuestos para tu tema de debate. Puede editar estos puntos de vista para reflejar mejor las perspectivas que desea explorar.',
    review_and_customize_debate_views: 'Revisar y personalizar las vistas del debate',
    view_1_you_will_defend_this_position: 'Opinión 1 (Usted defenderá esta posición):',
    view_2_your_opponent_will_defend_this_position: 'Vista 2 (Su oponente defenderá esta posición):',
    your_debate_will_be_visible_to_everyone_your_argum: 'Tu debate será visible para todos. Tus argumentos y estilo de debate contribuirán a tu puntuación oficial en la tabla de clasificación. Esto ayuda a otros interesados en el tema a encontrar explicaciones de alta calidad.',
    number_of_spots_that_can_be_filled_by_any_public_u: 'Número de plazas que puede ocupar cualquier usuario público. El máximo es el tamaño del equipo rival.',
    private_debate: 'Debate privado',
    your_debate_will_only_be_visible_to_participants_t: 'Tu debate sólo será visible para los participantes. Es perfecto para practicar o debatir sobre temas delicados.',
    the_maximum_number_of_members_allowed_in_the_oppon: 'El número máximo de miembros permitidos en el equipo adversario. Por defecto es 1.',
    configure_teams_and_debate_settings: 'Configuración de equipos y debates',
    the_maximum_number_of_members_allowed_in_your_team: 'El número máximo de miembros permitidos en tu equipo, incluyéndote a ti. Por defecto es 1 (sólo tú).',
    debate_visibility: 'Visibilidad del debate',
    increase_team_size_to_allow_public_spots: 'Aumentar el tamaño de los equipos para permitir plazas públicas',
    debate_information: 'Información sobre el debate',
    members: 'miembros',
    review_all_details_below_when_you_create_the_debat: 'Revise todos los detalles a continuación. Cuando crees el debate, recibirás enlaces de invitación para compartir con los miembros de tu equipo y tu oponente.',
    member: 'miembro',
    your_viewpoint: 'Su punto de vista:',
    including_you: '(incluyéndote a ti)',
    debate_overview: 'Debate general',
    opponent_viewpoint: 'Punto de vista del oponente:',
    visibility: 'Visibilidad:',
    invite_opponent: 'Invitar al adversario',
    share_this_link_with_people_you_want_to_join_your_: 'Comparte este enlace con las personas que quieras que se unan a tu equipo:',
    share_this_link_with_the_person_you_want_to_debate: 'Comparte este enlace con la persona contra la que quieras debatir:',
    your_debate_has_been_created_successfully: '¡Tu debate se ha creado correctamente!',
    invite_members_to_your_team: 'Invite a miembros a su equipo',
    the_links_will_work_until_your_debate_is_completed: 'Los enlaces funcionarán hasta que finalice el debate. Todos los participantes tendrán que registrarse o iniciar sesión para unirse.',
    share_this_link_with_the_people_you_want_to_debate: 'Comparte este enlace con las personas contra las que quieras debatir:',
    share: 'Compartir',
    go_to_debate_room: 'Ir a la sala de debate',
    copy_opponents_link: 'Copiar enlace del oponente',
    copy_opponent_link: 'Copiar enlace del oponente',
    your_team: 'Su equipo:',
    opponent_team: 'Equipo adversario:',
    return_to_home: 'Volver a la página de inicio',
    in_progress: 'En curso',
    messages: 'Mensajes:',
    team_members: 'Miembros del equipo',
    status: 'Estado:',
    last_message: 'Último mensaje:',
    jesters_gauntlet_counts: 'El Guantelete del Bufón Cuenta',
    no_messages_yet: 'Aún no hay mensajes',
    no_penalties: 'Sin sanciones',
    about_penalties: 'Sobre las sanciones:',
    started: 'Comenzó:',
    back_to_home: 'Volver al inicio',
    no_messages_yet_start_the_debate_by_sending_a_mess: 'Aún no hay mensajes. Inicia el debate enviando un mensaje.',
    teams_must_reformulate_messages_that_contain_issue: 'Los equipos deben reformular los mensajes que contengan problemas. Los Guanteletes del Bufón graves cuentan como penalizaciones, mientras que las faltas leves sólo requieren reformulación. El equipo contrario puede levantar banderas cuando las considere irrelevantes.',
    waiting_for_the_first_team_to_start_the_debate: 'A la espera de que el primer equipo inicie el debate.',
    youve_chosen_to_reformulate_your_message_please_ad: 'Ha optado por reformular su mensaje. Por favor, aborda las cuestiones señaladas en tu respuesta.',
    the_other_team_has_requested_that_you_reformulate_: 'El otro equipo le ha pedido que reformule su mensaje. Por favor, resuelva los problemas señalados en su respuesta.',
    choose_to_reformulate_your_message_now: 'Elija reformular su mensaje ahora.',
    if_there_are_penalty_flags_reformulating_will_not_: 'Si hay banderas de penalización, reformular NO eliminará la penalización. Sólo el otro equipo que levante todas las banderas puede hacerlo.',
    teams_lose_if_they_accumulate_3_penalties_of_the_s: 'Los equipos pierden si acumulan 3 penalizaciones del mismo tipo o 5 penalizaciones en total. Los problemas menores requieren reformulación pero no cuentan para tu puntuación.',
    realtime_connection_established: 'Conexión en tiempo real establecida',
    the_other_team_has_chosen_to_reformulate_their_mes: 'El otro equipo ha optado por reformular su mensaje',
    reformulation_request_sent_waiting_for_the_other_t: 'Solicitud de reformulación enviada. Esperando a que el otro equipo reformule.',
    all_flags_have_been_lifted: 'Se han izado todas las banderas',
    you_have_chosen_to_reformulate_your_message: 'Ha optado por reformular su mensaje.',
    failed_to_lift_some_flags_please_try_again: 'No se han podido levantar algunas banderas. Por favor, inténtelo de nuevo.',
    failed_to_send_reformulation_request: 'No se ha podido enviar la solicitud de reformulación.',
    you_have_conceded_the_victory_goes_to: 'Has cedido. La victoria es para',
    failed_to_concede_the_debate_please_try_again: 'No ha concedido el debate. Por favor, inténtelo de nuevo.',
    failed_to_export_debate: 'Debate fallido sobre la exportación',
    link_copied: 'Enlace copiado',
    copy_debate_link: 'Copiar enlace del debate',
    export_debate: 'Debate sobre la exportación',
    lift_all_flags: 'Izar todas las banderas',
    request_reformulation: 'Solicitar reformulación',
    send_message: 'Enviar mensaje',
    waiting: 'En espera',
    is_typing: 'está escribiendo',
    and: 'y',
    others: 'otros',
    loading_debate: 'Cargando debate...',
    debate_not_found: 'Debate no encontrado',
    error_loading_debate: 'Error al cargar el debate',
    retry: 'Reintentar',
    login: 'Inicio de sesión',
    please_login_to_participate_in_this_debate: 'Inicie sesión para participar en este debate',
    error_lifting_flag_missing_message_id: 'Bandera de levantamiento de error: Falta ID de mensaje',
    failed_to_initiate_reformulation: 'No se ha iniciado la reformulación.',
    flag_has_been_lifted: 'Se ha izado la bandera',
    error_lifting_flag_please_try_again: 'Error al levantar la bandera. Por favor, inténtelo de nuevo.',
    could_not_identify_opposing_team: 'No se pudo identificar al equipo contrario',
    there_was_an_issue_updating_the_debate_status_on_t: 'Se ha producido un problema al actualizar el estado del debate en el servidor, pero el debate se ha marcado como finalizado localmente.',
    failed_to_end_the_debate_after_concession_please_t: 'No ha podido finalizar el debate tras la concesión. Por favor, inténtelo de nuevo.',
    only_the_first_team_can_start_the_debate: 'Sólo el primer equipo puede iniciar el debate',
    message_sent_successfully: '¡Mensaje enviado con éxito!',
    copy_link: 'Copiar enlace',
    i_prefer_to_reformulate: 'Prefiero reformular',
    are_you_sure_you_want_to_concede_this_debate_this_: '¿Estás seguro de que quieres conceder este debate? Esta acción no se puede deshacer.',
    analyzing: 'Analizar...',
    you_requested_the_other_team_to_reformulate_this_m: 'Ha pedido al otro equipo que reformule este mensaje.',
    by: 'por',
    recommendation: 'Recomendación:',
    action_required: 'Acción requerida:',
    reformulation_requested: 'Reformulación solicitada:',
    the_other_teams_message_has_been_flagged: 'El mensaje del otro equipo ha sido marcado.',
    your_message_has_been_flagged_again_you_can: 'Su mensaje ha sido marcado de nuevo. Puedes hacerlo:',
    selfinitiated_reformulation: 'Reformulación por iniciativa propia:',
    please_review_the_flags_and_either_lift_all_flags_: 'Por favor, revise las banderas y, o bien levante todas las banderas (si cree que no son aplicables), o bien solicite una reformulación (si el mensaje necesita ser mejorado).',
    '1_wait_for_the_other_team_to_review_the_flags_and_': '1. Esperar a que el otro equipo revise las banderas y decida, o',
    your_most_recent_message_has_been_flagged_you_can: 'Tu mensaje más reciente ha sido marcado. Puedes hacerlo:',
    your_message_has_been_flagged_and_needs_reformulat: 'Su mensaje ha sido marcado y necesita ser reformulado.',
    '2_choose_to_reformulate_your_message_again': '2. Opta por reformular de nuevo tu mensaje.',
    reformulation_requested_wait_for_your_turn_to_subm: 'Reformulación solicitada. Espere su turno para enviar un nuevo mensaje.',
    please_submit_an_improved_message_that_addresses_t: 'Envíe un mensaje mejorado que aborde los problemas señalados.',
    waiting_for_the_other_team_to_send_a_message: 'Esperando a que el otro equipo envíe un mensaje.',
    waiting_for_them_to_submit_an_improved_message_tha: 'A la espera de que envíen un mensaje mejorado que aborde el problema señalado.',
    current_turn: 'Giro actual',
    aboutpagekeyfeaturesargumentrepository: 'aboutPage.keyFeatures.argumentRepository',
    aboutpagekeyfeaturesargumentrepositorydescription: 'aboutPage.keyFeatures.argumentRepositoryDescription',
    '2_present_your_arguments_each_team_takes_turns_pre': '2. Presenta tus argumentos: Cada equipo presenta por turnos argumentos en apoyo de su punto de vista. La IA vigilará las falacias y las tácticas de debate que infrinjan las directrices.',
    '1_create_or_join_a_debate_start_a_new_debate_with_': '1. Crear o unirse a un debate: Inicie un nuevo debate con un tema claro y puntos de vista opuestos, o únase a uno ya existente.',
    '3_maintain_debate_quality_if_a_message_is_flagged_': '3. Mantener la calidad del debate: Si un mensaje está marcado, los equipos contrarios pueden levantar las banderas que consideren irrelevantes o pedir una reformulación.',
    technical_implementation_the_excalibur_index_curre: 'Implementación técnica: El Índice Excalibur utiliza actualmente ChatGPT-4o (el último modelo de OpenAI) para evaluar la calidad del debate, la fuerza de los argumentos y la capacidad de acertar. A medida que evolucione la tecnología de IA, podremos actualizar nuestros modelos para mantener los más altos estándares de evaluación, priorizando siempre la imparcialidad y la profundidad del análisis.',
    how_its_calculated_the_excalibur_index_combines_th: 'Cómo se calcula: El Índice Excalibur combina tres componentes clave:',
    imperfect_ai_judgments: 'Juicios imperfectos de la IA',
    both_our_jesters_gauntlet_system_and_cube_reposito: 'Tanto nuestro sistema Jester\'s Gauntlet™ como el repositorio CUBE son obras en constante evolución, moldeadas por las ideas y experiencias de nuestra comunidad. Reconocemos varios aspectos importantes de nuestra plataforma:',
    '5_insightful_summaries_final_scores_each_debate_co': '5. Resúmenes perspicaces y puntuaciones finales: Cada debate concluye con un resumen generado por IA que destaca los mejores argumentos de ambas partes, junto con puntuaciones finales que reflejan el rendimiento de cada equipo. Esta combinación de evaluación cualitativa y puntuación cuantitativa convierte a nuestra plataforma en un valioso repositorio de perspectivas bien razonadas sobre temas complejos.',
    '4_conclude_the_debate_debates_can_end_in_several_w': '4. Concluir el debate: Los debates pueden terminar de varias maneras: cuando un equipo concede, cuando un equipo acumula demasiadas penalizaciones, o automáticamente después de una semana de inactividad (en cuyo caso la IA analizará el debate para determinar un ganador o declararlo un empate).',
    neither_system_will_likely_ever_be_perfect_and_tha: 'Es probable que ninguno de los dos sistemas sea nunca perfecto, y eso es por diseño. La naturaleza del lenguaje, la retórica y el conocimiento es demasiado compleja para establecer reglas absolutas. Lo que importa es nuestro compromiso con la mejora continua y los objetivos compartidos de elevar la calidad del debate y crear un valioso repositorio de conocimientos. Creemos que incluso los sistemas imperfectos pueden guiarnos hacia conversaciones más productivas y un mejor entendimiento que la ausencia total de normas.',
    sometimes_messages_may_be_incorrectly_flagged_or_c: 'A veces los mensajes pueden ser marcados incorrectamente, o los retos CUBE pueden ser evaluados de forma que parezcan cuestionables. Cuando esto ocurra con mensajes marcados, recuerda que la reformulación es una oportunidad para aclarar tu punto de vista, y los equipos contrarios pueden levantar las marcas que consideren injustificadas. En el caso de los desafíos CUBE, los comentarios de la comunidad nos ayudan a perfeccionar nuestros criterios de evaluación.',
    gauntlet_debates: 'Gauntlet Debates™',
    discover_the_current_best_explanations_on_any_topi: 'Descubra las mejores explicaciones actuales sobre cualquier tema a partir de nuestros debates de mayor calidad.',
    see_how_you_rank_among_debaters_and_track_your_pro: 'Consulta tu clasificación entre los debatientes y sigue tu progreso en el Índice Excalibur.',
    my_dashboard: 'Mi panel de control',
    welcome_back_testgd4: '¡Bienvenido de nuevo, testgd4! 👋',
    your_overall_performance_across_all_debates: 'Su rendimiento general en todos los debates',
    total_debates: 'Total Debates',
    search_topics: 'Buscar temas...',
    cube_current_best_explanations: 'CUBE - Las mejores explicaciones actuales',
    discover_the_highest_quality_explanations_on_topic: 'Descubra las explicaciones de mayor calidad sobre los temas que le interesan',
    cube_explanations: 'CUBE Explicaciones',
    explanations: 'Explicaciones',
    welcome_back_testgd5: '¡Bienvenido de nuevo, testgd5! 👋',
    welcome_back_testgd7: '¡Bienvenido de nuevo, testgd7! 👋',
    welcome_back_testgd8: '¡Bienvenido de nuevo, testgd8! 👋',
    welcome_back_testgd: '¡Bienvenido de nuevo, TestGD! 👋',
    welcome_back_testgd3: '¡Bienvenido de nuevo, TestGD3! 👋',
    settingsPage: {
      title: 'Configuración de usuario',
      loadingSettings: 'Cargando ajustes...',
      loginRequired: 'Inicie sesión para ver su configuración',
      failedToLoadSettings: 'No se ha podido cargar la configuración. Vuelve a intentarlo más tarde.',
      failedToSaveSettings: 'No se ha podido guardar la configuración. Vuelve a intentarlo más tarde.',
      settingsSavedSuccessfully: '¡Ajustes guardados correctamente!',
      saving: 'Ahorrar...',
      saveSettings: 'Guardar ajustes',
      emailNotifications: 'Notificaciones por correo electrónico',
      emailNotificationsDescription: 'Configura cuándo quieres recibir notificaciones por correo electrónico.',
      notifyOnTurn: 'Notificarme cuando sea mi turno y no haya contestado en 24 horas',
      notifyOnDebateEndingSoon: 'Notificarme cuando un debate vaya a terminar pronto y sea mi turno',
      notifyOnReformulation: 'Notificarme cuando se pida a nuestro equipo que reformule un mensaje',
      emailAddress: 'Dirección de correo electrónico',
      currentEmailAddress: 'Su dirección de correo electrónico actual:',
    },
  },
  de: {
    common: {
      home: 'Startseite',
      newDebate: 'Neue Debatte',
      yourDebates: 'Ihre Debatten',
      publicDebates: 'Öffentliche Debatten',
      leaderboards: 'Bestenlisten',
      cube: 'CUBE',
      rules: 'Regeln',
      about: 'Über uns',
      login: 'Anmelden',
      register: 'Registrieren',
      logout: 'Abmelden',
      settings: 'Einstellungen',
      create: 'Erstellen',
      join: 'Beitreten',
      view: 'Siehe',
      edit: 'Bearbeiten',
      delete: 'Löschen',
      save: 'Speichern Sie',
      cancel: 'Abbrechen',
      submit: 'Einreichen',
      back: 'Zurück',
      next: 'Weiter',
      refresh: 'Auffrischen',
      search: 'Suche',
      active: 'Aktiv',
      completed: 'Abgeschlossen',
      pending: 'Anhängig',
      loading: 'Daten laden...',
      welcome: 'Herzlich willkommen!',
      emailVerification: 'E-Mail-Überprüfung',
      required: 'Erforderlich',
      optional: 'Optional',
      activeDebates: 'Aktive Debatten',
      completedDebates: 'Abgeschlossene Debatten',
      yourTurn: 'Du bist dran',
      continueDebate: 'Debatte fortsetzen',
      viewResults: 'Ergebnisse anzeigen',
      createYourFirstDebate: 'Erstellen Sie Ihre erste Debatte',
      noActiveDebatesYet: 'Sie haben noch keine aktiven Debatten',
      noCompletedDebatesYet: 'Sie haben noch keine Debatten abgeschlossen',
      searchDebates: 'Debatten nach Thema oder Beschreibung suchen...',
      created: 'Erstellt',
      noDescriptionProvided: 'Keine Beschreibung vorhanden.',
      teams: 'Mannschaften',
      noActivityForOverSixDays: 'Keine Aktivität seit über 6 Tagen',
      viewOnly: 'Nur Ansicht',
      noActivePublicDebates: 'Es gibt keine aktiven öffentlichen Debatten zu Ihrer Suche.',
      noCompletedPublicDebates: 'Keine abgeschlossenen öffentlichen Debatten entsprechen Ihrer Suche.',
      welcomeToGauntletDebates: 'Willkommen bei Gauntlet Debates™',
      platformForStructuredDebates: 'Eine Plattform für strukturierte, seriöse Debatten',
      jestersGauntletAlt: 'Fehdehandschuh des Narren™',
      searchYourDebates: 'Suchen Sie Ihre Debatten...',
      noPublicDebatesAvailable: 'Keine öffentlichen Debatten verfügbar',
      createNewDebateToBegin: 'Erstellen Sie eine neue Debatte, um die Diskussion zu beginnen',
      createNewDebate: 'Neue Debatte eröffnen',
      joinDebate: 'An der Debatte teilnehmen',
      viewDebate: 'Ansicht Debatte',
      emailNotVerified: 'E-Mail nicht verifiziert',
      pleaseVerifyEmail: 'Bitte verifizieren Sie Ihre E-Mail-Adresse, um alle Funktionen nutzen zu können.',
      verifyEmailNow: 'E-Mail jetzt verifizieren',
      checkEmailInbox: 'Prüfen Sie Ihren E-Mail-Posteingang und klicken Sie auf den Bestätigungslink.',
      resendVerificationEmail: 'Verifizierungs-E-Mail erneut senden',
      verificationEmailSent: 'Verifizierungs-E-Mail gesendet!',
      emailVerificationRequired: 'E-Mail-Verifizierung erforderlich',
      mustVerifyEmailTo: 'Sie müssen Ihre E-Mail Adresse verifizieren, um',
      emailAddress: 'E-Mail Adresse',
      password: 'Passwort',
      confirmPassword: 'Bestätigen Sie Ihr Passwort',
      username: 'Benutzername',
      signIn: 'Eintragen',
      signUp: 'Anmelden',
      dontHaveAccount: 'Sie haben noch kein Konto?',
      alreadyHaveAccount: 'Sie haben bereits ein Konto?',
      forgotPassword: 'Passwort vergessen?',
      rememberMe: 'Erinnern Sie sich an mich',
      inviteToTeam: 'Zum Team einladen',
      inviteToDebate: 'Einladen zur Debatte',
      sendInvitation: 'Einladung versenden',
      invitationSent: 'Einladung verschickt!',
      invitationExpired: 'Einladung ist abgelaufen',
      acceptInvitation: 'Einladung annehmen',
      declineInvitation: 'Einladung ablehnen',
      youAreInvited: 'Sie sind eingeladen',
      debateTopic: 'Thema der Debatte',
      debateDescription: 'Debatte Beschreibung',
      selectTeamSize: 'Teamgröße auswählen',
      maxArguments: 'Maximale Argumente',
      timeLimit: 'Zeitliche Begrenzung',
      isPublic: 'Öffentliche Debatte',
      allowPublicJoining: 'Öffentlichen Beitritt zulassen',
      rulesAndGuidelines: 'Regeln und Leitlinien',
      conductGuidelines: 'Leitlinien für das Verhalten',
      respectfulDebate: 'Respektvolle Debatte',
      evidenceBasedArguments: 'Auf Beweisen basierende Argumente',
      timeManagement: 'Zeitmanagement',
      minutes: 'minuten',
      hours: 'stunden',
      days: 'tage',
      yes: 'Ja',
      no: 'Nein',
      close: 'Schließen Sie',
      continue: 'Weiter',
      finish: 'Oberfläche',
      step: 'Schritt',
      of: 'von',
      success: 'Erfolg',
      error: 'Fehler',
      warning: 'Warnung',
      info: 'Informationen',
      limitedAccessVerifyEmail: '⚔️ Eingeschränkter Zugang - Überprüfen Sie Ihre E-Mail',
      limitedAccessPendingApproval: '⚔️ Eingeschränkter Zugang - Genehmigung der Verwaltung ausstehend',
      nobleUserLimitedAccess: 'Noble(r) {username}, Ihr Konto hat nur eingeschränkten Zugang, bis Sie Ihre E-Mail-Adresse verifiziert haben.',
      nobleUserPendingApproval: 'Noble(r) {username}, Ihr Konto muss noch vom Administrator genehmigt werden. Nach der Genehmigung erhalten Sie eine Bestätigungs-E-Mail.',
      verificationEmailSentSuccess: 'verifizierungs-E-Mail erfolgreich gesendet! Prüfen Sie Ihren Posteingang.',
      hideDetails: 'Details ausblenden',
      showDetails: 'Details anzeigen',
      sending: 'Ich schicke...',
      resendEmail: 'E-Mail erneut senden',
      currentlyUnavailable: '🚫 Derzeit nicht verfügbar:',
      availableNow: '✅ Jetzt verfügbar:',
      tipCheckSpamFolder: '💡 Tipp: Überprüfen Sie Ihren Spam-Ordner, wenn Sie die Bestätigungs-E-Mail nicht sehen!',
      creatingNewDebates: 'Neue Debatten schaffen',
      joiningDebatesViaInvites: 'Teilnahme an Debatten über Einladungslinks',
      receivingEmailNotifications: 'Empfang von E-Mail-Benachrichtigungen',
      participatingInTeamDebates: 'Teilnahme an Teamdebatten',
      accessingPremiumFeatures: 'Zugang zu Premium-Funktionen (Zukunft)',
      viewingPublicDebates: 'Öffentliche Debatten verfolgen',
      browsingTheCube: 'Durchsuchen des CUBE',
      viewingLeaderboards: 'Anzeige der Ranglisten',
      readingRulesAndAbout: 'Lesen von Regeln und Informationsseiten',
      registrationSuccessful: 'Anmeldung erfolgreich!',
      accountCreatedSuccessfully: 'Ihr Konto wurde erfolgreich erstellt!',
      verificationEmailSentTo: 'Wir haben eine Bestätigungs-E-Mail an {email} gesendet.',
      checkInboxAndSpam: 'Bitte überprüfen Sie Ihren Posteingang (und Ihren Spam-Ordner) und klicken Sie auf den Bestätigungslink, um Ihr Konto zu aktivieren.',
      canLoginNowLimited: 'Sie können sich jetzt anmelden, aber einige Funktionen können eingeschränkt sein, bis Sie Ihre E-Mail verifiziert haben.',
      goToLogin: 'Zum Login gehen',
      passwordsDoNotMatch: 'Passwörter stimmen nicht überein',
      passwordTooShort: 'Das Passwort muss mindestens 6 Zeichen lang sein',
      mustAcceptTerms: 'Um sich zu registrieren, müssen Sie die Allgemeinen Geschäftsbedingungen akzeptieren',
      iAgreeToTerms: 'Ich akzeptiere die',
      termsOfService: 'Bedingungen der Dienstleistung',
      loginHere: 'Hier anmelden',
      needToVerifyEmailBefore: 'Sie müssen Ihre E-Mail-Adresse verifizieren, bevor Sie {Aktion} durchführen können.',
      sentVerificationLinkTo: 'Wir haben einen Bestätigungslink an {email} gesendet.',
      checkEmailAndClick: 'Bitte prüfen Sie Ihre E-Mail und klicken Sie auf den Bestätigungslink, um fortzufahren.',
      whatYouCanDoUnverified: 'Was Sie tun können, solange Sie nicht überprüft wurden:',
      viewPublicDebates: 'Öffentliche Debatten ansehen',
      browseLeaderboards: 'Bestenlisten durchsuchen',
      exploreTheCube: 'Erkunden Sie den CUBE',
      createOrParticipateDebates: 'Erstellen von oder Teilnahme an Debatten',
      enterTopic: 'Thema eingeben',
      reviewGeneratedViews: 'Überprüfung der generierten Ansichten',
      customize: 'Anpassen',
      overview: 'Übersicht',
      pleaseEnterDebateTopic: 'Bitte geben Sie ein Diskussionsthema ein',
      failedToGenerateViews: 'Es konnten keine Debattenansichten erstellt werden. Bitte versuchen Sie es erneut.',
      debateTopicPlaceholder: 'Worüber würden Sie gerne diskutieren?',
      debateDescriptionPlaceholder: 'Geben Sie zusätzlichen Kontext oder Hintergrund für Ihre Debatte an...',
      generateViews: 'Ansichten generieren',
      yourTeamName: 'Ihr Teamname',
      yourTeamViewpoint: 'Die Sichtweise Ihres Teams',
      opponentTeamName: 'Gegner Mannschaftsname',
      customizePrompt: 'Eingabeaufforderung anpassen',
      customizeCounterPrompt: 'Zähler-Eingabeaufforderung anpassen',
      teamConfiguration: 'Team-Konfiguration',
      teamSize: 'Teamgröße',
      publicSpots: 'Öffentliche Spots',
      createDebate: 'Debatte entfachen',
      createPrivateDebate: 'Private Debatte erstellen',
      createPublicDebate: 'Öffentliche Debatten anstoßen',
      debateCreatedSuccessfully: 'Debatte erfolgreich geführt!',
      inviteOpponents: 'Gegner einladen',
      shareInviteLink: 'Einladungslink freigeben',
      copyInviteLink: 'Einladungslink kopieren',
      inviteLinkCopied: 'Einladungslink in die Zwischenablage kopiert!',
      swapViews: 'Ansichten tauschen',
      inviteToJoinDebate: 'Einladung zur Teilnahme an der Debatte',
      inviteViaLink: 'Über Link einladen',
      teamInviteLink: 'Team einladen Link',
      generalInviteLink: 'Allgemeiner Einladungslink',
      shareThisLink: 'Teilen Sie diesen Link mit Personen, die Sie einladen möchten',
      linkCopiedToClipboard: 'Link in die Zwischenablage kopiert!',
      introductionToHonorableDebate: 'Einführung in die ehrenwerte Debatte',
      productiveDebateRelies: 'Eine produktive Debatte beruht auf einer soliden Argumentation, einem respektvollen Diskurs und der Vermeidung von logischen Fehlschlüssen und rhetorischen Taktiken, die das Verständnis untergraben. Das Jester\'s Gauntlet™-System hilft dabei, Probleme zu erkennen, die eine sinnvolle Konversation zum Scheitern bringen können, und ermöglicht es den Teilnehmern, sich auf den Inhalt und nicht auf die Rhetorik zu konzentrieren.',
      aiModeratorAnalyzes: 'Unser KI-Moderator analysiert die Nachrichten auf gängige Debattenverstöße. Wenn er potenzielle Probleme identifiziert, kategorisiert er sie in zwei Typen:',
      seriousIssuesPenalties: 'Schwerwiegende Probleme (Sanktionen)',
      issuesRequiringReformulation: 'Fragen, die eine Neuformulierung erfordern (keine Strafe)',
      whenYourMessageContains: 'Wenn Ihr Beitrag einen markierten Punkt enthält, haben Sie die Möglichkeit, ihn neu zu formulieren. Schwerwiegende Punkte zählen als Strafpunkte für Ihre Debattenwertung, während andere Punkte eine Neuformulierung erfordern, aber nicht als Strafpunkte zählen. Das gegnerische Team kann Flaggen, die es für irrelevant hält, aufheben oder eine Neuformulierung verlangen.',
      debateMisconductTypes: 'Arten von Debattenfehlverhalten',
      seriousIssuesCountAsPenalties: 'Schwerwiegende Probleme (zählen als Sanktionen)',
      theseAreOvertlyDisruptive: 'Dies sind offenkundig störende Techniken, die als Strafen zählen. bei 3 gleichen Techniken oder insgesamt 5 wird die Debatte verloren.',
      countsAgainstYou: 'Zählt gegen Sie',
      requiresReformulation: 'Erfordert eine Neuformulierung',
      debateRulesSummary: 'Zusammenfassung der Debattenregeln',
      turnBasedStructure: 'Rundenbasierter Aufbau',
      turnBasedDescription: 'Die Teams tragen abwechselnd ihre Argumente vor. Jedes Team hat die Möglichkeit, seinen Standpunkt darzulegen und auf die Punkte des gegnerischen Teams zu antworten.',
      jestersGauntletPenalties: 'Jester\'s Gauntlet™ Strafen',
      jestersGauntletDescription: 'Wenn ein Team 3 schwere Vergehen der gleichen Art ODER insgesamt 5 schwere Vergehen begeht, verliert es automatisch die Debatte.',
      flaggedMessages: 'Markierte Nachrichten',
      flaggedMessagesDescription: 'Wenn die KI einen Jester\'s Gauntlet™ in einer Nachricht entdeckt, wird sie markiert. Das gegnerische Team kann entweder Flaggen aufheben, die es für irrelevant hält, oder eine Neuformulierung verlangen.',
      reformulationProcess: 'Prozess der Neuformulierung',
      reformulationDescription: 'Das Team mit einer markierten Nachricht muss die festgestellten Probleme beheben, bevor die Debatte fortgesetzt werden kann, um einen qualitativ hochwertigen Diskurs zu gewährleisten.',
      conceding: 'Zugeständnisse',
      concedingDescription: 'Ein Team kann die Debatte jederzeit aufgeben, wodurch die Debatte sofort mit dem Sieg des gegnerischen Teams endet.',
      inactiveDebates: 'Inaktive Debatten',
      inactiveDebatesDescription: 'Wenn es eine Woche lang keine Beiträge gibt, fasst die KI die Debatte zusammen und versucht, anhand der Qualität der Argumente einen Gewinner zu ermitteln, oder erklärt sie für unentschieden.',
      adHominem: 'Ad Hominem',
      adHominemDescription: 'Persönliche Angriffe, die vom eigentlichen Thema ablenken',
      textualIntimidation: 'Textliche Einschüchterung',
      textualIntimidationDescription: 'Formatierung (z. B. ALLE GROSSBUCHSTABEN), um einzuschüchtern, statt zu argumentieren',
      grammarPolicing: 'Grammatik-Polizei',
      grammarPolicingDescription: 'Ausweichen vor dem Argument, indem man sich auf triviale Fehler konzentriert',
      sarcasmOverload: 'Sarkasmus-Überlastung',
      sarcasmOverloadDescription: 'Übertriebener Spott, der den Diskurs vergiftet',
      strawman: 'Strohmann',
      strawmanDescription: 'Falsche Darstellung des Arguments eines anderen, um eine schwächere Version zu schaffen, die man angreifen kann',
      wordTwisting: 'Wortverdrehung',
      wordTwistingDescription: 'Verdrehung der Worte eines anderen, um ein schwächeres Argument zu schaffen und anzugreifen',
      whataboutism: 'Whataboutism',
      whataboutismDescription: 'Ablenken vom aktuellen Thema durch Aufgreifen von Themen, die nichts damit zu tun haben',
      gishGallop: 'Gish Gallop',
      gishGallopDescription: 'Überwältigend mit zu vielen Punkten oder Links, die eine sinnvolle Antwort verhindern',
      shiftingGoalposts: 'Verschiebung der Torpfosten',
      shiftingGoalpostsDescription: 'Ändern der Kriterien eines Arguments, wenn die ursprünglichen Kriterien erfüllt sind',
      quoteSniping: 'Zitat Sniping',
      quoteSnipingDescription: 'Entstellung von Zitaten aus dem Zusammenhang, um absichtlich irrezuführen',
      sealioning: 'Versiegelung',
      sealioningDescription: 'Unerbittliches Hinterfragen, das eher erschöpft als beschäftigt',
      feigningConfusion: 'Vorgetäuschte Verwirrung',
      feigningConfusionDescription: 'Vorgeben, nicht zu verstehen, um die Diskussion zu verzögern oder abzulenken',
      overgeneralizations: 'Übergeneralisierungen',
      overgeneralizationsDescription: 'Pauschale Aussagen, denen es an Präzision mangelt, die aber verfeinert werden können',
      falseDichotomy: 'Falsche Dichotomie',
      falseDichotomyDescription: 'Eine "Entweder-Oder"-Entscheidung erzwingen, um zu vereinfachen',
      appealToAuthority: 'Appell an die Autorität',
      appealToAuthorityDescription: 'Verwendung irrelevanter Zitate oder Behörden, die ersetzt werden können',
      appealToEmotion: 'Appell an die Emotionen',
      appealToEmotionDescription: 'Verwendung emotionaler Appelle, die die Logik umgehen, aber umformuliert werden können',
      cherryPicking: 'Rosinenpickerei',
      cherryPickingDescription: 'Verwendung von selektiven Beweisen, die irreführend, aber korrigierbar sind',
      unsubstantiatedClaims: 'Unbewiesene Behauptungen',
      unsubstantiatedClaimsDescription: 'Kühne Behauptungen ohne Beweise oder Substanz',
      selectiveSkepticism: 'Selektiver Skeptizismus',
      selectiveSkepticismDescription: 'Ungleiche Anwendung von Zweifeln aufgrund von Vorurteilen',
      circularReasoning: 'Zirkuläre Argumentation',
      circularReasoningDescription: 'Angenommen, die Schlussfolgerung in der Prämisse',
      exampleOf: 'Beispiel für {type}:',
      betterApproach: 'Bessere Herangehensweise:',
      internalCode: 'Interner Code:',
      important: 'Wichtig',
      readonly: 'Schreibgeschützt',
      private: 'Privat',
      public: 'Öffentlich',
      someone: 'Jemand',
      theGauntletHasBeenThrown: 'Der Fehdehandschuh ist geworfen!',
      challengedYouToDebate: 'hat Sie herausgefordert zu einem',
      debateOfHonor: 'Debatte über die Ehre',
      onTheTopic: 'zu diesem Thema',
      publicDebateNotice: 'Diese Debatte wird für jeden Besucher von Gauntlet Debates™ sichtbar sein und zählt zu Ihrem öffentlichen Punktestand.',
      privateDebateNotice: 'Diese Debatte wird nur für die Teilnehmer sichtbar sein.',
      prepareYourDefense: 'Bereiten Sie Ihre Verteidigung vor',
      readCarefullyWarning: 'Lesen Sie genau, was Sie verteidigen werden! Die erste unehrenhafte Handlung könnte in dem vorformulierten Standpunkt versteckt sein. Nehmen Sie sich einen Moment Zeit, um ihn so zu bearbeiten, dass er mit dem übereinstimmt, was Sie wirklich verteidigen wollen.',
      yourOpponentsPosition: 'Die Position Ihres Gegners',
      yourPosition: 'Ihr Standpunkt',
      editAsNeeded: 'Nach Bedarf bearbeiten',
      yourBattleName: 'Ihr Kampfname',
      battleNamePlaceholder: 'Geben Sie einen Namen ein, der Ihrem Gegner Angst einjagt',
      youMustBeLoggedIn: 'Sie müssen eingeloggt sein, um diese Herausforderung anzunehmen. Sie werden zur Anmeldung weitergeleitet.',
      declineChallenge: 'Herausforderung Abstieg',
      acceptTheChallenge: 'Nehmen Sie die Herausforderung an',
      invalidOrExpiredInvitation: 'Ungültiger oder abgelaufener Einladungslink',
      pleaseLoginToViewInvitation: 'Bitte melden Sie sich an, um diese Einladung zu sehen',
      invalidInvitationLink: 'Ungültiger Einladungslink',
      pleaseEnterTeamName: 'Bitte geben Sie einen Teamnamen ein',
      successfullyJoinedDebate: 'Erfolgreich an der Debatte teilgenommen!',
      failedToJoinDebate: 'Nicht an der Debatte teilgenommen',
      pleaseLoginOrRegisterToJoin: 'Bitte anmelden oder registrieren, um an dieser Debatte teilzunehmen',
      messageFlags: 'Nachrichten-Flags',
      flagType: 'Flagge Typ:',
      options: 'Optionen:',
      reformulateYourMessage: 'Formulieren Sie Ihre Botschaft neu',
      concedeDefeat: 'Niederlage eingestehen',
      concedeDebate: 'Debatte zugeben',
      backHome: 'Zurück zur Startseite',
      areYouSureYouWantToConcede: 'Sind Sie sicher, dass Sie diese Debatte aufgeben wollen? Ihr Team wird als Verlierer gewertet.',
      debateResults: 'Ergebnisse der Debatte',
      debateCompleted: 'Debatte abgeschlossen',
      thisDebateHasConcluded: 'Diese Debatte ist abgeschlossen.',
      theWinnerIs: 'Der Gewinner ist',
      summaryGenerated: 'Eine Zusammenfassung der Debatte wurde erstellt und wird oben auf der Seite angezeigt.',
      viewSummary: 'Zusammenfassung anzeigen',
      jestersGauntletDetected: 'Spießrutenlauf des Narren entdeckt:',
      reformulationNeeded: 'Eine Neuformulierung könnte erforderlich sein:',
      thisMessageContainsLanguage: 'Diese Nachricht enthält Formulierungen oder Argumente, die neu formuliert werden müssen.',
      leaderboard: {
        leaderboards: 'Bestenlisten',
        viewTopPerformers: 'Sehen Sie sich die besten Leistungen in öffentlichen Debatten an. Für diese Rangliste werden nur die Ergebnisse öffentlicher Debatten berücksichtigt.',
        topDebaters: 'Top-Debattierer',
        topDebates: 'Top-Debatten',
        rankingBy: 'Rangliste nach',
        rank: 'Rang',
        user: 'Benutzer',
        publicDebates: 'Öffentliche Debatten',
        wins: 'Gewinnt',
        winRate: 'Gewinnrate',
        excaliburIndex: 'Excalibur-Index',
        explanationStrength: 'Erläuterung Stärke (schwer zu variieren)',
        argumentClarity: 'Klarheit der Argumente',
        rebuttalEffectiveness: 'Wirksamkeit der Gegenargumente',
        evidenceQuality: 'Qualität der Nachweise',
        steelmanningRating: 'Stahlmanning-Fähigkeit',
        topic: 'Thema',
        teams: 'Mannschaften',
        overallQuality: 'Allgemeine Qualität',
        explanationQuality: 'Erläuterung Qualität',
        logicalCoherence: 'Logische Kohärenz',
        civilDiscourse: 'Zivilgesellschaftlicher Diskurs',
        messageCount: 'Anzahl der Nachrichten',
        date: 'Datum',
        noUsersFound: 'Keine Benutzer gefunden. Seien Sie der Erste, der sich an öffentlichen Debatten beteiligt!',
        noDebatesFound: 'Keine Debatten gefunden. Seien Sie der Erste, der eine öffentliche Debatte eröffnet!',
      },
      aboutPage: {
        title: 'Über Gauntlet Debates™',
        subtitle: 'Eine Plattform für strukturierte, KI-moderierte Debatten, die einen nachdenklichen Diskurs fördern',
        mission: {
          title: 'Unser Auftrag',
          description: 'Gauntlet Debates befasst sich mit einem grundlegenden Problem in unserem digitalen Diskurs: dem Vorherrschen schlechter Argumente und rhetorischer Taktiken ("Jester\'s Gauntlets"), die darauf abzielen, Debatten ohne Rücksicht auf Wahrheit oder Verständnis zu gewinnen. Diese Taktiken spalten die Menschen in Stämme, denen das Gewinnen wichtiger ist als die Entdeckung der Wahrheit, wodurch es immer schwieriger wird, produktive Gespräche über komplexe Themen zu führen.',
          goal: 'Wir sind davon überzeugt, dass die Demokratie von unserer Fähigkeit abhängt, ehrliche, rigorose Diskussionen über wichtige Themen zu führen. Unsere Plattform bietet eine strukturierte Umgebung, in der die KI-Moderation Irrtümer in Echtzeit identifiziert und es den Teilnehmern ermöglicht, sich auf den Aufbau guter Argumente zu konzentrieren, anstatt rhetorische Tricks anzuwenden. Unsere langfristige Vision ist es, ein Wikipedia-ähnliches Repository der aktuell besten Erklärungen (CUBE) zu jedem Thema zu schaffen, das durch ehrenhafte Debatten kuratiert wird. Diese Wissensdatenbank wird den Menschen helfen, komplexe Themen anhand der stärksten verfügbaren Argumente zu verstehen, und nicht anhand derjenigen, die am eindringlichsten vorgetragen oder am häufigsten wiederholt werden.',
        },
        keyFeatures: {
          title: 'Wesentliche Merkmale',
          structuredDebate: 'Strukturierte Debatte',
          structuredDebateDescription: 'Klare Leitlinien zur Gewährleistung fairer und zielgerichteter Diskussionen zwischen gegensätzlichen Standpunkten.',
          jesterGauntlet: 'Fehdehandschuh des Narren™',
          jesterGauntletDescription: 'KI-gestützte Erkennung von logischen Fehlern und Debattenverstößen in Echtzeit.',
          honorBasedScoring: 'Ehrenbasierte Punktevergabe',
          honorBasedScoringDescription: 'Ein Punktesystem, das die Qualität der Argumente belohnt und die Irrtümer der Debatte bestraft.',
          fairResolutions: 'Faire Lösungen',
          fairResolutionsDescription: 'Die gegnerischen Teams haben die Möglichkeit, die Flaggen für Nachrichten, die gegen die Debattenregeln verstoßen, aufzuheben oder den Flaggen zuzustimmen und eine Neuformulierung zu verlangen. Die Teams können ihre markierte Nachricht auch selbst umformulieren.',
          debateSummaries: 'Zusammenfassungen der Debatten',
          debateSummariesDescription: 'Umfassende KI-generierte Zusammenfassungen von Debatten dienen als wertvolle Ressource für das Verständnis komplexer Themen und den Zugang zu gut begründeten Perspektiven.',
          cubeRepository: 'CUBE-Repository',
          cubeRepositoryDescription: 'Die aktuell besten Erklärungen (CUBE), die aus Debatten extrahiert werden, bilden eine Wikipedia-ähnliche Wissensbasis der stärksten Argumente zu jedem Thema, mit der einzigartigen Eigenschaft, dass diese Erklärungen in Frage gestellt werden können.',
        },
        cubeSystem: {
          title: 'Das CUBE-System & Herausforderungen',
          description: 'CUBE (Current best explanations) ist der Wissensspeicher, der aus unseren Debatten hervorgeht - eine lebendige Sammlung der stärksten Erklärungen zu jedem Thema, die durch unser Challenge-System kontinuierlich verfeinert wird.',
          howCubeWorks: 'So funktioniert CUBE',
          cubeExtraction: 'Nach Abschluss jeder Debatte extrahiert unsere KI die überzeugendsten Erklärungen und fügt sie dem CUBE-Repository hinzu. Diese Erklärungen werden auf der Grundlage ihrer Klarheit, der Qualität der Beweise und der logischen Stärke ausgewählt.',
          challengeSystem: 'Das Challenge-System',
          challengeDescription: 'Im Gegensatz zu herkömmlichen Wikis, die jeder bearbeiten kann, können CUBE-Erklärungen nur durch formale Herausforderungen verbessert werden. Wenn Sie glauben, dass Sie eine bessere Erklärung liefern können, können Sie den aktuellen CUBE-Eintrag in Frage stellen.',
          challengeProcess: 'Ablauf der Herausforderung: Sie reichen Ihre alternative Erklärung ein und geben CUBE-Punkte aus, um die Herausforderung zu starten. Unsere KI bewertet sowohl den aktuellen CUBE-Eintrag als auch Ihre Herausforderung anhand der Stärke der Erklärung, der Qualität der Beweise und der logischen Kohärenz. Wenn Ihre Erklärung als besser eingestuft wird, ersetzt sie den aktuellen CUBE-Eintrag und Ihre CUBE-Punkte werden zurückerstattet.',
          cubePoints: 'CUBE-Punkte: Sie erhalten CUBE-Punkte, wenn Ihre Erklärungen für neue CUBE-Einträge von Debatten ausgewählt werden. Diese Punkte können verwendet werden, um bestehende Erklärungen anzufechten. So entsteht eine Wirtschaft, die qualitativ hochwertige Beiträge belohnt und gleichzeitig leichtfertige Anfechtungen verhindert.',
          continuousImprovement: 'Dieses System stellt sicher, dass die CUBE-Erklärungen im Laufe der Zeit immer besser werden und immer das aktuell beste Verständnis eines Themas repräsentieren, das durch eine strenge Bewertung ermittelt wurde.',
        },
        honorableChallenges: {
          title: 'Den Fehdehandschuh hinwerfen',
          description: 'Wenn jemand eine Behauptung aufstellt, die Ihrer Meinung nach genauer geprüft werden sollte, können Sie ihm den "Fehdehandschuh" hinwerfen, indem Sie ihn auffordern, seinen Standpunkt in einer ehrenhaften Debatte zu verteidigen.',
          howToChallenge: 'Diese uralte Praxis, jemanden herauszufordern, seine Behauptungen zu beweisen, findet auf unserer Plattform neues Leben, wo der Schwerpunkt auf Substanz und nicht auf Rhetorik liegt. Teilen Sie einen Link zu einer Debatte als ehrenvolle Herausforderung: "Glauben Sie, dass Sie das, was Sie gerade gesagt haben, in einem strukturierten, fairen Gespräch verteidigen können?"',
          jesterGauntlet: 'Im Gegensatz zu typischen Online-Diskussionen belohnt unsere Plattform intellektuell ehrliche Teilnehmer, die ihre Ansichten klar artikulieren können, ohne auf falsche Argumente zurückzugreifen. Das "Jester\'s Gauntlets"-System unterscheidet zwischen ehrlichen Debattierern und solchen, die sich auf rhetorische Tricks verlassen.',
        },
        howItWorks: {
          title: 'Wie es funktioniert',
          step1: '1. Erstellen Sie eine Debatte oder nehmen Sie an einer Debatte teil: Starten Sie eine neue Debatte mit einem klaren Thema und gegensätzlichen Standpunkten oder nehmen Sie an einer bestehenden Debatte teil.',
          step2: '2. Präsentieren Sie Ihre Argumente: Jedes Team präsentiert abwechselnd Argumente zur Unterstützung seines Standpunkts. Die KI achtet auf Irrtümer und Diskussionstaktiken, die gegen die Richtlinien verstoßen.',
          step3: '3. Qualität der Debatte aufrechterhalten: Wenn eine Nachricht markiert ist, können gegnerische Teams die Markierungen, die sie für irrelevant halten, aufheben oder um eine Umformulierung bitten.',
          step4: '4. Beenden Sie die Debatte: Debatten können auf verschiedene Arten beendet werden: wenn ein Team aufgibt, wenn ein Team zu viele Strafpunkte sammelt oder automatisch nach einer Woche Inaktivität (in diesem Fall analysiert die KI die Debatte, um einen Gewinner zu ermitteln oder sie für unentschieden zu erklären).',
          step5: '5. Aufschlussreiche Zusammenfassungen und Endnoten: Jede Debatte endet mit einer KI-generierten Zusammenfassung, in der die besten Argumente beider Seiten hervorgehoben werden, sowie mit Endnoten, die die Leistung jedes Teams widerspiegeln. Diese Kombination aus qualitativer Beurteilung und quantitativer Bewertung macht unsere Plattform zu einer wertvollen Fundgrube für gut begründete Perspektiven zu komplexen Themen.',
        },
        excaliburIndex: {
          title: 'Der Excalibur-Index',
          description: 'Der Excalibur-Index ist unser umfassender Maßstab für herausragende Debatten. So wie das legendäre Schwert nur von den wahrhaft Würdigen geführt werden konnte, zeichnet unser Index diejenigen aus, die sich in der ehrenhaften Debatte auszeichnen.',
          calculation: 'Wie er berechnet wird: Der Excalibur Index kombiniert drei Schlüsselkomponenten:',
          qualityComponent: 'Qualitätskomponente (60%)',
          qualityComponentDescription: 'Basierend auf der Stärke der Erklärung (40 %) nach David Deutschs Konzept der "schwer zu variierenden" Erklärungen, der Wirksamkeit der Widerlegung (25 %), der Qualität der Beweise (20 %) und der Klarheit der Argumente (15 %), wie von AI bewertet.',
          performanceComponent: 'Leistungskomponente (25%)',
          performanceComponentDescription: 'Berechnet aus der Gewinnquote und skaliert mit der Teilnahmequote, um beständigen Erfolg zu belohnen.',
          goodFaithComponent: 'Gutgläubigkeitskomponente (15%)',
          goodFaithComponentDescription: 'Stahlmanning-Fähigkeit (85 %) - faire Darstellung gegensätzlicher Standpunkte und Pünktlichkeit der Antwort (15 %).',
          scale: 'Der Excalibur-Index wird auf einer Skala von 0-100 mit einer Nachkommastelle angezeigt, was eine feine Unterscheidung zwischen den Debattierern ermöglicht. Eine Punktzahl von 100 steht für Perfektion in allen Debattenmetriken, ein praktisch unerreichbares Ideal, das selbst den versiertesten Debattierern Raum für Wachstum lässt.',
          technicalImplementation: 'Technische Umsetzung: Der Excalibur-Index verwendet derzeit ChatGPT-4o (das neueste OpenAI-Modell), um die Qualität der Debatte, die Stärke der Argumente und die Fähigkeit des Steelmaning zu bewerten. Wenn sich die KI-Technologie weiterentwickelt, können wir unsere Modelle aktualisieren, um die höchsten Bewertungsstandards aufrechtzuerhalten, wobei Fairness und Tiefe der Analyse immer im Vordergrund stehen.',
          publicPrivateIndices: 'Wir führen getrennte öffentliche und private Indizes, um sicherzustellen, dass die Debattanten im privaten Rahmen üben können, ohne dass ihr öffentliches Ranking beeinträchtigt wird. Nur der öffentliche Excalibur-Index wird in den Bestenlisten angezeigt.',
        },
        evolvingSystem: {
          title: 'Entwicklung mit unserer Gemeinschaft',
          description: 'Sowohl unser Jester\'s Gauntlet™-System als auch unser CUBE-Repository werden ständig weiterentwickelt und von den Erkenntnissen und Erfahrungen unserer Community geprägt. Wir erkennen mehrere wichtige Aspekte unserer Plattform an:',
          communityDrivenImprovement: 'Von der Gemeinschaft gesteuerte Verbesserung',
          communityDrivenImprovementDescription: 'Wir ermutigen die Nutzer aktiv, neue rhetorische Taktiken zu identifizieren, die als Jester\'s Gauntlets klassifiziert werden sollten, und uns Feedback zur Qualität der CUBE-Erklärungen und zur Bewertung der KI-Herausforderungen zu geben. Ihr Input fließt direkt in die Weiterentwicklung beider Systeme ein.',
          learningCurve: 'Lernkurve',
          learningCurveDescription: 'Wenn Sie die Plattform zum ersten Mal benutzen, brauchen Sie eine gewisse Einarbeitungszeit. Wenn Sie sich mit unseren Richtlinien vertraut machen, werden Sie ein natürliches Gespür dafür entwickeln, Argumente zu formulieren, die sich auf die Substanz und nicht auf die Rhetorik konzentrieren, und zu erkennen, was eine starke CUBE-Erklärung ausmacht.',
          falsePositives: 'Unvollkommene AI-Urteile',
          falsePositivesDescription: 'Es kann vorkommen, dass Nachrichten falsch gekennzeichnet werden oder dass CUBE-Herausforderungen in einer Weise bewertet werden, die fragwürdig erscheint. Wenn dies bei gekennzeichneten Nachrichten der Fall ist, denken Sie daran, dass eine Neuformulierung eine Gelegenheit ist, Ihren Standpunkt zu verdeutlichen, und dass gegnerische Teams Kennzeichnungen aufheben können, die ihrer Meinung nach ungerechtfertigt sind. Bei CUBE-Herausforderungen hilft uns das Feedback der Community, unsere Bewertungskriterien zu verfeinern.',
          cubeEvolution: 'CUBE System Entwicklung',
          cubeEvolutionDescription: 'Das CUBE-Repository wächst und verbessert sich durch die Beteiligung der Gemeinschaft. Je mehr Debatten stattfinden und je mehr Herausforderungen gestellt werden, desto besser lernt unsere KI, hochwertige Erklärungen zu erkennen und Herausforderungen genauer zu bewerten. Das Engagement der Community für CUBE-Einträge hilft uns zu verstehen, was Erklärungen wirklich wertvoll macht.',
          nature: 'Keines der beiden Systeme wird jemals perfekt sein, und das ist auch so gewollt. Die Natur der Sprache, der Rhetorik und des Wissens ist zu komplex für absolute Regeln. Was zählt, ist unser Engagement für eine kontinuierliche Verbesserung und das gemeinsame Ziel, die Qualität der Debatten zu verbessern und eine wertvolle Wissensdatenbank aufzubauen. Wir glauben, dass selbst unvollkommene Systeme uns zu produktiveren Gesprächen und einem besseren Verständnis führen können, als wenn es gar keine Standards gäbe.',
          reformulation: 'Betrachten Sie es als Chance und nicht als Rückschlag, wenn eine Nachricht beanstandet wird. Der Umformulierungsprozess führt oft zu stärkeren, klareren Argumenten. In ähnlicher Weise trägt jede Interaktion mit dem CUBE-System zum kollektiven Verständnis komplexer Themen bei - ganz gleich, ob Ihre Erklärung zu einem CUBE-Eintrag wird oder Ihre Herausforderung erfolgreich ist oder nicht.',
        },
        callToAction: {
          title: 'Bereit zum Debattieren?',
          description: 'Schließen Sie sich unserer Gemeinschaft von nachdenklichen Debattierern an und beteiligen Sie sich an sinnvollen Diskussionen über wichtige Themen.',
          startNewDebate: 'Eine neue Debatte beginnen',
          checkPublicDebates: 'Öffentliche Debatten prüfen',
          imageAlt: 'Spießrutenlauf des Narren™',
        },
      },
      debateRules: 'Regeln für Debatten',
      cubePage: {
        title: 'CUBE - CUrrent Best Explanations',
        subtitle: 'Entdecken Sie die hochwertigsten Erklärungen zu wichtigen Themen',
        searchPlaceholder: 'Themen suchen...',
        explanations: 'Erklärungen',
        cubeExplanations: 'CUBE-Erklärungen',
        underChallenge: 'Unter Herausforderung',
        notInCube: 'Derzeit nicht in CUBE',
      },
      landingPage: {
        title: 'Gauntlet Debates™',
        subtitle: 'Wo Ehre auf Intellekt trifft',
        description: 'Beteiligen Sie sich an strukturierten, KI-moderierten Debatten, die den Diskurs anregen und Wissen aufbauen.',
        startDebating: 'Debatten beginnen',
        learnMore: 'Mehr erfahren',
        cubeTitle: 'CUBE',
        cubeDescription: 'Entdecken Sie die aktuell besten Erklärungen zu jedem Thema aus unseren hochqualitativen Debatten.',
        leaderboardsTitle: 'Bestenlisten',
        leaderboardsDescription: 'Sehen Sie, wie Sie unter den Debattierern abschneiden und verfolgen Sie Ihre Fortschritte im Excalibur-Index.',
      },
      accountPendingApprovalMessage: 'Ihr Konto muss noch vom Administrator genehmigt werden. Sie erhalten eine Bestätigungs-E-Mail an {email}, sobald Ihr Konto genehmigt wurde.',
      canLoginLimitedUntilApproved: 'Sie können sich jetzt anmelden, aber die meisten Funktionen sind eingeschränkt, bis Ihr Konto genehmigt und Ihre E-Mail-Adresse verifiziert ist.',
      whileWaitingApprovalBrowse: 'Während Sie auf die Genehmigung warten, können Sie öffentliche Debatten verfolgen, Bestenlisten einsehen und das CUBE-System erkunden.',
      debateTopicLabel: 'Thema der Debatte',
      descriptionLabel: 'Beschreibung (fakultativ)',
      placeholderExample: 'z. B. Künstliches Bewusstsein ist mit den heutigen Methoden des maschinellen Lernens erreichbar',
      placeholderDescription: 'Fügen Sie Kontext, Einschränkungen oder spezifische Aspekte hinzu, die Sie in der Debatte behandeln möchten',
      teamSizeTooltip: 'Die maximale Anzahl von Mitgliedern in Ihrem Team, einschließlich Ihnen selbst. Standard ist 1 (nur Sie).',
      increaseTeamSizeTooltip: 'Teamgröße erhöhen, um öffentliche Plätze zu ermöglichen',
      increaseTeamSizeToAllowPublicSpots: 'Teamgröße erhöhen, um öffentliche Plätze zu ermöglichen',
      publicSpotsTooltip: 'Anzahl der Plätze, die von jedem öffentlichen Nutzer besetzt werden können. Das Maximum ist die Teamgröße minus Sie (der Ersteller).',
      opponentTeamSizeTooltip: 'Die maximale Anzahl der Mitglieder, die im gegnerischen Team erlaubt sind. Standard ist 1.',
      opponentPublicSpotsTooltip: 'Anzahl der Plätze, die von jedem öffentlichen Nutzer besetzt werden können. Das Maximum ist die Größe des gegnerischen Teams.',
      memberText: 'mitglied',
      membersText: 'mitglieder',
      includingYouText: '(einschließlich Ihnen)',
      publicDebateText: 'Öffentliche Debatte',
      privateDebateText: 'Private Debatte',
      yesText: 'Ja',
      noText: 'Nein',
      debateCreatedSuccessfullyText: 'Debatte erfolgreich geführt',
      debateCreatedMessageText: 'Ihre Debatte wurde erfolgreich erstellt! Als Ersteller senden Sie bitte die erste Nachricht, um die Debatte zu starten.',
      inviteMembersText: 'Laden Sie Mitglieder in Ihr Team ein',
      shareTeamLinkText: 'Geben Sie diesen Link an Personen weiter, die Sie für Ihr Team gewinnen möchten:',
      inviteOpponentText: 'Gegner einladen',
      inviteOpponentsText: 'Gegner einladen',
      shareOpponentLinkText: 'Teilen Sie diesen Link mit der Person, gegen die Sie debattieren möchten:',
      shareOpponentsLinkText: 'Geben Sie diesen Link an die Personen weiter, mit denen Sie diskutieren möchten:',
      linksWorkUntilText: 'Die Links funktionieren so lange, bis Ihre Debatte abgeschlossen ist. Alle Teilnehmer müssen sich registrieren oder anmelden, um teilnehmen zu können.',
      copyOpponentLinkText: 'Gegnerischen Link kopieren',
      copyOpponentsLinkText: 'Link des Gegners kopieren',
      shareText: 'Teilen Sie',
      goToDebateRoomText: 'Zum Debattenraum gehen',
      returnToHomeText: 'Zurück zur Startseite',
      youAreText: 'Sie sind',
      aFellowKnightText: 'Ein ritterlicher Gefährte',
      callingForAlliesText: 'verbündete für eine bevorstehende',
      debateOfHonorText: 'Debatte über die Ehre',
      onTheTopicText: 'zu diesem Thema:',
      publicText: 'Öffentlich',
      publicDebateDescText: 'Diese Debatte wird für jeden Besucher von Gauntlet Debates™ sichtbar sein und zählt zu Ihrem öffentlichen Punktestand.',
      privateText: 'Privat',
      privateDebateDescText: 'Diese Debatte wird nur für die Teilnehmer sichtbar sein.',
      fellowKnightText: 'Ritterkollege',
      youAreBeingCalledText: 'Sie sind aufgerufen, an der Seite von',
      yourTeamText: 'ihr Team',
      inDefenseOfHonorText: 'zur Verteidigung von Ehre und Wahrheit. Ihre Stimme und Ihre Weisheit werden in diesem Kampf des Verstandes gebraucht.',
      teamPositionNotDefinedText: 'Die Position dieses Teams ist noch nicht festgelegt.',
      teamNotNamedText: 'Dieses Team wurde noch nicht benannt.',
      knightText: 'Ritter',
      knightsText: 'Ritter',
      standText: 'stand',
      standsText: 'steht',
      readyText: 'bereit',
      noKnightsJoinedText: 'Es sind noch keine Ritter beigetreten.',
      invalidOrExpiredText: 'Ungültiger oder abgelaufener Einladungslink',
      pleaseLoginToViewText: 'Bitte melden Sie sich an, um diese Einladung zu sehen',
      invalidInvitationLinkText: 'Ungültiger Einladungslink',
      pleaseLoginOrRegisterText: 'Bitte anmelden oder registrieren, um diesem Team beizutreten',
      successfullyJoinedTeamText: 'Erfolgreich zum Team gestoßen!',
      failedToJoinTeamText: 'Nicht zum Team gestoßen',
      failedToJoinTeamRetryText: 'Beitritt zum Team fehlgeschlagen. Bitte versuchen Sie es erneut.',
      mustBeLoggedInText: 'Sie müssen eingeloggt sein, um diesem Team beizutreten. Sie werden zum Login weitergeleitet.',
      aCallToArms: 'Ein Aufruf zu den Waffen!',
      debateType: 'Art der Debatte:',
      joinTheNobleCause: 'Schließen Sie sich der edlen Sache an',
      yourTeamsPosition: 'Die Position Ihres Teams:',
      currentMembers: 'Derzeitige Mitglieder:',
      declineTheCall: 'Ablehnen des Anrufs',
      joinTheRanks: 'In die Ränge eintreten',
      yourTeamHeader: 'Ihr Team',
      teamMembersText: 'Teammitglieder',
      debateOverviewText: 'Überblick über die Debatte',
      statusText: 'Status:',
      inProgressText: 'In Arbeit',
      completedText: 'Abgeschlossen',
      pendingText: 'Anhängig',
      messagesText: 'Nachrichten:',
      startedText: 'Angefangen:',
      lastMessageText: 'Letzte Nachricht:',
      noMessagesYetText: 'Noch keine Nachrichten',
      noPenaltiesText: 'Keine Sanktionen',
      backToHomeText: 'Zurück zur Startseite',
      theWinnerIsText: 'Der Gewinner ist',
      yourTurnText: 'Sie sind dran',
      waitingText: 'Warten',
      currentTurnText: 'Aktuelle Wende',
      jestersGauntletCountsText: 'Narrenhandschuh Zählt',
      aboutPenaltiesText: 'Über Sanktionen:',
      noMessagesStartDebateText: 'Noch keine Nachrichten. Starten Sie die Debatte, indem Sie eine Nachricht senden.',
      waitingForFirstTeamText: 'Ich warte darauf, dass der Urheber der Debatte die erste Nachricht sendet.',
      reformulateMessageText: 'Formulieren Sie Ihre Botschaft neu',
      liftAllFlagsText: 'Alle Flaggen hissen',
      requestReformulationText: 'Antrag auf Neuformulierung',
      sendMessageText: 'Nachricht senden',
      reformulateText: 'Neu formulieren',
      isTypingText: 'tippt',
      areTypingText: 'tippen',
      loadingDebateText: 'Debatte laden...',
      debateNotFoundText: 'Debatte nicht gefunden',
      errorLoadingDebateText: 'Fehler beim Laden der Debatte',
      concedeDefeatText: 'Niederlage eingestehen',
      closeText: 'Schließen Sie',
      copyLinkText: 'Link kopieren',
      downloadText: 'Herunterladen',
      backHomeText: 'Zurück zur Startseite',
      allFlagsLiftedText: 'Alle Flaggen sind eingeholt worden',
      failedLiftFlagsText: 'Einige Flaggen konnten nicht gehoben werden. Bitte versuchen Sie es erneut.',
      reformulationRequestSentText: 'Antrag auf Neuformulierung gesendet. Warten auf die Neuformulierung durch das andere Team.',
      failedSendReformulationText: 'Die Anfrage zur Neuformulierung konnte nicht gesendet werden.',
      youConcededText: 'Sie haben sich geschlagen gegeben. Der Sieg geht an',
      failedConcedeText: 'Sie konnten die Debatte nicht beenden. Bitte versuchen Sie es erneut.',
      flagLiftedText: 'Die Flagge wurde gehisst',
      errorLiftingFlagText: 'Fehler beim Aufheben der Flagge. Bitte versuchen Sie es erneut.',
      messageSentSuccessfullyText: 'Nachricht erfolgreich gesendet!',
      byText: 'von',
      analyzingText: 'Analysieren...',
      recommendationText: 'Empfehlung:',
      iPreferToReformulateText: 'Ich ziehe es vor, umzuformulieren',
      concedeDebateText: 'Debatte zugeben',
      reformulateYourMessageText: 'Formulieren Sie Ihre Botschaft neu',
      andText: 'und',
      othersText: 'andere',
      retryText: 'Wiederholung',
      pleaseLoginText: 'Bitte melden Sie sich an, um an dieser Debatte teilzunehmen',
      loginText: 'Anmelden',
      failedExportText: 'Die Debatte wurde nicht exportiert',
      copyDebateLinkText: 'Link zur Debatte kopieren',
      linkCopiedText: 'Link kopiert!',
      exportDebateText: 'Debatte über die Ausfuhr',
      debateResultsText: 'Ergebnisse der Debatte',
      debateCompletedText: 'Debatte abgeschlossen',
      viewSummaryText: 'Zusammenfassung anzeigen',
      youChosenReformulateText: 'Sie haben sich entschieden, Ihre Nachricht umzuformulieren. Bitte gehen Sie in Ihrer Antwort auf die markierten Punkte ein.',
      otherTeamRequestedReformulateText: 'Das andere Team hat Sie gebeten, Ihre Nachricht umzuformulieren. Bitte gehen Sie in Ihrer Antwort auf die markierten Punkte ein.',
      areYouSureConcedeText: 'Sind Sie sicher, dass Sie diese Debatte aufgeben wollen? Diese Aktion kann nicht rückgängig gemacht werden.',
      thisDebateHasConcludedText: 'Diese Debatte ist abgeschlossen.',
      chooseReformulateNowText: 'Entscheiden Sie sich jetzt, Ihre Botschaft neu zu formulieren.',
      penaltyFlagsNoteText: 'Wenn es Strafflaggen gibt, wird die Strafe durch eine Neuformulierung NICHT aufgehoben. Nur das andere Team, das alle Flaggen aufhebt, kann dies tun.',
      teamsLoseTooltipText: 'Teams verlieren automatisch, wenn sie 3 Strafen der gleichen Art oder insgesamt 5 Strafen erreichen. Andere Flaggen können eine Neuformulierung vorschlagen, fügen aber keine Strafpunkte zu dieser Verlustregel hinzu.',
      realTimeConnectionText: 'Verbindung in Echtzeit hergestellt',
      otherTeamChosenReformulateToastText: 'Das andere Team hat sich für eine Neuformulierung seiner Botschaft entschieden',
      otherTeamRequestedReformulateToastText: 'Das andere Team hat Sie gebeten, Ihre Nachricht neu zu formulieren',
      youChosenReformulateToastText: 'Sie haben sich entschieden, Ihre Botschaft neu zu formulieren.',
      areYouSureConcededText: 'Sind Sie sicher, dass Sie diese Debatte aufgeben wollen? Ihr Team wird als Verlierer gewertet.',
      summaryGeneratedText: 'Eine Zusammenfassung der Debatte wurde erstellt und wird oben auf der Seite angezeigt.',
      jestersGauntletDetectedText: 'Spießrutenlauf des Narren entdeckt:',
      reformulationNeededText: 'Eine Neuformulierung könnte erforderlich sein:',
      thisMessageContainsText: 'Diese Nachricht enthält Formulierungen oder Argumente, die neu formuliert werden müssen.',
      flagTypeText: 'Flagge Typ:',
      optionsText: 'Optionen:',
      messageFlagsText: 'Nachrichten-Flags',
      failedInitiateReformulationText: 'Die Neuformulierung wurde nicht in die Wege geleitet.',
      errorLiftingFlagMissingIdText: 'Fehler beim Aufheben der Flagge: Fehlende Nachrichten-ID',
      issueUpdatingDebateStatusText: 'Es gab ein Problem bei der Aktualisierung des Debattenstatus auf dem Server, aber die Debatte wurde lokal als abgeschlossen markiert.',
      couldNotIdentifyOpposingTeamText: 'Konnte die gegnerische Mannschaft nicht identifizieren',
      failedEndDebateAfterConcessionText: 'Die Debatte konnte nach dem Zugeständnis nicht beendet werden. Bitte versuchen Sie es erneut.',
      aboutPenaltiesDescText: 'Flaggen weisen auf mögliche Probleme mit der Art und Weise hin, wie etwas gesagt wurde - sie sind keine endgültigen Urteile. Ernsthafte Jester\'s Gauntlets zählen als Strafen; andere Flaggen erfordern lediglich eine Umformulierung. Das gegnerische Team kann Flaggen aufheben, wenn es mit dem Assistenten nicht einverstanden ist.',
      onlyFirstTeamCanStartText: 'Nur das erste Team kann die Debatte beginnen',
      youRequestedReformulationText: 'Sie haben das andere Team aufgefordert, diese Nachricht neu zu formulieren.',
      otherTeamChosenReformulateText: 'Das andere Team hat sich für eine Neuformulierung seiner Botschaft entschieden.',
      actionRequiredText: 'Erforderliche Maßnahmen:',
      otherTeamMessageFlaggedText: 'Die Nachricht des anderen Teams wurde markiert.',
      reviewFlagsText: 'Bitte überprüfen Sie die Kennzeichen und heben Sie entweder alle Kennzeichen auf (wenn Sie glauben, dass sie nicht zutreffend sind) oder fordern Sie eine Neuformulierung (wenn die Nachricht verbessert werden muss).',
      selfInitiatedReformulationText: 'Selbstinitiierte Reformulierung:',
      reformulationRequestedText: 'Umformulierung beantragt:',
      yourMessageFlaggedAgainText: 'Ihre Nachricht wurde erneut markiert. Sie können:',
      waitOtherTeamReviewText: 'Warten, bis das andere Team die Flaggen überprüft hat und eine Entscheidung trifft, oder',
      chooseReformulateAgainText: 'Entscheiden Sie sich, Ihre Botschaft noch einmal neu zu formulieren.',
      yourRecentMessageFlaggedText: 'Ihre letzte Nachricht wurde markiert. Sie können:',
      reformulationRequestedWaitText: 'Umformulierung erbeten. Warten Sie, bis Sie an der Reihe sind, eine neue Nachricht zu übermitteln.',
      yourMessageFlaggedNeedsReformulationText: 'Ihre Nachricht wurde markiert und muss umformuliert werden.',
      submitImprovedMessageText: 'Bitte übermitteln Sie eine verbesserte Nachricht, die auf die markierten Probleme eingeht.',
      waitingOtherTeamSendText: 'Warten, bis die andere Mannschaft eine Nachricht sendet.',
      waitingThemSubmitImprovedText: 'Ich warte darauf, dass sie eine verbesserte Nachricht übermitteln, die das markierte Problem behebt.',
      debateFormHeaderText: 'Worüber würden Sie gerne diskutieren?',
      productiveDebateTipsText: 'Tipps für eine produktive Debatte:',
      debateTopicGuidanceText: 'Wählen Sie ein Thema, das zu gezielten, klar umrissenen Ansichten führen kann. Spezifische Themen (z. B. "Sollten die Subventionen für erneuerbare Energien in der EU erhöht werden?") eignen sich besser als zu weit gefasste Themen (z. B. "Klimawandel").',
      focusedTopicHelperText: 'Geben Sie ein Schwerpunktthema an, zu dem von verschiedenen Seiten effektiv argumentiert werden kann',
      contextHelperText: 'Verwenden Sie dies, um zusätzlichen Kontext zu liefern. Wir empfehlen 150-300 Zeichen für beste Ergebnisse.',
      reviewAndCustomizeDebateViews: 'Überprüfen und Anpassen von Debattenansichten',
      aiGeneratedViewpointsDescription: 'Unsere KI hat zwei gegensätzliche Standpunkte für Ihr Diskussionsthema erstellt. Sie können diese Standpunkte bearbeiten, um die Perspektiven, die Sie erkunden möchten, besser wiederzugeben.',
      view1DefendPosition: 'Ansicht 1 (Sie werden diesen Standpunkt verteidigen):',
      view2OpponentPosition: 'Ansicht 2 (Ihr Gegner wird diese Position verteidigen):',
      configureTeamsAndDebateSettings: 'Teams und Debatteneinstellungen konfigurieren',
      debateVisibility: 'Sichtbarkeit von Debatten',
      publicDebate: 'Öffentliche Debatte',
      publicDebateDescription: 'Ihre Debatte wird für alle sichtbar sein. Ihre Argumente und Ihr Diskussionsstil tragen zu Ihrer offiziellen Punktzahl in der Rangliste bei. Dies hilft anderen, die sich für das Thema interessieren, hochwertige Erklärungen zu finden.',
      privateDebate: 'Private Debatte',
      privateDebateDescription: 'Ihre Debatte wird nur für die Teilnehmer sichtbar sein. Dies ist ideal zum Üben oder zur Erörterung sensibler Themen.',
      makeDebatePubliclyJoinable: 'Machen Sie die Debatte öffentlich zugänglich (erlauben Sie Nutzern, ohne Einladung teilzunehmen, wenn öffentliche Plätze verfügbar sind)',
      debateOverview: 'Überblick über die Debatte',
      reviewAllDetailsDescription: 'Prüfen Sie alle Details unten. Wenn Sie die Debatte erstellen, erhalten Sie Einladungslinks, die Sie mit Ihren Teammitgliedern und dem Gegner teilen können.',
      debateInformation: 'Informationen zur Debatte',
      topicLabel: 'Thema:',
      visibilityLabel: 'Sichtbarkeit:',
      yourTeamLabel: 'Ihr Team:',
      yourViewpointLabel: 'Ihr Blickwinkel:',
      teamSizeLabel: 'Teamgröße:',
      opponentTeamLabel: 'Gegnerische Mannschaft:',
      opponentViewpointLabel: 'Standpunkt des Gegners:',
      needToBeLoggedInToCreate: 'Sie müssen eingeloggt sein, um eine Debatte zu eröffnen. Sie werden zum Login weitergeleitet.',
      startANewDebate: 'Eine neue Debatte beginnen',
      publiclyJoinableLabel: 'Öffentlicher Beitritt möglich:',
      publicSpotsLabel: 'Öffentliche Spots:',
      inviteLinks: 'Links einladen',
      creatorLabel: 'Schöpfer',
      inviteOpponent: 'Gegner einladen',
      inviteTeamMembers: 'Teammitglieder einladen',
      copyOpponentInviteLink: 'Link zur Einladung des Gegners kopieren',
      copyTeamInviteLink: 'Link zur Teameinladung kopieren',
      inviteLinksHelpText: '💡 Dies sind die gleichen Links wie bei der Erstellung der Debatte. Teilen Sie sie, um Teilnehmer einzuladen!',
      reformulateYourMessagePlaceholder: 'Formulieren Sie Ihre Botschaft neu...',
      typeYourMessagePlaceholder: 'Geben Sie hier Ihre Nachricht ein...',
      reformulateButton: 'Neu formulieren',
      messageInputSendButton: 'Senden Sie',
      messageInputSendButtonOffline: 'Senden*',
      messageInputCharactersText: 'zeichen',
      messageInputConnectionIssuesWarning: '*Verbindungsprobleme können die Zustellung von Nachrichten verzögern',
      messageInputDraftRestored: 'Entwurf wiederhergestellt. Ihre nicht gesendete Nachricht wurde gespeichert.',
      messageInputSendFailedRestored: 'Senden fehlgeschlagen. Ihre Nachricht ist noch da - versuchen Sie es erneut, wenn Sie verbunden sind.',
      hailNobleBetaKnight: '🛡️ Seid gegrüßt, edler Beta-Ritter! ⚔️',
      betaTesterDescription: 'Sie gehören zu den wenigen Auserwählten, die unser Reich testen. Euer Mut, Fehler zu finden und Feedback zu geben, prägt die Zukunft der ehrenwerten Debatte!',
      privacyDataProtection: '🔒 Privatsphäre und Datenschutz',
      privateDebatesWarning: 'Private Debatten: Wir verpflichten uns zwar, den Inhalt privater Debatten nicht zu lesen, aber es handelt sich noch um eine Beta-Software. Wenn Sie den Entwickler nicht kennen oder ihm nicht vertrauen, seien Sie bitte vorsichtig mit sensiblen Informationen in privaten Debatten.',
      publicContentReminder: 'Öffentliche Inhalte: Denken Sie daran, dass öffentliche Debatten, Bestenlisten und CUBE-Einträge für alle Benutzer sichtbar sind.',
      yourQuestAsBetaKnight: '🏰 Deine Aufgabe als Beta-Ritter',
      betaTesterPowerDescription: 'Als Mitglied unseres Elite-Prüfungsordens haben Sie die Macht zu:',
      huntBugsGlitches: 'Bugs und Pannen jagen',
      shareYourWisdom: 'Teilen Sie Ihre Weisheit',
      testNewFeatures: 'Neue Funktionen testen',
      rememberRealmBeingForged: '⚡ Denkt daran: Dieses Reich ist noch im Entstehen begriffen! Es kann sein, dass du darauf triffst:',
      featuresBeingRefined: 'Funktionen, die noch weiterentwickelt werden',
      uiElementsChanging: 'UI-Elemente, die sich im Zuge der Verbesserung ändern können',
      limitedAiResponses: 'Begrenzte AI-Reaktionen zur Kostenkontrolle während der Tests',
      reportIssuesText: '📧 Probleme melden: Senden Sie Ihre Erkenntnisse an',
      feedbackForgePath: 'Ihr Feedback ebnet den Weg zu einer ehrenvolleren Debattenwelt! 🗡️⚔️',
      challengeAssessment: 'Herausforderung Bewertung',
      debateRoomSummaryText: 'Zusammenfassung',
      debateHasConcluded: 'Diese Debatte ist abgeschlossen. Es können keine weiteren Nachrichten gesendet werden.',
      takenActionOnFlaggedMessage: 'Sie haben auf die markierte Nachricht reagiert.',
      waitingOtherTeamRespond: 'Warten auf die Antwort des anderen Teams mit einer neuen oder umformulierten Nachricht.',
      loadingMessages: 'Meldungen werden geladen...',
      yourMessageHasBeenFlagged: 'Ihre Nachricht wurde markiert.',
      waitForOtherTeamDecision: 'Sie sollten abwarten, bis das andere Team entschieden hat, ob Sie es umformulieren müssen.',
      debateRoomResultsText: 'Ergebnisse der Debatte',
      invitePageImportantText: 'Das ist wichtig:',
      opponentPositionReadonly: 'Die Position Ihres Gegners (Nur-Lesezugriff):',
      yourPositionEditNeeded: 'Ihre Position (bei Bedarf bearbeiten):',
      invitePagePrivateLabel: 'Privat',
      settingsPage: {
        title: 'Benutzereinstellungen',
        loadingSettings: 'Einstellungen laden...',
        loginRequired: 'Bitte melden Sie sich an, um Ihre Einstellungen zu sehen',
        failedToLoadSettings: 'Ihre Einstellungen konnten nicht geladen werden. Bitte versuchen Sie es später noch einmal.',
        failedToSaveSettings: 'Ihre Einstellungen konnten nicht gespeichert werden. Bitte versuchen Sie es später noch einmal.',
        settingsSavedSuccessfully: 'Einstellungen erfolgreich gespeichert!',
        saving: 'Sparen...',
        saveSettings: 'Einstellungen speichern',
        emailNotifications: 'E-Mail-Benachrichtigungen',
        emailNotificationsDescription: 'Legen Sie fest, wann Sie E-Mail-Benachrichtigungen erhalten möchten.',
        notifyOnTurn: 'Benachrichtigen Sie mich, wenn ich an der Reihe bin und innerhalb von 24 Stunden nicht geantwortet habe',
        notifyOnDebateEndingSoon: 'Benachrichtigen Sie mich, wenn eine Debatte bald zu Ende ist und ich an der Reihe bin',
        notifyOnReformulation: 'Benachrichtigen Sie mich, wenn unser Team gebeten wird, eine Nachricht neu zu formulieren',
        emailAddress: 'E-Mail Adresse',
        currentEmailAddress: 'Ihre aktuelle E-Mail Adresse:',
        changePassword: 'Passwort ändern',
        changePasswordDescription: 'Aktualisieren Sie Ihr Kontopasswort aus Sicherheitsgründen.',
        currentPassword: 'Aktuelles Passwort',
        newPassword: 'Neues Passwort',
        confirmNewPassword: 'Neues Passwort bestätigen',
        currentPasswordRequired: 'Aktuelles Passwort ist erforderlich',
        newPasswordRequired: 'Neues Passwort ist erforderlich',
        confirmPasswordRequired: 'Bitte bestätigen Sie Ihr neues Passwort',
        passwordMinLength: 'Das Passwort muss mindestens 8 Zeichen lang sein',
        passwordsDoNotMatch: 'Passwörter stimmen nicht überein',
        currentPasswordIncorrect: 'Aktuelles Passwort ist falsch',
        passwordChangedSuccessfully: 'Passwort erfolgreich geändert',
        failedToChangePassword: 'Passwortänderung fehlgeschlagen',
        changingPassword: 'Passwort ändern...',
      },
      myDashboard: {
        title: 'Mein Dashboard',
        yourStats: 'Deine Statistiken',
        excaliburIndex: 'Excalibur-Index',
        totalDebates: 'Debatten insgesamt',
        debatesWon: 'Gewonnene Debatten',
        currentStreak: 'Aktuelle Serie',
        averageScore: 'Durchschnittliche Punktzahl',
        exploreCube: 'CUBE erforschen',
        viewLeaderboard: 'Leaderboard ansehen',
        pleaseLogin: 'Bitte melden Sie sich an, um Ihr Dashboard zu sehen.',
        welcomeBack: 'Willkommen zurück, {{Benutzername}}! 👋',
        debateOverview: 'Überblick über die Debatte',
        overallPerformance: 'Ihre Gesamtleistung in allen Debatten',
        quickActions: 'Quick Actions',
        startNewDebate: 'Neue Debatte beginnen',
        browseDebates: 'Debatten durchsuchen',
        totalDebatesWon: 'Gewonnene Debatten insgesamt',
        publicDebates: 'Öffentliche Debatten',
        winRate: 'Gewinnrate',
        argumentClarity: 'Klarheit der Argumente',
        explanationStrength: 'Erläuterung Stärke',
        getStartedWithCommonTasks: 'Beginnen Sie mit allgemeinen Aufgaben',
      },
      cubeDashboard: {
        title: 'CUBE Dashboard',
        subtitle: 'Überwachen Sie Ihre CUBE-Herausforderungsaktivitäten und verteidigen Sie Erklärungen',
        pleaseLogin: 'Bitte melden Sie sich an, um Ihr CUBE-Dashboard anzuzeigen.',
        cubePoints: 'CUBE-Punkte',
        successfulChallenges: 'Erfolgreiche Herausforderungen',
        defensesWon: 'Gewonnene Verteidigungen',
        cubeContributions: 'CUBE-Beiträge',
        total: 'insgesamt',
        challengeActivity: 'Herausforderung Aktivität',
        noSubmittedChallenges: 'Keine eingereichten Herausforderungen',
        noReceivedChallenges: 'Keine eingegangenen Herausforderungen',
        noAvailableChallenges: 'Keine Herausforderungen verfügbar',
        challengeSuccessful: 'Herausforderung Erfolgreich',
        challengeFailed: 'Herausforderung gescheitert',
        explanationMaintained: 'Erläuterung Beibehalten',
        refunded: 'Erstattet',
        acceptDefense: 'Verteidigung akzeptieren',
        aiAssessmentResults: 'AI-Bewertungsergebnisse',
        finalDecision: 'Endgültige Entscheidung:',
        yourChallengeWasSuccessful: 'Ihre Herausforderung war erfolgreich!',
        originalExplanationMaintained: 'Die ursprüngliche Erklärung wurde beibehalten',
        aiReasoning: 'AI Reasoning:',
        assessmentCompleted: 'Bewertung abgeschlossen:',
        viewChallengeDebate: 'Ansicht Herausforderung Debatte',
        publicView: 'Öffentliche Ansicht',
        browseCube: 'CUBE durchsuchen',
        viewDebates: 'Debatten ansehen',
        submitted: 'Eingereicht',
        received: 'Empfangen',
        availableToDefend: 'Zum Verteidigen verfügbar',
        completed: 'Abgeschlossen',
        failedToLoadData: 'Daten konnten nicht geladen werden',
        failedToAcceptDefense: 'Die Verteidigung nicht akzeptiert',
        points: 'punkte',
        completedOn: 'Abgeschlossen',
      },
      checkYourEmail: 'Prüfen Sie Ihre E-Mail',
      resetYourPassword: 'Ihr Passwort zurücksetzen',
      newPassword: 'Neues Passwort',
      confirmNewPassword: 'Neues Passwort bestätigen',
      sendResetLink: 'Reset-Link senden',
      backToLogin: 'Zurück zur Anmeldung',
      sendAnotherEmail: 'Weitere E-Mail senden',
      goToLogin2: 'Zum Login gehen',
      requestNewResetLink: 'Neuen Reset-Link anfordern',
      resetPassword: 'Passwort zurücksetzen',
      rememberYourPassword: 'Erinnern Sie sich an Ihr Passwort?',
      enterYourEmailInstruction: 'Geben Sie Ihre E-Mail-Adresse ein, damit wir Ihnen einen Link zum Zurücksetzen Ihres Passworts zusenden können.',
      enterNewPasswordInstruction: 'Geben Sie unten Ihr neues Passwort ein.',
      checkEmailInboxInstruction: 'Prüfen Sie Ihren E-Mail-Posteingang und folgen Sie den Anweisungen, um Ihr Passwort zurückzusetzen.',
      passwordResetEmailSent: 'Wenn ein Konto mit dieser E-Mail existiert, erhalten Sie in Kürze eine E-Mail zum Zurücksetzen des Passworts.',
      passwordResetSuccessful: 'Passwort zurücksetzen erfolgreich',
      passwordResetSuccessMessage: 'Ihr Passwort wurde erfolgreich zurückgesetzt. Sie können sich jetzt mit Ihrem neuen Passwort anmelden.',
      invalidResetLink: 'Ungültiger Reset-Link',
      invalidResetLinkMessage: 'Dieser Link zum Zurücksetzen des Kennworts ist ungültig oder fehlerhaft formatiert.',
      passwordResetRateLimit: 'Zu viele Versuche, das Passwort zurückzusetzen. Bitte versuchen Sie es in {Minuten} Minuten erneut.',
      loginRateLimit: 'Zu viele Anmeldeversuche. Bitte versuchen Sie es in {Minuten} Minuten erneut.',
      passwordValidationError: 'Das Passwort muss mindestens 8 Zeichen lang sein und mindestens einen Großbuchstaben, einen Kleinbuchstaben und eine Zahl enthalten.',
      passwordsDoNotMatchError: 'Passwörter stimmen nicht überein',
      invalidResetTokenError: 'Ungültiges Reset-Token',
      passwordRequirementHelper: 'Das Passwort muss aus mehr als 8 Zeichen bestehen und Groß- und Kleinbuchstaben sowie Zahlen enthalten',
      passwordsDoNotMatchHelper: 'Passwörter stimmen nicht überein',
      errorOccurredTryAgain: 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
      debates: 'Debatten',
      formattingHint: 'Verwenden Sie **fett** und *kursiv* in normalem Text oder in der Symbolleiste. Einfügen verwendet nur reinen Text (wie Notepad).',
      unverifiedLinkNote: ' (nicht geprüft)',
      boldTooltip: 'Fettdruck (**Text**)',
      italicTooltip: 'Kursiv (*Text*)',
      selectTextToFormat: 'Markieren Sie den Text und klicken Sie auf die Formatierungsschaltflächen, oder geben Sie **fett** und *kursiv* ein',
      pleaseEnterOpponentTeamName: 'Bitte geben Sie den Namen des gegnerischen Teams ein',
      missingRequiredInformation: 'Fehlende erforderliche Informationen',
      backToLandingPage: 'Zurück zur Startseite',
      waitingCubeAIResponseText: 'Warten auf die Reaktion des KI-Verteidigungssystems. Dies sollte nicht länger als 5 Minuten dauern. Wenn es länger dauert, aktualisieren Sie bitte diese Seite.',
      cubeAIDefenseFailedText: 'AI-Verteidigungssystem gescheitert',
      cubeAIDefenseFailedDescription: 'Das KI-Verteidigungssystem ist auf einen Fehler gestoßen und konnte nicht auf Ihre Herausforderung reagieren. Ihre Punkte wurden automatisch zurückerstattet.',
      cubeAIDefenseFailedPermanent: 'Das KI-Verteidigungssystem war aufgrund eines technischen Problems nicht in der Lage, auf diese Herausforderung zu reagieren. Die Punkte des Herausforderers wurden zurückerstattet.',
      cubeSubmissionSuccessTitle: 'Herausforderung erfolgreich abgeschickt! 🎯',
      cubeSubmissionSuccessDescription: 'Ihre Herausforderung wird zunächst von unserem KI-Verteidigungssystem bewertet. Wenn die KI die Erklärung nicht angemessen verteidigen kann, wird sie für die menschliche Verteidigung geöffnet.',
      cubeSubmissionSuccessWaitingText: 'Die KI arbeitet jetzt an Ihrer Aufgabe. Sie können den Fortschritt in Ihrem',
      cubeSubmissionSuccessDebatesLink: 'Meine Debatten',
      cubeSubmissionSuccessLinkText: 'wo Sie diese Herausforderung finden können.',
      allFlagsLiftedByUserText: 'Alle Flaggen wurden von {{Benutzername}} eingeholt',
      aiWillRespondAutomaticallyText: 'Die KI wird automatisch reagieren.',
      notifyOnOpponentMessageText: 'E-Mail an mich, wenn der Gegner eine neue Nachricht veröffentlicht',
      linkGuidance: 'Nur Links, die auf der Zulassen-Liste stehen, können mit einem Klick geöffnet werden. Andere URLs erscheinen weiterhin als Text mit einem kurzen Hinweis "nicht geprüft". Um ein Durcheinander zu vermeiden, sollten Sie nicht mehr als 2 Links pro Nachricht angeben. Siehe die aktuelle Whitelist.',
      linkWhitelist: 'Websites auf der Whitelist',
      whitelistPage: {
        title: 'Websites auf der Whitelist',
        description: 'Nur Links von den folgenden Domänen und öffentlichen Suffixen können mit einem Klick in den Debatten geöffnet werden. Andere URLs erscheinen nach wie vor als reiner Text mit einem kurzen Vermerk "nicht geprüft". Diese Liste kann im Laufe der Zeit aktualisiert werden.',
        domains: 'Domains',
        suffixes: 'Öffentliche Suffixe (beliebige Subdomain)',
        note: 'Hinweis: Aus Sicherheitsgründen sind nur http/https-Links erlaubt, und Umleitungen oder verkürzte Links können weiterhin blockiert werden.',
        whyTitle: 'Warum diese Websites?',
        whyBody: 'Whitelist-Domains dienen der Sicherheit, nicht der Autorität. Ein Meinungsartikel in einer Boulevardzeitung kann manchmal eine solide Erklärung enthalten; eine angesehene Domain kann schwache Argumente enthalten. Links sind Referenzen, keine Befürwortungen.',
        howTitle: 'Wie man Links richtig einsetzt',
        howBullet1: 'Fassen Sie die Kernaussage, auf die Sie sich beziehen, in Ihren eigenen Worten zusammen.',
        howBullet2: 'Geben Sie maximal 2 Referenzen pro Nachricht an.',
        howBullet3: 'Schieben Sie das Lesen nicht auf Ihren Gegner ab. Wenn Ihr Argument nur dann stichhaltig ist, wenn sie den Link lesen, haben Sie noch nicht argumentiert.',
        disclaimer: 'Beim Whitelisting geht es um Schadensbegrenzung, nicht um Autorität.',
      },
      protectOpponentInviteWithPassword: 'Ein Passwort verlangen, um den Einladungslink zu akzeptieren',
      invitePasswordLabel: 'Passwort einladen',
      invitePasswordHelp: 'Sowohl für den Link zur Einladung des Gegners als auch für den Link zur Einladung der Mannschaft ist dieses Kennwort erforderlich. Geben Sie es privat an die eingeladenen Teilnehmer weiter.',
      invitePasswordPrompt: 'Diese Einladung zur Debatte ist passwortgeschützt. Geben Sie das vom Herausforderer mitgeteilte Passwort ein:',
      invitePasswordIncorrect: 'Falsches Passwort. Bitte versuchen Sie es erneut.',
      tooManyAttempts: 'Zu viele Versuche. Bitte versuchen Sie es später noch einmal.',
      teamFull: 'Dieses Team ist bereits voll besetzt.',
      showNotes: 'Notizen anzeigen',
      hideNotes: 'Anmerkungen ausblenden',
      yourNotes: 'Ihre Notizen (nur Sie können dies sehen)',
      notesPlaceholder: 'Notieren Sie Gedanken, TODOs, Quellen, um sie später zu überprüfen...',
      notesTooLong: 'Notizen zu lang (max. 50000 Zeichen)',
      linkGuidanceWithLink: 'Links dienen nur als Referenz/Kontext. Verlassen Sie sich nicht darauf, dass der Gegner den Link öffnet. Bringen Sie die Hauptargumente aus der Referenz explizit in diese Debatte ein. Verwenden Sie nicht mehr als 2 Links in einer Nachricht. URLs, die nicht auf der Liste stehen, werden als reiner Text mit einem kurzen Hinweis angezeigt. Siehe <0>Whitelist-Websites</0> für weitere Informationen.',
      continueToHome: 'Weiter zur Startseite',
      registrationVerificationEmailSent: 'Wir haben einen Bestätigungslink an {email} gesendet. Öffnen Sie ihn, um Ihre Adresse zu bestätigen.',
      canLoginLimitedUntilVerified: 'Sie sind eingeloggt. Einige Funktionen bleiben eingeschränkt, bis Sie Ihre E-Mail verifiziert haben.',
      whileWaitingVerifyBrowse: 'Während Sie warten, können Sie öffentliche Debatten verfolgen, Bestenlisten einsehen und das CUBE-System erkunden.',
    },
    debate: {
      topic: 'Thema',
      description: 'Beschreibung',
      teams: 'Mannschaften',
      participants: 'Teilnehmer',
      createDebate: 'Debatte entfachen',
      joinDebate: 'An der Debatte teilnehmen',
      viewDebate: 'Ansicht Debatte',
      startDebate: 'Debatte beginnen',
      generateViews: 'Ansichten generieren',
      customizeViews: 'Anpassen von Ansichten',
      inviteOpponents: 'Gegner einladen',
      debateInProgress: 'Laufende Debatte',
      teamName: 'Name der Mannschaft',
      teamSize: 'Teamgröße',
      publicSpots: 'Öffentliche Spots',
      argument: 'Argument',
      counterArgument: 'Gegenargument',
      evidence: 'Beweise',
      rebuttal: 'Gegenbeweis',
    },
    auth: {
      emailAddress: 'E-Mail Adresse',
      password: 'Passwort',
      username: 'Benutzername',
      confirmPassword: 'Bestätigen Sie Ihr Passwort',
      forgotPassword: 'Passwort vergessen?',
      verifyEmail: 'E-Mail verifizieren',
      emailNotVerified: 'E-Mail nicht verifiziert',
      resendVerification: 'Verifizierung erneut senden',
      checkEmail: 'Prüfen Sie Ihre E-Mail auf einen Verifizierungslink',
      loginSuccess: 'Erfolgreich eingeloggt',
      registrationSuccess: 'Anmeldung erfolgreich',
      verificationSent: 'Verifizierungs-E-Mail gesendet',
      invalidCredentials: 'Ungültige Berechtigungsnachweise',
    },
    errors: {
      general: 'Etwas ist schief gelaufen',
      network: 'Netzwerkfehler',
      unauthorized: 'Unbefugter Zugang',
      forbidden: 'Zugang untersagt',
      notFound: 'Seite nicht gefunden',
      validation: 'Bitte überprüfen Sie Ihre Eingaben',
      emailRequired: 'E-Mail ist erforderlich',
      passwordRequired: 'Passwort ist erforderlich',
    },
    cubePage: {
      title: 'CUBE - CUrrent Best Explanations',
      subtitle: 'Entdecken Sie die hochwertigsten Erklärungen zu wichtigen Themen',
      searchPlaceholder: 'Themen suchen...',
      explanations: 'Erklärungen',
      cubeExplanations: 'CUBE-Erklärungen',
      underChallenge: 'Unter Herausforderung',
      notInCube: 'Derzeit nicht in CUBE',
    },
    cubeDashboard: {
      title: 'CUBE Dashboard',
      subtitle: 'Überwachen Sie Ihre CUBE-Herausforderungsaktivitäten und verteidigen Sie Erklärungen',
      pleaseLogin: 'Bitte melden Sie sich an, um Ihr CUBE-Dashboard anzuzeigen.',
      cubePoints: 'CUBE-Punkte',
      successfulChallenges: 'Erfolgreiche Herausforderungen',
      defensesWon: 'Gewonnene Verteidigungen',
      cubeContributions: 'CUBE-Beiträge',
      total: 'insgesamt',
      finalDecision: 'Endgültige Entscheidung:',
      yourChallengeWasSuccessful: 'Ihre Herausforderung war erfolgreich!',
      originalExplanationMaintained: 'Die ursprüngliche Erklärung wurde beibehalten',
      browseCube: 'CUBE durchsuchen',
      viewDebates: 'Debatten ansehen',
      completed: 'Abgeschlossen',
      failedToLoadData: 'Daten konnten nicht geladen werden',
      failedToAcceptDefense: 'Die Verteidigung nicht akzeptiert',
      points: 'punkte',
      completedOn: 'Abgeschlossen',
    },
    myDashboard: {
      title: 'Mein Dashboard',
      pleaseLogin: 'Bitte melden Sie sich an, um Ihr Dashboard zu sehen.',
      welcomeBack: 'Willkommen zurück, {{Benutzername}}! 👋',
      debateOverview: 'Überblick über die Debatte',
      overallPerformance: 'Ihre Gesamtleistung in allen Debatten',
      totalDebates: 'Totale Debatten',
      quickActions: 'Quick Actions',
      startNewDebate: 'Neue Debatte beginnen',
      browseDebates: 'Debatten durchsuchen',
      exploreCube: 'Explora CUBE',
      viewLeaderboard: 'Ver clasificación',
      yourStats: 'Ihre Estadísticas',
      excaliburIndex: 'Índice Excalibur',
      debatesWon: 'Debatten Ganados',
      currentStreak: 'Racha Actual',
      averageScore: 'Puntuación Media',
    },
    landingPage: {
      title: 'Gauntlet Debates™',
      subtitle: 'Wo die Ehre dem Intellekt zugewandt ist',
      description: 'Participe en debates estructurados y moderados por IA que elevan el discurso y construyen conocimiento.',
      startDebating: 'Empezar a debatir',
      learnMore: 'Mehr Informationen',
      cubeTitle: 'CUBO',
      cubeDescription: 'Entdecken Sie die besten aktuellen Erklärungen zu jedem Thema in unseren hochkarätigen Debatten.',
      leaderboardsTitle: 'Tablas de clasificación',
      leaderboardsDescription: 'Konsultieren Sie Ihre Einstufung in die Gruppe der Schuldner und verfolgen Sie Ihren Fortschritt im Excalibur-Index.',
    },
    rules_guidelines: 'Regeln und Leitlinien',
    cube: 'CUBE',
    leaderboards: 'Bestenlisten',
    about: 'Über',
    'Your Debates': 'common:Ihre Debatten',
    'New Debate': 'gemeinsam:Neue Debatte',
    'Public Debates': 'common:Öffentliche Debatten',
    rulesguidelines: 'RegelnLeitlinien',
    'Rules & Guidelines': 'common:Regeln und Leitlinien',
    'common:Your Debates': 'common:common:Ihre Debatten',
    'common:Public Debates': 'common:common:Öffentliche Debatten',
    'common:New Debate': 'common:common:Neue Debatte',
    view: 'Siehe',
    edit: 'bearbeiten',
    join: 'Beitreten',
    new_debate: 'Neue Debatte',
    your_debates: 'Ihre Debatten',
    delete: 'Löschen',
    public_debates: 'Öffentliche Debatten',
    logout: 'Abmelden',
    register: 'Registrieren',
    back: 'Zurück',
    active: 'Aktiv',
    search: 'Suche',
    cancel: 'Abbrechen',
    submit: 'Einreichen',
    next: 'Weiter',
    completed: 'Abgeschlossen',
    save: 'Speichern Sie',
    pending: 'Anhängig',
    refresh: 'Auffrischen',
    create: 'erstellen.',
    email_verification: 'E-Mail-Überprüfung',
    optional: 'Optional',
    settings: 'Einstellungen',
    welcome: 'Willkommen',
    required: 'Erforderlich',
    loading: 'Laden...',
    yourturn: 'yourTurn',
    viewresults: 'viewResults',
    continuedebate: 'continueDebate',
    activedebates: 'activeDebates',
    completeddebates: 'completedDebates',
    searchdebates: 'searchDebates',
    nodescriptionprovided: 'noDescriptionProvided',
    created: 'erstellt',
    teams: 'Teams',
    noactivedebatesyet: 'noActiveDebatesYet',
    nocompleteddebatesyet: 'noCompletedDebatesYet',
    createyourfirstdebate: 'createYourFirstDebate',
    noactivityforoversixdays: 'noActivityForOverSixDays',
    your_turn: 'Du bist dran',
    view_results: 'Ergebnisse anzeigen',
    continue_debate: 'Debatte fortsetzen',
    you_dont_have_any_active_debates_yet: 'Sie haben noch keine aktiven Debatten',
    create_your_first_debate: 'Erstellen Sie Ihre erste Debatte',
    you_dont_have_any_completed_debates_yet: 'Sie haben noch keine Debatten abgeschlossen',
    search_debates_by_topic_or_description: 'Debatten nach Thema oder Beschreibung suchen...',
    active_debates: 'Aktive Debatten',
    completed_debates: 'Abgeschlossene Debatten',
    no_description_provided: 'Keine Beschreibung vorhanden.',
    no_activity_for_over_6_days: 'Keine Aktivität seit über 6 Tagen',
    no_completed_public_debates_match_your_search: 'Keine abgeschlossenen öffentlichen Debatten entsprechen Ihrer Suche.',
    view_only: 'Nur Ansicht',
    no_active_public_debates_match_your_search: 'Es gibt keine aktiven öffentlichen Debatten zu Ihrer Suche.',
    welcome_to_gauntlet_debates: 'Willkommen bei Gauntlet Debates™',
    no_public_debates_available: 'Keine öffentlichen Debatten verfügbar',
    a_platform_for_structured_honorable_debates: 'Eine Plattform für strukturierte, seriöse Debatten',
    search_your_debates: 'Suchen Sie Ihre Debatten...',
    create_new_debate: 'Neue Debatte eröffnen',
    view_debate: 'Ansicht Debatte',
    please_verify_your_email_address_to_access_all_fea: 'Bitte verifizieren Sie Ihre E-Mail-Adresse, um alle Funktionen nutzen zu können.',
    create_a_new_debate_to_begin_the_discussion: 'Erstellen Sie eine neue Debatte, um die Diskussion zu beginnen',
    sign_up: 'Registrieren',
    verify_email_now: 'E-Mail jetzt verifizieren',
    email_address: 'E-Mail Adresse',
    password: 'Passwort',
    join_debate: 'An der Debatte teilnehmen',
    already_have_an_account: 'Mit bestehendem Konto',
    dont_have_an_account: 'Sie haben noch kein Benutzerkonto?',
    username: 'Benutzername',
    verification_email_sent: 'Verifizierungs-E-Mail gesendet!',
    confirm_password: 'Passwort bestätigen',
    resend_verification_email: 'Verifizierungs-E-Mail erneut senden',
    you_must_verify_your_email_address_to: 'Sie müssen Ihre E-Mail Adresse verifizieren, um',
    jesters_gauntlet: 'Spießrutenlauf des Narren™',
    sign_in: 'Anmelden',
    email_not_verified: 'E-Mail nicht verifiziert',
    invitation_sent: 'Gesendet!',
    email_verification_required: 'E-Mail-Verifizierung erforderlich',
    accept_invitation: 'Einladung annehmen',
    forgot_password: 'Kennwort vergessen?',
    remember_me: 'Angemeldet bleiben',
    invite_to_team: 'Einladung in Team',
    invite_to_debate: 'Zur Debatte einladen',
    you_are_invited: 'Sie sind eingeladn',
    decline_invitation: 'Einladung ablehnen',
    debate_topic: 'Debattenthema',
    time_limit: 'Zeit Limit',
    conduct_guidelines: 'Verhaltensrichtlinien',
    invitation_expired: 'Invitation expired',
    respectful_debate: 'Respektvolle Debatte',
    public_debate: 'Öffentliche Diskussion',
    time_management: 'Zeitmanagement',
    yes: 'Ja',
    allow_public_joining: 'Öffentlichen Beitritt erlauben',
    minutes: 'Minuten',
    days: 'Tage',
    hours: 'Stunden',
    of: 'von',
    no: 'Nein',
    send_invitation: 'Einladung senden',
    success: 'Erfolg',
    continue: 'Weiter',
    finish: 'Oberfläche',
    error: 'Fehler',
    information: 'Informationen',
    warning: 'Warnung',
    evidencebased_arguments: 'Evidenzbasierte Argumente',
    check_your_email_inbox_and_click_the_verification_: 'Überprüfe deinen E-Mail-Posteingang und klicke auf den Bestätigungslink.',
    select_team_size: 'Team auswählen',
    debate_description: 'Beschreibung der Debatte',
    step: 'Schritt',
    close: 'Schließen',
    max_arguments: 'Max. Argumente',
    hide_details: 'Details ausblenden',
    verification_email_sent_successfully_check_your_in: 'Verifizierungs-E-Mail erfolgreich gesendet! Prüfen Sie Ihren Posteingang.',
    limited_access_verify_your_email: '⚔️ Eingeschränkter Zugang - Überprüfen Sie Ihre E-Mail',
    show_details: 'Details anzeigen',
    sending: 'Ich schicke...',
    currently_unavailable: '🚫 Derzeit nicht verfügbar:',
    creating_new_debates: 'Neue Debatten schaffen',
    resend_email: 'E-Mail erneut senden',
    available_now: '✅ Jetzt verfügbar:',
    tip_check_your_spam_folder_if_you_dont_see_the_ver: '💡 Tipp: Überprüfen Sie Ihren Spam-Ordner, wenn Sie die Bestätigungs-E-Mail nicht sehen!',
    accessing_premium_features_future: 'Zugang zu Premium-Funktionen (Zukunft)',
    receiving_email_notifications: 'Empfang von E-Mail-Benachrichtigungen',
    viewing_leaderboards: 'Anzeige der Ranglisten',
    noble_username_your_account_has_limited_access_unt: 'Noble {Benutzername}, Ihr Konto hat nur eingeschränkten Zugang, bis Sie Ihre E-Mail-Adresse verifiziert haben.',
    joining_debates_via_invite_links: 'Teilnahme an Debatten über Einladungslinks',
    participating_in_team_debates: 'Teilnahme an Teamdebatten',
    reading_rules_and_about_pages: 'Lesen von Regeln und Informationsseiten',
    browsing_the_cube: 'Durchsuchen des CUBE',
    viewing_public_debates: 'Öffentliche Debatten verfolgen',
    registration_successful: 'Anmeldung erfolgreich!',
    go_to_login: 'Zum Login gehen',
    passwords_do_not_match: 'Passwörter stimmen nicht überein',
    i_agree_to_the: 'Ich akzeptiere die',
    weve_sent_a_verification_email_to_email: 'Wir haben eine Bestätigungs-E-Mail an {email} gesendet.',
    terms_of_service: 'Bedingungen der Dienstleistung',
    login_here: 'Hier anmelden',
    you_need_to_verify_your_email_address_before_you_c: 'Sie müssen Ihre E-Mail-Adresse verifizieren, bevor Sie {Aktion} durchführen können.',
    password_must_be_at_least_6_characters: 'Das Passwort muss mindestens 6 Zeichen lang sein.',
    your_account_has_been_created_successfully: 'Ihr Konto wurde erfolgreich erstellt!',
    view_public_debates: 'Öffentliche Debatten anzeigen',
    you_can_log_in_now_but_some_features_may_be_limite: 'Sie können sich jetzt anmelden, aber einige Funktionen können eingeschränkt sein, bis Sie Ihre E-Mail verifiziert haben.',
    please_check_your_inbox_and_spam_folder_and_click_: 'Bitte überprüfen Sie Ihren Posteingang (und Ihren Spam-Ordner) und klicken Sie auf den Bestätigungslink, um Ihr Konto zu aktivieren.',
    browse_leaderboards: 'Bestenlisten durchsuchen',
    what_you_can_do_while_unverified: 'Was Sie tun können, solange Sie nicht überprüft wurden:',
    please_check_your_email_and_click_the_verification: 'Bitte prüfen Sie Ihre E-Mail und klicken Sie auf den Bestätigungslink, um fortzufahren.',
    you_must_accept_the_terms_of_service_to_register: 'Um sich zu registrieren, müssen Sie die Allgemeinen Geschäftsbedingungen akzeptieren.',
    weve_sent_a_verification_link_to_email: 'Wir haben einen Bestätigungslink an {email} gesendet.',
    explore_the_cube: 'Erkunden Sie den CUBE',
    create_or_participate_in_debates: 'Erstellen von oder Teilnahme an Debatten',
    overview: 'Übersicht',
    enter_topic: 'Thema eingeben',
    failed_to_generate_debate_views_please_try_again: 'Es konnten keine Debattenansichten erstellt werden. Bitte versuchen Sie es erneut.',
    what_would_you_like_to_debate_about: 'Worüber würden Sie gerne diskutieren?',
    provide_additional_context_or_background_for_your_: 'Geben Sie zusätzlichen Kontext oder Hintergrund für Ihre Debatte an...',
    customize: 'Anpassen',
    review_generated_views: 'Überprüfung der generierten Ansichten',
    your_team_viewpoint: 'Die Sichtweise Ihres Teams',
    opponent_team_name: 'Gegner Mannschaftsname',
    customize_prompt: 'Eingabeaufforderung anpassen',
    customize_counter_prompt: 'Zähler-Eingabeaufforderung anpassen',
    team_configuration: 'Team-Konfiguration',
    team_size: 'Teamgröße',
    please_enter_a_debate_topic: 'Bitte geben Sie ein Diskussionsthema ein',
    create_debate: 'Debatte entfachen',
    create_private_debate: 'Private Debatte erstellen',
    create_public_debate: 'Öffentliche Debatten anstoßen',
    invite_opponents: 'Gegner einladen',
    share_invite_link: 'Einladungslink freigeben',
    copy_invite_link: 'Einladungslink kopieren',
    invite_link_copied_to_clipboard: 'Einladungslink in die Zwischenablage kopiert!',
    onthetopic: 'onTheTopic',
    debatetype: 'debattiertyp',
    publicdebatenotice: 'publicDebateNotice',
    privatedebatenotice: 'privateDebateNotice',
    your_team_name: 'Dein Team Name',
    yourbattlename: 'yourBattleName',
    readcarefullywarning: 'readCarefullyWarning',
    declinechallenge: 'declineChallenge',
    prepareyourdefense: 'prepareYourDefense',
    description: 'Beschreibung',
    yourposition: 'yourPosition',
    pleaseenterteamname: 'pleaseEnterTeamName',
    battlenameplaceholder: 'battleNamePlaceholder',
    acceptthechallenge: 'acceptTheChallenge',
    invalidinvitationlink: 'invalidInvitationLink',
    editasneeded: 'editAsNeeded',
    generate_views: 'Ansichten generieren',
    successfullyjoineddebate: 'successfullyJoinedDebate',
    failedtojoindebate: 'failedToJoinDebate',
    invalidorexpiredinvitation: 'invalidOrExpiredInvitation',
    challengedyoutodebate: 'challengeYouToDebate',
    returntohome: 'returnToHome',
    thegauntlethasbeenthrown: 'theGauntletHasBeenThrown',
    youmustbeloggedin: 'youMustBeLoggedIn',
    pleaselogintoviewinvitation: 'pleaseLoginToViewInvitation',
    pleaseloginorregistertojoin: 'bitteLoginOrRegisterToJoin',
    youropponentsposition: 'yourOpponentsPosition',
    debateofhonor: 'debatteOfHonor',
    public_spots: 'Öffentliche Spots',
    debate_created_successfully: 'Debatte erfolgreich geführt!',
    introduction_to_honorable_debate: 'Einführung in die ehrenwerte Debatte',
    issues_requiring_reformulation_no_penalty: 'Fragen, die eine Neuformulierung erfordern (keine Strafe)',
    productive_debate_relies_on_sound_reasoning_respec: 'Eine produktive Debatte beruht auf einer soliden Argumentation, einem respektvollen Diskurs und der Vermeidung von logischen Fehlschlüssen und rhetorischen Taktiken, die das Verständnis untergraben. Das Jester\'s Gauntlet™-System hilft dabei, Probleme zu erkennen, die eine sinnvolle Konversation zum Scheitern bringen können, und ermöglicht es den Teilnehmern, sich auf den Inhalt und nicht auf die Rhetorik zu konzentrieren.',
    when_your_message_contains_a_flagged_issue_youll_h: 'Wenn Ihr Beitrag einen markierten Punkt enthält, haben Sie die Möglichkeit, ihn neu zu formulieren. Schwerwiegende Punkte zählen als Strafpunkte für Ihre Debattenwertung, während andere Punkte eine Neuformulierung erfordern, aber nicht als Strafpunkte zählen. Das gegnerische Team kann Flaggen, die es für irrelevant hält, aufheben oder eine Neuformulierung verlangen.',
    debate_misconduct_types: 'Arten von Debattenfehlverhalten',
    these_are_overtly_disruptive_techniques_that_count: 'Dies sind offenkundig störende Techniken, die als Strafen zählen. Bei 3 gleichen Techniken oder insgesamt 5 wird die Debatte verloren.',
    debate_rules_summary: 'Zusammenfassung der Debattenregeln',
    counts_against_you: 'Zählt gegen Sie',
    turnbased_structure: 'Rundenbasierter Aufbau',
    teams_take_turns_making_arguments_each_team_has_an: 'Die Teams tragen abwechselnd ihre Argumente vor. Jedes Team hat die Möglichkeit, seinen Standpunkt darzulegen und auf die Punkte des gegnerischen Teams zu antworten.',
    serious_issues_penalties: 'Schwerwiegende Probleme (Sanktionen)',
    our_ai_moderator_analyzes_messages_for_common_deba: 'Unser KI-Moderator analysiert die Nachrichten auf gängige Debattenverstöße. Wenn er potenzielle Probleme identifiziert, kategorisiert er sie in zwei Typen:',
    flagged_messages: 'Markierte Nachrichten',
    reformulation_process: 'Prozess der Neuformulierung',
    when_the_ai_detects_a_jesters_gauntlet_in_a_messag: 'Wenn die KI einen Jester\'s Gauntlet™ in einer Nachricht entdeckt, wird sie markiert. Das gegnerische Team kann entweder Flaggen aufheben, die es für irrelevant hält, oder eine Neuformulierung verlangen.',
    the_team_with_a_flagged_message_must_correct_the_i: 'Das Team mit einer markierten Nachricht muss die festgestellten Probleme beheben, bevor die Debatte fortgesetzt werden kann, um einen qualitativ hochwertigen Diskurs zu gewährleisten.',
    conceding: 'Zugeständnisse',
    a_team_may_concede_the_debate_at_any_time_which_im: 'Ein Team kann die Debatte jederzeit aufgeben, wodurch die Debatte sofort mit dem Sieg des gegnerischen Teams endet.',
    using_formatting_like_all_caps_to_intimidate_rathe: 'Verwenden von Formatierungen (wie GROSSBUCHSTABEN), um einzuschüchtern, anstatt zu argumentieren',
    using_excessive_mockery_that_poisons_discourse: 'Übertriebener Spott, der den Diskurs vergiftet',
    personal_attacks_that_derail_focus_from_the_argume: 'Persönliche Angriffe, die den Fokus vom Argument entgleisen lassen',
    misrepresenting_someones_words_to_create_a_weaker_: 'Falsche Darstellung der Worte einer Person, um ein schwächeres Argument für einen Angriff zu schaffen',
    avoiding_the_argument_by_focusing_on_trivial_error: 'Vermeiden Sie das Argument, indem Sie sich auf triviale Fehler konzentrieren',
    ad_hominem: 'Ad Hominem',
    whataboutism: 'Whataboutism',
    jesters_gauntlet_penalties: 'Narrenhandschuh™ -Strafen',
    if_there_are_no_posts_for_a_week_the_ai_will_summa: 'Wenn es eine Woche lang keine Beiträge gibt, fasst die KI die Debatte zusammen und versucht, einen Gewinner basierend auf der Argumentationsqualität zu ermitteln oder als Unentschieden zu deklarieren.',
    strawman: 'Strohmann',
    changing_criteria_of_an_argument_when_original_cri: 'Ändern von Kriterien eines Arguments, wenn die ursprünglichen Kriterien erfüllt sind',
    if_a_team_accumulates_3_of_the_same_type_of_seriou: 'Wenn ein Team 3 der gleichen Art von schwerwiegenden Vergehen oder 5 schwerwiegende Vergehen ansammelt, verliert es automatisch die Debatte.',
    deflecting_from_the_current_point_by_bringing_up_u: 'Vom aktuellen Punkt abweichen, indem nicht zusammenhängende Probleme angesprochen werden',
    sealioning: 'Sealioning',
    inactive_debates: 'Inaktive Debatten',
    relentless_questioning_that_exhausts_rather_than_e: 'Unaufhörliches Infragestellen, das erschöpft, anstatt sich zu engagieren',
    misrepresenting_someones_argument_to_create_a_weak: 'Falsche Darstellung des Arguments einer Person, um eine schwächere Version für den Angriff zu erstellen',
    gish_gallop: 'Gish Gallop',
    sarcasm_overload: 'Sarkasmusüberlastung',
    distorting_quotes_out_of_context_to_mislead_delibe: 'Verzerren von Zitaten aus dem Kontext, um sie absichtlich in die Irre zu führen',
    word_twisting: 'Wortverdrehung',
    textual_intimidation: 'Textliche Einschüchterung',
    quote_sniping: 'Zitat Sniping',
    pretending_not_to_understand_to_stall_or_derail_di: 'Vortäuschen, nicht zu verstehen, um die Diskussion ins Stocken zu bringen oder zu entgleisen',
    using_irrelevant_citations_or_authorities_that_can: 'Verwendung irrelevanter Zitate oder ersetzbarer Behörden',
    appeal_to_emotion: 'Appell an Emotionen',
    appeal_to_authority: 'Appell an die Behörde',
    requires_reformulation: 'Erfordert eine Neuformulierung',
    making_bold_claims_without_evidence_or_substance: 'Mutige Behauptungen ohne Beweise oder Substanz',
    forcing_an_eitheror_choice_to_oversimplify: 'Eine "Entweder-Oder" -Option erzwingen, um zu stark zu vereinfachen',
    serious_issues_count_as_penalties: 'Schwerwiegende Probleme (zählen als Sanktionen)',
    false_dichotomy: 'Falsche Dichotomie',
    internal_code: 'Interne Nummer',
    grammar_policing: 'Grammatik-Policing',
    circular_reasoning: 'Zirkelschluss',
    applying_doubt_unevenly_based_on_bias: 'Zweifel aufgrund von Voreingenommenheit ungleichmäßig anwenden',
    overgeneralizations: 'Übergeneralisierungen',
    using_selective_evidence_that_is_misleading_but_co: 'Verwendung selektiver Beweise, die irreführend, aber korrigierbar sind',
    better_approach: 'Ein besserer Ansatz',
    example_of_type: 'Beispiel von {type}:',
    feigning_confusion: 'Verwirrung vortäuschen',
    overwhelming_with_too_many_points_or_links_to_prev: 'Überwältigend mit zu vielen Punkten oder Links, um eine sinnvolle Reaktion zu verhindern',
    sweeping_statements_that_lack_precision_but_can_be: 'Umfassende Aussagen, denen es an Präzision mangelt, die aber verfeinert werden können',
    selective_skepticism: 'Selektive Skepsis',
    unsubstantiated_claims: 'Unbestätigte Behauptungen',
    shifting_goalposts: 'Verschieben von Zielpfosten',
    assuming_the_conclusion_in_the_premise: 'Annahme des Abschlusses in der Prämisse',
    using_emotional_appeals_that_bypass_logic_but_can_: 'Verwendung emotionaler Appelle, die die Logik umgehen, aber umformuliert werden können',
    cherrypicking: 'Wählerisch',
    has_challenged_you_to_a: 'hat Sie herausgefordert zu einem',
    the_gauntlet_has_been_thrown: 'Der Fehdehandschuh ist geworfen!',
    on_the_topic: 'zu diesem Thema',
    debate_of_honor: 'Debatte über die Ehre',
    this_debate_will_only_be_visible_to_participants: 'Diese Debatte wird nur für die Teilnehmer sichtbar sein.',
    your_opponents_position: 'Die Position Ihres Gegners',
    this_debate_will_be_visible_to_anyone_visiting_gau: 'Diese Debatte wird für jeden Besucher von Gauntlet Debates™ sichtbar sein und zählt zu Ihrem öffentlichen Punktestand.',
    your_position: 'Ihr Standpunkt',
    prepare_your_defense: 'Bereiten Sie Ihre Verteidigung vor',
    edit_as_needed: 'Nach Bedarf bearbeiten',
    read_carefully_what_youll_be_defending_the_first_d: 'Lesen Sie genau, was Sie verteidigen werden! Die erste unehrenhafte Handlung könnte in dem vorformulierten Standpunkt versteckt sein. Nehmen Sie sich einen Moment Zeit, um ihn so zu bearbeiten, dass er mit dem übereinstimmt, was Sie wirklich verteidigen wollen.',
    your_battle_name: 'Ihr Kampfname',
    decline_challenge: 'Herausforderung Ablehnen',
    invalid_or_expired_invitation_link: 'Ungültiger oder abgelaufener Einladungslink',
    successfully_joined_the_debate: 'Erfolgreich an der Debatte teilgenommen!',
    accept_the_challenge: 'Nehmen Sie die Herausforderung an',
    invalid_invitation_link: 'Ungültiger Einladungslink',
    failed_to_join_debate: 'Nicht an der Debatte teilgenommen',
    please_enter_a_team_name: 'Bitte geben Sie einen Teamnamen ein',
    please_login_to_view_this_invitation: 'Bitte melden Sie sich an, um diese Einladung zu sehen',
    please_login_or_register_to_join_this_debate: 'Bitte anmelden oder registrieren, um an dieser Debatte teilzunehmen',
    enter_a_name_that_inspires_fear_in_your_opponent: 'Gib einen Namen ein, der deinem Gegner Angst einflößt',
    you_must_be_logged_in_to_accept_this_challenge_you: 'Sie müssen angemeldet sein, um diese Herausforderung anzunehmen. Sie werden zur Anmeldung weitergeleitet.',
    flag_type: 'Flagge Typ:',
    concede_defeat: 'Niederlage eingestehen',
    are_you_sure_you_want_to_concede_this_debate_your_: 'Sind Sie sicher, dass Sie diese Debatte aufgeben wollen? Ihr Team wird als Verlierer gewertet.',
    debate_results: 'Ergebnisse der Debatte',
    this_debate_has_concluded: 'Diese Debatte ist abgeschlossen.',
    debate_completed: 'Debatte abgeschlossen',
    the_winner_is: 'Der Gewinner ist',
    view_summary: 'Zusammenfassung anzeigen',
    a_summary_of_the_debate_has_been_generated_and_is_: 'Eine Zusammenfassung der Debatte wurde erstellt und wird oben auf der Seite angezeigt.',
    reformulation_needed: 'Möglicherweise Neuformulierung nötig:',
    jesters_gauntlet_detected: 'Spießrutenlauf des Narren entdeckt:',
    options: 'Optionen:',
    reformulate_your_message: 'Formulieren Sie Ihre Botschaft neu',
    message_flags: 'Nachrichten-Flags',
    this_message_contains_language_or_reasoning_that_r: 'Diese Nachricht enthält Formulierungen oder Argumente, die neu formuliert werden müssen.',
    back_home: 'Zurück zur Startseite',
    concede_debate: 'Debatte zugeben',
    swap_views: 'Ansichten tauschen',
    rank: 'Rang',
    view_top_performers_in_public_debates_only_metrics: 'Sehen Sie sich die besten Leistungen in öffentlichen Debatten an. Für diese Rangliste werden nur die Ergebnisse öffentlicher Debatten berücksichtigt.',
    ranking_by: 'Rangliste nach',
    top_debates: 'Top-Debatten',
    top_debaters: 'Top-Debattierer',
    win_rate: 'Gewinnrate',
    rebuttal_effectiveness: 'Wirksamkeit der Gegenargumente',
    excalibur_index: 'Excalibur-Index',
    explanation_strength_hard_to_vary: 'Erläuterung Stärke (schwer zu variieren)',
    logical_coherence: 'Logische Kohärenz',
    message_count: 'Anzahl der Nachrichten',
    civil_discourse: 'Zivilgesellschaftlicher Diskurs',
    topic: 'Thema',
    steelmanning_ability: 'Stahlmanning-Fähigkeit',
    explanation_quality: 'Erläuterung Qualität',
    overall_quality: 'Allgemeine Qualität',
    date: 'Datum',
    no_users_found_be_the_first_to_participate_in_publ: 'Keine Benutzer gefunden. Seien Sie der Erste, der sich an öffentlichen Debatten beteiligt!',
    wins: 'Siege',
    user: 'Benutzer',
    argument_clarity: 'Klarheit der Argumente',
    evidence_quality: 'Qualität der Nachweise',
    no_debates_found_be_the_first_to_create_a_public_d: 'Keine Debatten gefunden. Seien Sie der Erste, der eine öffentliche Debatte anregt!',
    key_features: 'Wesentliche Merkmale',
    our_mission: 'Unser Auftrag',
    structured_debate: 'Strukturierte Debatte',
    fair_resolutions: 'Faire Lösungen',
    throwing_down_the_gauntlet: 'Den Fehdehandschuh hinwerfen',
    honorbased_scoring: 'Ehrenbasierte Punktevergabe',
    comprehensive_summaries_of_debates_serve_as_a_valu: 'Umfassende Zusammenfassungen von Debatten dienen als wertvolle Ressource für das Verständnis komplexer Themen und den Zugang zu den aktuell besten Erklärungen.',
    when_someone_makes_a_claim_you_believe_should_be_e: 'Wenn jemand eine Behauptung aufstellt, die Ihrer Meinung nach genauer geprüft werden sollte, können Sie ihm den "Fehdehandschuh hinwerfen", indem Sie ihn auffordern, seinen Standpunkt in einer ehrenhaften Debatte zu verteidigen.',
    argument_repository: 'Argument-Repository',
    this_ancient_practice_of_challenging_someone_to_pr: 'Diese uralte Praxis, jemanden herauszufordern, seine Behauptungen zu beweisen, findet auf unserer Plattform neues Leben, wo der Schwerpunkt auf Substanz und nicht auf Rhetorik liegt. Teilen Sie einen Link zu einer Debatte als ehrenvolle Herausforderung: "Glauben Sie, dass Sie das, was Sie gerade gesagt haben, in einem strukturierten, fairen Gespräch verteidigen können?"',
    clear_guidelines_to_ensure_fair_and_focused_discus: 'Klare Leitlinien zur Gewährleistung fairer und zielgerichteter Diskussionen zwischen gegensätzlichen Standpunkten.',
    opposing_teams_have_the_ability_to_lift_flags_on_m: 'Die gegnerischen Teams haben die Möglichkeit, Flaggen für Nachrichten, die gegen die Debattenregeln verstoßen, aufzuheben oder den Flaggen zuzustimmen und eine Neuformulierung zu verlangen.',
    gauntlet_debates_addresses_a_fundamental_problem_i: 'Gauntlet Debates befasst sich mit einem grundlegenden Problem in unserem digitalen Diskurs: dem Vorherrschen schlechter Argumente und rhetorischer Taktiken ("Jester\'s Gauntlets"), die darauf abzielen, Debatten ohne Rücksicht auf Wahrheit oder Verständnis zu gewinnen. Diese Taktiken spalten die Menschen in Stämme, denen das Gewinnen wichtiger ist als die Entdeckung der Wahrheit, was es immer schwieriger macht, produktive Gespräche über komplexe Themen zu führen.',
    we_believe_democracy_depends_on_our_ability_to_eng: 'Wir sind davon überzeugt, dass die Demokratie von unserer Fähigkeit abhängt, ehrliche, rigorose Diskussionen über wichtige Themen zu führen. Unsere Plattform bietet eine strukturierte Umgebung, in der die KI-Moderation Irrtümer in Echtzeit identifiziert und es den Teilnehmern ermöglicht, sich auf die Entwicklung guter Argumente zu konzentrieren, anstatt rhetorische Tricks anzuwenden. Unsere langfristige Vision ist es, ein Wikipedia-ähnliches Repository der besten aktuellen Erklärungen zu jedem Thema zu schaffen, das durch ehrenhafte Debatten kuratiert wird. Diese Wissensbasis wird den Menschen helfen, komplexe Themen anhand der stärksten verfügbaren Argumente zu verstehen, und nicht anhand derjenigen, die am stärksten vorgetragen oder am häufigsten wiederholt werden.',
    aipowered_detection_of_logical_fallacies_and_debat: 'KI-gestützte Erkennung von logischen Fehlern und Debattenverstößen in Echtzeit.',
    about_gauntlet_debates: 'Über Gauntlet Debates™',
    a_point_system_that_rewards_quality_arguments_and_: 'Ein Punktesystem, das die Qualität der Argumente belohnt und die Irrtümer der Debatte bestraft.',
    a_platform_for_structured_aimoderated_debates_that: 'Eine Plattform für strukturierte, KI-moderierte Debatten, die einen nachdenklichen Diskurs fördern',
    how_it_works: 'Wie es funktioniert',
    performance_component_25: 'Leistungskomponente (25%)',
    learning_curve: 'Lernkurve',
    good_faith_component_15: 'Gutgläubigkeitskomponente (15%)',
    '2_strongpresent_your_argumentsstrong_each_team_tak': '2. <strong>Präsentiert eure Argumente:</strong> Jedes Team präsentiert abwechselnd Argumente zur Unterstützung seines Standpunkts. Die künstliche Intelligenz achtet auf Irrtümer und Debattentaktiken, die gegen die Richtlinien verstoßen.',
    stronghow_its_calculatedstrong_the_excalibur_index: '<strong>Wie wird er berechnet:</strong> Der Excalibur Index kombiniert drei Schlüsselkomponenten:',
    calculated_from_win_rate_and_scaled_by_participati: 'Berechnet aus der Gewinnquote und skaliert mit der Teilnahmequote, um beständigen Erfolg zu belohnen.',
    '3_strongmaintain_debate_qualitystrong_if_a_message': '3. <strong>Die Qualität der Debatte aufrechterhalten:</strong> Wenn eine Nachricht markiert ist, können gegnerische Teams die Markierungen aufheben, die sie für irrelevant halten, oder um eine Umformulierung bitten.',
    we_maintain_separate_public_and_private_indices_to: 'Wir führen getrennte öffentliche und private Indizes, um sicherzustellen, dass die Debattanten im privaten Rahmen üben können, ohne dass ihr öffentliches Ranking beeinträchtigt wird. Nur der öffentliche Excalibur-Index wird in den Bestenlisten angezeigt.',
    evolving_with_our_community: 'Entwicklung mit unserer Gemeinschaft',
    measures_steelmanning_ability_85_representing_oppo: 'Stahlmanning-Fähigkeit (85 %) - faire Darstellung gegensätzlicher Standpunkte und Pünktlichkeit der Antwort (15 %).',
    quality_component_60: 'Qualitätskomponente (60%)',
    the_excalibur_index: 'Der Excalibur-Index',
    based_on_explanation_strength_40_following_david_d: 'Basierend auf der Stärke der Erklärung (40 %) nach David Deutschs Konzept der "schwer zu variierenden" Erklärungen, der Wirksamkeit der Widerlegung (25 %), der Qualität der Beweise (20 %) und der Klarheit der Argumente (15 %), wie von AI bewertet.',
    unlike_typical_online_arguments_our_platform_rewar: 'Im Gegensatz zu typischen Online-Diskussionen belohnt unsere Plattform intellektuell ehrliche Teilnehmer, die ihre Ansichten klar artikulieren können, ohne zu falschen Argumenten zu greifen. Das "Jester\'s Gauntlets"-System unterscheidet zwischen ehrlichen Debattierern und solchen, die sich auf rhetorische Tricks verlassen.',
    we_actively_encourage_users_to_identify_new_rhetor: 'Wir ermutigen die Benutzer aktiv, neue rhetorische Taktiken zu identifizieren, die als Jester\'s Gauntlets klassifiziert werden sollten. Ihr Feedback fließt direkt in die Weiterentwicklung unseres Systems ein.',
    the_excalibur_index_is_our_comprehensive_metric_fo: 'Der Excalibur-Index ist unser umfassender Maßstab für herausragende Debatten. So wie das legendäre Schwert nur von den wahrhaft Würdigen geführt werden konnte, zeichnet unser Index diejenigen aus, die sich in der ehrenhaften Debatte auszeichnen.',
    the_jesters_gauntlet_system_like_any_aipowered_too: 'Das Jester\'s Gauntlet™-System ist, wie jedes KI-gestützte Werkzeug, ein sich ständig weiterentwickelndes Projekt. Wir erkennen mehrere wichtige Aspekte unserer Plattform an:',
    communitydriven_improvement: 'Von der Gemeinschaft gesteuerte Verbesserung',
    '5_stronginsightful_summaries_final_scoresstrong_ea': '5. <strong>Aufschlussreiche Zusammenfassungen und Endnoten:</strong> Jede Debatte endet mit einer KI-generierten Zusammenfassung, in der die besten Argumente beider Seiten hervorgehoben werden, sowie mit Endnoten, die die Leistung der einzelnen Teams widerspiegeln. Diese Kombination aus qualitativer Beurteilung und quantitativer Bewertung macht unsere Plattform zu einer wertvollen Fundgrube für gut begründete Perspektiven zu komplexen Themen.',
    strongtechnical_implementationstrong_the_excalibur: '<strong>Technische Umsetzung:</strong> Der Excalibur-Index verwendet derzeit ChatGPT-4o (das neueste OpenAI-Modell), um die Qualität der Debatte, die Stärke der Argumente und die Fähigkeit des Steelmaning zu bewerten. Wenn sich die KI-Technologie weiterentwickelt, können wir unsere Modelle aktualisieren, um die höchsten Standards bei der Bewertung beizubehalten, wobei Fairness und Tiefe der Analyse immer im Vordergrund stehen.',
    there_is_a_learning_period_when_first_using_the_pl: 'Wenn Sie die Plattform zum ersten Mal nutzen, müssen Sie sich erst einarbeiten. Wenn Sie sich mit unseren Leitlinien vertraut machen, werden Sie ein natürliches Gespür für die Formulierung von Argumenten entwickeln, die sich auf den Inhalt und nicht auf die Rhetorik konzentrieren.',
    the_excalibur_index_is_displayed_on_a_scale_of_010: 'Der Excalibur-Index wird auf einer Skala von 0-100 mit einer Nachkommastelle angezeigt, was eine feine Unterscheidung zwischen den Debattierern ermöglicht. Eine Punktzahl von 100 steht für Perfektion in allen Debattenmetriken, ein praktisch unerreichbares Ideal, das selbst den versiertesten Debattierern Raum für Wachstum lässt.',
    '1_strongcreate_or_join_a_debatestrong_start_a_new_': '1. <strong>Erstellen Sie eine Debatte oder nehmen Sie an einer teil:</strong> Starten Sie eine neue Debatte mit einem klaren Thema und gegensätzlichen Standpunkten oder nehmen Sie an einer bestehenden Debatte teil.',
    '4_strongconclude_the_debatestrong_debates_can_end_': '4. <strong>Beenden Sie die Debatte:</strong> Debatten können auf verschiedene Arten beendet werden: wenn ein Team aufgibt, wenn ein Team zu viele Strafpunkte sammelt oder automatisch nach einer Woche Inaktivität (in diesem Fall analysiert die KI die Debatte, um einen Gewinner zu ermitteln oder sie für unentschieden zu erklären).',
    check_public_debates: 'Öffentliche Debatten prüfen',
    false_positives: 'Falsche Positivmeldungen',
    when_a_message_is_flagged_view_it_as_an_opportunit: 'Wenn eine Nachricht beanstandet wird, sehen Sie dies als Chance und nicht als Rückschlag. Der Prozess der Neuformulierung führt oft zu stärkeren, klareren Argumenten, die dem eigentlichen Zweck besser dienen: komplexe Themen durch einen durchdachten Diskurs zu verstehen.',
    join_our_community_of_thoughtful_debaters_and_enga: 'Schließen Sie sich unserer Gemeinschaft von nachdenklichen Debattierern an und beteiligen Sie sich an sinnvollen Diskussionen über wichtige Themen.',
    start_a_new_debate: 'Eine neue Debatte beginnen',
    sometimes_messages_may_be_incorrectly_flagged_when: 'Manchmal kann es vorkommen, dass Nachrichten fälschlicherweise gekennzeichnet werden. In diesem Fall sollten Sie daran denken, dass eine Neuformulierung eine Gelegenheit ist, Ihren Standpunkt zu verdeutlichen, und dass die gegnerischen Teams Flaggen aufheben können, die sie für ungerechtfertigt halten.',
    our_system_will_likely_never_be_perfect_and_thats_: 'Unser System wird wahrscheinlich nie perfekt sein, und das ist auch so gewollt. Die Natur von Sprache und Rhetorik ist zu komplex für absolute Regeln. Was zählt, ist unser Engagement für kontinuierliche Verbesserungen und das gemeinsame Ziel, die Qualität der Debatten zu verbessern. Wir glauben, dass selbst unvollkommene Leitplanken uns zu produktiveren Gesprächen führen können, als wenn es überhaupt keine Standards gäbe.',
    ready_to_start_debating: 'Bereit zum Debattieren?',
    eg_artificial_consciousness_is_achievable_through_: 'z. B. Künstliches Bewusstsein ist mit den heutigen Methoden des maschinellen Lernens erreichbar',
    description_optional: 'Beschreibung (fakultativ)',
    add_context_constraints_or_specific_aspects_you_wa: 'Fügen Sie Kontext, Einschränkungen oder spezifische Aspekte hinzu, die Sie in der Debatte behandeln möchten',
    use_this_to_provide_additional_context_we_recommen: 'Verwenden Sie dies, um zusätzlichen Kontext zu liefern. Wir empfehlen 150-300 Zeichen für beste Ergebnisse.',
    tips_for_a_productive_debate: 'Tipps für eine produktive Debatte:',
    specify_a_focused_topic_that_can_be_argued_effecti: 'Geben Sie ein Schwerpunktthema an, zu dem von verschiedenen Seiten effektiv argumentiert werden kann.',
    choose_a_topic_that_can_lead_to_focused_welldefine: 'Wählen Sie ein Thema, das zu gezielten, klar umrissenen Ansichten führen kann. Spezifische Themen (z. B. "Sollten die Subventionen für erneuerbare Energien in der EU erhöht werden?") eignen sich besser als zu weit gefasste Themen (z. B. "Klimawandel").',
    our_ai_has_generated_two_opposing_viewpoints_for_y: 'Unsere KI hat zwei gegensätzliche Standpunkte für Ihr Diskussionsthema erstellt. Sie können diese Standpunkte bearbeiten, um die Perspektiven, die Sie erkunden möchten, besser wiederzugeben.',
    review_and_customize_debate_views: 'Überprüfen und Anpassen von Debattenansichten',
    view_1_you_will_defend_this_position: 'Ansicht 1 (Sie werden diesen Standpunkt verteidigen):',
    view_2_your_opponent_will_defend_this_position: 'Ansicht 2 (Ihr Gegner wird diese Position verteidigen):',
    your_debate_will_be_visible_to_everyone_your_argum: 'Ihre Debatte wird für alle sichtbar sein. Ihre Argumente und Ihr Diskussionsstil tragen zu Ihrer offiziellen Punktzahl in der Rangliste bei. Dies hilft anderen, die sich für das Thema interessieren, hochwertige Erklärungen zu finden.',
    number_of_spots_that_can_be_filled_by_any_public_u: 'Anzahl der Plätze, die von jedem öffentlichen Nutzer besetzt werden können. Das Maximum ist die Größe des gegnerischen Teams.',
    private_debate: 'Private Debatte',
    your_debate_will_only_be_visible_to_participants_t: 'Ihre Debatte wird nur für die Teilnehmer sichtbar sein. Dies ist ideal zum Üben oder für die Diskussion sensibler Themen.',
    the_maximum_number_of_members_allowed_in_the_oppon: 'Die maximale Anzahl der Mitglieder, die im gegnerischen Team erlaubt sind. Standard ist 1.',
    configure_teams_and_debate_settings: 'Teams und Debatteneinstellungen konfigurieren',
    make_debate_publicly_joinable_allow_users_to_join_: 'Machen Sie die Debatte öffentlich zugänglich (erlauben Sie Nutzern, ohne Einladung teilzunehmen, wenn öffentliche Plätze verfügbar sind)',
    the_maximum_number_of_members_allowed_in_your_team: 'Die maximale Anzahl von Mitgliedern in Ihrem Team, einschließlich Ihnen selbst. Standard ist 1 (nur Sie).',
    debate_visibility: 'Sichtbarkeit von Debatten',
    increase_team_size_to_allow_public_spots: 'Teamgröße erhöhen, um öffentliche Plätze zu ermöglichen',
    debate_information: 'Informationen zur Debatte',
    members: 'Mitglieder',
    review_all_details_below_when_you_create_the_debat: 'Prüfen Sie alle Details unten. Wenn Sie die Debatte erstellen, erhalten Sie Einladungslinks, die Sie mit Ihren Teammitgliedern und dem Gegner teilen können.',
    member: 'Mitglied',
    publicly_joinable: 'Öffentlicher Beitritt möglich:',
    your_viewpoint: 'Ihr Blickwinkel:',
    including_you: '(einschließlich Ihnen)',
    debate_overview: 'Überblick über die Debatte',
    opponent_viewpoint: 'Standpunkt des Gegners:',
    visibility: 'Sichtbarkeit:',
    invite_opponent: 'Gegner einladen',
    share_this_link_with_people_you_want_to_join_your_: 'Geben Sie diesen Link an Personen weiter, die Sie für Ihr Team gewinnen möchten:',
    share_this_link_with_the_person_you_want_to_debate: 'Teilen Sie diesen Link mit der Person, gegen die Sie debattieren möchten:',
    your_debate_has_been_created_successfully: 'Ihre Debatte wurde erfolgreich erstellt!',
    invite_members_to_your_team: 'Laden Sie Mitglieder in Ihr Team ein',
    the_links_will_work_until_your_debate_is_completed: 'Die Links funktionieren so lange, bis Ihre Debatte abgeschlossen ist. Alle Teilnehmer müssen sich registrieren oder anmelden, um teilnehmen zu können.',
    share_this_link_with_the_people_you_want_to_debate: 'Geben Sie diesen Link an die Personen weiter, mit denen Sie diskutieren möchten:',
    share: 'Teilen Sie',
    go_to_debate_room: 'Zum Debattenraum gehen',
    copy_opponents_link: 'Link des Gegners kopieren',
    copy_opponent_link: 'Gegnerischen Link kopieren',
    private: 'Privat',
    your_position_edit_as_needed: 'Ihre Position (bei Bedarf bearbeiten):',
    important: 'Das ist wichtig:',
    your_opponents_position_readonly: 'Die Position Ihres Gegners (Nur-Lesezugriff):',
    your_team: 'Ihr Team:',
    opponent_team: 'Gegnerische Mannschaft:',
    return_to_home: 'Zurück zur Startseite',
    please_login_or_register_to_join_this_team: 'Bitte anmelden oder registrieren, um diesem Team beizutreten',
    you_must_be_logged_in_to_join_this_team_youll_be_r: 'Sie müssen eingeloggt sein, um diesem Team beizutreten. Sie werden zum Login weitergeleitet.',
    you_are_being_called_to_stand_with: 'Sie sind aufgerufen, an der Seite von',
    a_fellow_knight: 'Ein ritterlicher Gefährte',
    public: 'Öffentlich',
    you_are: 'Sie sind',
    a_call_to_arms: 'Ein Aufruf zu den Waffen!',
    stands: 'steht',
    debate_type: 'Art der Debatte:',
    fellow_knight: 'Fellow Knight:',
    this_team_has_not_been_named_yet: 'Dieses Team wurde noch nicht benannt.',
    knight: 'Ritter',
    ready: 'bereit',
    failed_to_join_team: 'Nicht zum Team gestoßen',
    no_knights_have_joined_yet: 'Es sind noch keine Ritter beigetreten.',
    join_the_noble_cause: 'Schließen Sie sich der edlen Sache an',
    your_teams_position: 'Die Position Ihres Teams:',
    failed_to_join_team_please_try_again: 'Beitritt zum Team fehlgeschlagen. Bitte versuchen Sie es erneut.',
    stand: 'Stand',
    this_teams_position_has_not_been_defined_yet: 'Die Position dieses Teams ist noch nicht festgelegt.',
    calling_for_allies_in_an_upcoming: 'Verbündete für eine bevorstehende',
    knights: 'Ritter',
    successfully_joined_the_team: 'Erfolgreich ins Team gekommen!',
    decline_the_call: 'Ablehnen des Anrufs',
    join_the_ranks: 'In die Ränge eintreten',
    in_defense_of_honor_and_truth_your_voice_and_wisdo: 'zur Verteidigung von Ehre und Wahrheit. Ihre Stimme und Ihre Weisheit werden in diesem Kampf des Verstandes gebraucht.',
    current_members: 'Derzeitige Mitglieder:',
    in_progress: 'In Arbeit',
    messages: 'Nachrichten:',
    team_members: 'Teammitglieder',
    status: 'Status:',
    last_message: 'Letzte Nachricht:',
    jesters_gauntlet_counts: 'Narrenhandschuh Zählt',
    no_messages_yet: 'Noch keine Nachrichten',
    no_penalties: 'Keine Sanktionen',
    about_penalties: 'Über Sanktionen:',
    started: 'Angefangen:',
    back_to_home: 'Zurück zur Startseite',
    no_messages_yet_start_the_debate_by_sending_a_mess: 'Noch keine Nachrichten. Starten Sie die Debatte, indem Sie eine Nachricht senden.',
    teams_must_reformulate_messages_that_contain_issue: 'Teams müssen Nachrichten, die Probleme enthalten, neu formulieren. Schwere Narrenstulpen zählen als Strafe, während kleinere Vergehen nur eine Neuformulierung erfordern. Das gegnerische Team kann Flaggen aufheben, wenn sie als irrelevant angesehen werden.',
    waiting_for_the_first_team_to_start_the_debate: 'Ich warte darauf, dass das erste Team die Debatte eröffnet.',
    youve_chosen_to_reformulate_your_message_please_ad: 'Sie haben sich entschieden, Ihre Nachricht umzuformulieren. Bitte gehen Sie in Ihrer Antwort auf die markierten Punkte ein.',
    the_other_team_has_requested_that_you_reformulate_: 'Das andere Team hat Sie gebeten, Ihre Nachricht umzuformulieren. Bitte gehen Sie in Ihrer Antwort auf die markierten Punkte ein.',
    choose_to_reformulate_your_message_again: 'Entscheiden Sie sich, Ihre Botschaft noch einmal neu zu formulieren.',
    choose_to_reformulate_your_message_now: 'Entscheiden Sie sich jetzt, Ihre Botschaft neu zu formulieren.',
    if_there_are_penalty_flags_reformulating_will_not_: 'Wenn es Strafflaggen gibt, wird die Strafe durch eine Neuformulierung NICHT aufgehoben. Nur das andere Team, das alle Flaggen aufhebt, kann dies tun.',
    teams_lose_if_they_accumulate_3_penalties_of_the_s: 'Teams verlieren, wenn sie 3 Strafen der gleichen Art oder insgesamt 5 Strafen kassieren. Geringfügige Probleme erfordern eine Neuformulierung, werden aber nicht auf das Ergebnis angerechnet.',
    realtime_connection_established: 'Verbindung in Echtzeit hergestellt',
    the_other_team_has_chosen_to_reformulate_their_mes: 'Das andere Team hat sich für eine Neuformulierung seiner Botschaft entschieden',
    reformulation_request_sent_waiting_for_the_other_t: 'Antrag auf Neuformulierung gesendet. Warten auf die Neuformulierung durch das andere Team.',
    all_flags_have_been_lifted: 'Alle Flaggen sind eingeholt worden',
    you_have_chosen_to_reformulate_your_message: 'Sie haben sich entschieden, Ihre Botschaft neu zu formulieren.',
    failed_to_lift_some_flags_please_try_again: 'Einige Flaggen konnten nicht gehoben werden. Bitte versuchen Sie es erneut.',
    failed_to_send_reformulation_request: 'Die Anfrage zur Neuformulierung konnte nicht gesendet werden.',
    you_have_conceded_the_victory_goes_to: 'Sie haben sich geschlagen gegeben. Der Sieg geht an',
    failed_to_concede_the_debate_please_try_again: 'Sie konnten die Debatte nicht beenden. Bitte versuchen Sie es erneut.',
    failed_to_export_debate: 'Die Debatte wurde nicht exportiert',
    link_copied: 'Link kopiert!',
    copy_debate_link: 'Link zur Debatte kopieren',
    export_debate: 'Debatte über die Ausfuhr',
    lift_all_flags: 'Alle Flaggen hissen',
    request_reformulation: 'Antrag auf Neuformulierung',
    send_message: 'Nachricht senden',
    reformulate: 'Neu formulieren',
    waiting: 'Warten',
    is_typing: 'tippt',
    are_typing: 'tippen',
    and: 'und',
    others: 'andere',
    loading_debate: 'Debatte laden...',
    debate_not_found: 'Debatte nicht gefunden',
    error_loading_debate: 'Fehler beim Laden der Debatte',
    retry: 'Wiederholung',
    login: 'Anmelden',
    please_login_to_participate_in_this_debate: 'Bitte melden Sie sich an, um an dieser Debatte teilzunehmen',
    error_lifting_flag_missing_message_id: 'Fehler beim Aufheben der Flagge: Fehlende Nachrichten-ID',
    failed_to_initiate_reformulation: 'Die Neuformulierung wurde nicht in die Wege geleitet.',
    flag_has_been_lifted: 'Die Flagge wurde gehisst',
    error_lifting_flag_please_try_again: 'Fehler beim Aufheben der Flagge. Bitte versuchen Sie es erneut.',
    could_not_identify_opposing_team: 'Konnte die gegnerische Mannschaft nicht identifizieren',
    there_was_an_issue_updating_the_debate_status_on_t: 'Es gab ein Problem bei der Aktualisierung des Debattenstatus auf dem Server, aber die Debatte wurde lokal als abgeschlossen markiert.',
    failed_to_end_the_debate_after_concession_please_t: 'Die Debatte konnte nach dem Zugeständnis nicht beendet werden. Bitte versuchen Sie es erneut.',
    only_the_first_team_can_start_the_debate: 'Nur das erste Team kann die Debatte beginnen',
    message_sent_successfully: 'Nachricht erfolgreich gesendet!',
    copy_link: 'Link kopieren',
    download: 'Herunterladen',
    i_prefer_to_reformulate: 'Ich ziehe es vor, umzuformulieren',
    are_you_sure_you_want_to_concede_this_debate_this_: 'Sind Sie sicher, dass Sie diese Debatte aufgeben wollen? Diese Aktion kann nicht rückgängig gemacht werden.',
    testing_automatic_translation_with_deepl: 'Testen Sie die automatische Übersetzung mit DeepL!',
    action_required: 'Erforderliche Maßnahmen',
    analyzing: 'Analysieren...',
    you_requested_the_other_team_to_reformulate_this_m: 'Sie haben das andere Team aufgefordert, diese Nachricht neu zu formulieren.',
    by: 'von',
    recommendation: 'Empfehlung:',
    reformulation_requested: 'Umformulierung beantragt:',
    the_other_teams_message_has_been_flagged: 'Die Nachricht des anderen Teams wurde markiert.',
    your_message_has_been_flagged_again_you_can: 'Ihre Nachricht wurde erneut markiert. Sie können:',
    selfinitiated_reformulation: 'Selbstinitiierte Reformulierung:',
    please_review_the_flags_and_either_lift_all_flags_: 'Bitte überprüfen Sie die Kennzeichen und heben Sie entweder alle Kennzeichen auf (wenn Sie glauben, dass sie nicht zutreffend sind) oder fordern Sie eine Neuformulierung (wenn die Nachricht verbessert werden muss).',
    '1_wait_for_the_other_team_to_review_the_flags_and_': '1. Warten, bis das andere Team die Flaggen überprüft hat und eine Entscheidung trifft, oder',
    your_most_recent_message_has_been_flagged_you_can: 'Ihre letzte Nachricht wurde markiert. Das können Sie:',
    your_message_has_been_flagged_and_needs_reformulat: 'Ihre Nachricht wurde markiert und muss umformuliert werden.',
    '2_choose_to_reformulate_your_message_again': '2. Entscheiden Sie sich, Ihre Botschaft noch einmal neu zu formulieren.',
    reformulation_requested_wait_for_your_turn_to_subm: 'Umformulierung erbeten. Warten Sie, bis Sie an der Reihe sind, eine neue Nachricht zu übermitteln.',
    please_submit_an_improved_message_that_addresses_t: 'Bitte übermitteln Sie eine verbesserte Nachricht, die auf die markierten Probleme eingeht.',
    waiting_for_the_other_team_to_send_a_message: 'Warten, bis die andere Mannschaft eine Nachricht sendet.',
    waiting_for_them_to_submit_an_improved_message_tha: 'Ich warte darauf, dass sie eine verbesserte Nachricht übermitteln, die das markierte Problem behebt.',
    current_turn: 'Aktuelle Wende',
    youve_taken_action_on_the_flagged_message: 'Sie haben auf die markierte Nachricht reagiert.',
    waiting_for_the_other_team_to_respond_with_a_new_o: 'Warten auf die Antwort des anderen Teams mit einer neuen oder umformulierten Nachricht.',
    aboutpagekeyfeaturesargumentrepository: 'aboutPage.keyFeatures.argumentRepository',
    aboutpagekeyfeaturesargumentrepositorydescription: 'aboutPage.keyFeatures.argumentRepositoryDescription',
    '2_present_your_arguments_each_team_takes_turns_pre': '2. Präsentiert eure Argumente: Jedes Team stellt abwechselnd Argumente zur Unterstützung seines Standpunkts vor. Die KI achtet auf Irrtümer und Diskussionstaktiken, die gegen die Richtlinien verstoßen.',
    '1_create_or_join_a_debate_start_a_new_debate_with_': '1. Erstellen Sie eine Debatte oder nehmen Sie an einer Debatte teil: Starten Sie eine neue Debatte mit einem klaren Thema und gegensätzlichen Standpunkten oder nehmen Sie an einer bestehenden Debatte teil.',
    '3_maintain_debate_quality_if_a_message_is_flagged_': '3. Behalten Sie die Qualität der Debatte bei: Wenn eine Nachricht markiert ist, können gegnerische Teams die Markierungen, die sie für irrelevant halten, aufheben oder um eine Umformulierung bitten.',
    technical_implementation_the_excalibur_index_curre: 'Technische Umsetzung: Der Excalibur-Index verwendet derzeit ChatGPT-4o (das neueste OpenAI-Modell), um die Qualität der Debatte, die Stärke der Argumente und die Fähigkeit des Steelmaning zu bewerten. Wenn sich die KI-Technologie weiterentwickelt, können wir unsere Modelle aktualisieren, um die höchsten Bewertungsstandards aufrechtzuerhalten, wobei Fairness und Tiefe der Analyse immer im Vordergrund stehen.',
    how_its_calculated_the_excalibur_index_combines_th: 'Wie er berechnet wird: Der Excalibur Index kombiniert drei Schlüsselkomponenten:',
    imperfect_ai_judgments: 'Unvollkommene AI-Urteile',
    both_our_jesters_gauntlet_system_and_cube_reposito: 'Sowohl unser Jester\'s Gauntlet™-System als auch unser CUBE-Repository werden ständig weiterentwickelt und von den Erkenntnissen und Erfahrungen unserer Community geprägt. Wir erkennen mehrere wichtige Aspekte unserer Plattform an:',
    '5_insightful_summaries_final_scores_each_debate_co': '5. Aufschlussreiche Zusammenfassungen und Endnoten: Jede Debatte endet mit einer KI-generierten Zusammenfassung, in der die besten Argumente beider Seiten hervorgehoben werden, sowie mit Endnoten, die die Leistung jedes Teams widerspiegeln. Diese Kombination aus qualitativer Beurteilung und quantitativer Bewertung macht unsere Plattform zu einer wertvollen Fundgrube für gut begründete Perspektiven zu komplexen Themen.',
    '4_conclude_the_debate_debates_can_end_in_several_w': '4. Beenden Sie die Debatte: Debatten können auf verschiedene Arten enden: wenn ein Team aufgibt, wenn ein Team zu viele Strafpunkte sammelt oder automatisch nach einer Woche Inaktivität (in diesem Fall analysiert die KI die Debatte, um einen Gewinner zu ermitteln oder sie für unentschieden zu erklären).',
    neither_system_will_likely_ever_be_perfect_and_tha: 'Keines der beiden Systeme wird jemals perfekt sein, und das ist auch so gewollt. Die Natur der Sprache, der Rhetorik und des Wissens ist zu komplex für absolute Regeln. Was zählt, ist unser Engagement für eine kontinuierliche Verbesserung und das gemeinsame Ziel, die Qualität der Debatten zu verbessern und eine wertvolle Wissensdatenbank aufzubauen. Wir glauben, dass selbst unvollkommene Systeme uns zu produktiveren Gesprächen und einem besseren Verständnis führen können, als wenn wir überhaupt keine Standards hätten.',
    sometimes_messages_may_be_incorrectly_flagged_or_c: 'Es kann vorkommen, dass Nachrichten falsch gekennzeichnet werden oder dass CUBE-Herausforderungen in einer Weise bewertet werden, die fragwürdig erscheint. Wenn dies bei gekennzeichneten Nachrichten der Fall ist, denken Sie daran, dass eine Neuformulierung eine Gelegenheit ist, Ihren Standpunkt zu verdeutlichen, und dass gegnerische Teams Kennzeichnungen aufheben können, die ihrer Meinung nach ungerechtfertigt sind. Bei CUBE-Herausforderungen hilft uns das Feedback der Community, unsere Bewertungskriterien zu verfeinern.',
    gauntlet_debates: 'Gauntlet Debates™',
    discover_the_current_best_explanations_on_any_topi: 'Entdecken Sie die aktuell besten Erklärungen zu jedem Thema aus unseren hochqualitativen Debatten.',
    see_how_you_rank_among_debaters_and_track_your_pro: 'Sehen Sie, wie Sie unter den Debattierern abschneiden und verfolgen Sie Ihre Fortschritte im Excalibur-Index.',
    settingsPage: {
      title: 'Benutzereinstellungen',
      loadingSettings: 'Einstellungen laden...',
      loginRequired: 'Bitte melden Sie sich an, um Ihre Einstellungen zu sehen',
      failedToLoadSettings: 'Ihre Einstellungen konnten nicht geladen werden. Bitte versuchen Sie es später noch einmal.',
      failedToSaveSettings: 'Ihre Einstellungen konnten nicht gespeichert werden. Bitte versuchen Sie es später noch einmal.',
      settingsSavedSuccessfully: 'Einstellungen erfolgreich gespeichert!',
      saving: 'Sparen...',
      saveSettings: 'Einstellungen speichern',
      emailNotifications: 'E-Mail-Benachrichtigungen',
      emailNotificationsDescription: 'Legen Sie fest, wann Sie E-Mail-Benachrichtigungen erhalten möchten.',
      notifyOnTurn: 'Benachrichtigen Sie mich, wenn ich an der Reihe bin und innerhalb von 24 Stunden nicht geantwortet habe',
      notifyOnDebateEndingSoon: 'Benachrichtigen Sie mich, wenn eine Debatte bald zu Ende ist und ich an der Reihe bin',
      notifyOnReformulation: 'Benachrichtigen Sie mich, wenn unser Team gebeten wird, eine Nachricht neu zu formulieren',
      emailAddress: 'E-Mail Adresse',
      currentEmailAddress: 'Ihre aktuelle E-Mail Adresse:',
    },
  },
};

// Check if i18n is already initialized to prevent double initialization in dev
if (i18n.isInitialized) {
  logger.info('🔄 i18n already initialized, skipping...');
} else {
  logger.info('🔍 i18n.ts DEBUG: Resources object loaded');
  logger.info('🔍 i18n.ts DEBUG: Available languages:', Object.keys(resources));
  logger.info('🔍 i18n.ts DEBUG: German common exists:', !!(resources as any).de?.common);
  logger.info('🔍 i18n.ts DEBUG: German cubePage exists:', !!(resources as any).de?.common?.cubePage);
  logger.info('🔍 i18n.ts DEBUG: German login value:', (resources as any).de?.common?.login);
}

// Only initialize if not already initialized (prevents hot reload issues)
if (!i18n.isInitialized) {
  i18n
    // .use(Backend) // Disabled temporarily to fix hot reload
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
    // Resources will be loaded from /public/locales/[lng]/[ns].json
    // For now using inline resources for demonstration
    resources,
    
    // Default language
    fallbackLng: 'en',
    
    // Available languages
    supportedLngs: ['en', 'es', 'de'],
    
    // Namespaces
    defaultNS: 'common',
    ns: ['common', 'debate', 'auth', 'errors', 'test'],
    
    // Debug mode (enable in development, disable in production)
    debug: process.env.NODE_ENV === 'development',
    
    // Language detection options
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    
    // Handle missing translations with batch DeepL
    parseMissingKeyHandler: (key: string, defaultValue?: string) => {
      logger.info(`🔍 Missing translation key: "${key}", lang: ${i18n.language}, defaultValue: "${defaultValue}"`);
      
      // For English, return the default value
      if (i18n.language === 'en') {
        logger.info(`⏭️ Returning default for English: "${defaultValue || key}"`);
        return defaultValue || key;
      }
      
      // Check if translation already exists in store (to prevent infinite loops)
      const store = i18n.store as any;
      if (store.data[i18n.language] && 
          store.data[i18n.language]['common'] && 
          store.data[i18n.language]['common'][key]) {
        const existingTranslation = store.data[i18n.language]['common'][key];
        logger.info(`✅ Translation already exists in store: "${key}" -> "${existingTranslation}"`);
        return existingTranslation;
      }
      
      // For other languages, queue for batch translation
      const currentLang = i18n.language;
      const textToTranslate = defaultValue || key;
      
      // Check for API key
      const apiKey = process.env.REACT_APP_DEEPL_API_KEY;
      logger.info(`🔑 API Key check:`, { 
        hasKey: !!apiKey, 
        keyLength: apiKey?.length || 0,
        envCheck: typeof process !== 'undefined' && !!process.env
      });
      
      if (apiKey && currentLang !== 'en') {
        logger.info(`📝 Queuing for batch translation: "${textToTranslate}" to ${currentLang}`);
        queueTranslation(key, textToTranslate, currentLang);
      } else {
        logger.warn(`🚫 No API key found - cannot auto-translate "${key}"`);
      }
      
      // Return fallback immediately (batch translation will update later)
      return textToTranslate;
    },
    
    // Interpolation options
    interpolation: {
      escapeValue: false, // React already handles XSS
    },
    
    // React options
    react: {
      useSuspense: false, // Disable to prevent hot reload loops
    },
    
    // Backend options (for loading from files) - disabled temporarily
    // backend: {
    //   loadPath: '/locales/{{lng}}/{{ns}}.json',
    // }
    });
} else {
  logger.info('⏭️ i18n already initialized, skipping initialization');
}

export default i18n; 